import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import "./AdminOrderList.css";
import "./StaffManagement.css";

/* ══════════════════════════════════════════════════
   API CONFIG
══════════════════════════════════════════════════ */
const RAW_API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "";

const CLEAN_API_BASE = String(RAW_API_BASE || "")
  .trim()
  .replace(/\/+$/, "");

const API_URL = CLEAN_API_BASE
  ? CLEAN_API_BASE.endsWith("/api")
    ? CLEAN_API_BASE
    : `${CLEAN_API_BASE}/api`
  : "/api";

const BOOKINGS_API = `${API_URL}/bookings`;
const RESERVATIONS_API = `${API_URL}/reservations`;
const TRANSFER_API = `${API_URL}/transfer`;
const TRANSFER_LIST_API = `${TRANSFER_API}/list`;
const HEALTH_API = `${API_URL}/health`;
const ADMIN_API = `${API_URL}/admin`;
const SUPERADMIN_API = `${API_URL}/superadmin`;
const EXTRA_ADDONS_API = `${API_URL}/extra-addons`;

const RESERVATION_HANDOVER_API = (id) =>
  `${RESERVATIONS_API}/handover/${encodeURIComponent(String(id || ""))}`;

const TRANSFER_ROW_API = (id) =>
  `${TRANSFER_API}/${encodeURIComponent(String(id || ""))}`;

/* ══════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════ */
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ADVANCE_PAID = 700;
const GST_RATE = 0.18;
const DEFAULT_FIXED_DECOR = 700;
const DEFAULT_EXTRA_ADULT_PRICE = 400;
const DEFAULT_KID_PRICE = 200;
const DEFAULT_3D_GLASS_PRICE = 300;
const PAYMENT_CONFIRMATION_KEY = "hexaplay_payment_confirmation";

/* limits for the text areas */
const CANCEL_REASON_MAX = 1000;
const CANCEL_REASON_MIN = 5;
const DISCOUNT_REASON_MAX = 500;
const EXTRA_COST_REASON_MAX = 500; /* NEW */



/* NEW: quick picks for the Extra Cost Category */
const EXTRA_COST_CATEGORY_OPTIONS = [
  "Extra Decoration",
  "Extra Food & Beverage",
  "Extra Time / Overtime",
  "Extra Guests Handling",
  "Damage / Breakage",
  "Special Request",
  "Other Charges",
];

/* quick picks for the Cancel Reason popup */
const CANCEL_QUICK_REASONS = [
  "Customer Requested to Cancel – Fully Refunded",
  "Customer Requested to Cancel – Non-Refunded (As Per Cancellation Policy)",
  "Customer Did Not Attend the Celebration – ₹700 Non-Refunded",
  "Cancelled Due to Technical Issues",
  "Cancelled Due to Scheduling or Timing Issues",
  "Cancelled Due to Payment Issues",
  "Cancelled by Management",
  "Customer Requested to Reschedule",
  "Duplicate Booking / Booking Created by Mistake",
  "Other Reasons",
];

/* Permanent workflow state store (suspended / cancelled) */
const ROW_STATE_KEY = "hexaplay_staff_row_workflow_state_v1";

const LEGACY_LOCAL_KEYS = [
  "hexaplay_staff_addons_permanent_cache",
  "hexaplay_staff_frozen_orders",
  "hexaplay_staff_permanent_frozen_order_keys",
];

const CATEGORY_DEFS = [
  { key: "cakes", label: "Cakes", emoji: "🎂", field: "cakes" },
  {
    key: "decorations",
    label: "Decorations",
    emoji: "🎨",
    field: "extraDecors",
  },
  { key: "gifts", label: "Gifts", emoji: "🎁", field: "gifts" },
  { key: "services", label: "Services", emoji: "🛎️", field: "services" },
];

/* ══════════════════════════════════════════════════
   EXTRA ADD-ONS — CATEGORY DEFINITIONS
   (Items are loaded live from /api/extra-addons/all,
   the Super Admin "Extra Add-ons" inventory page.
   No sample / dummy data is used here.)
══════════════════════════════════════════════════ */
const EXTRA_ADDON_CATEGORY_DEFS = [
  { key: "cakes", label: "Cakes", emoji: "🎂" },
  { key: "decorations", label: "Decorations", emoji: "🎨" },
  { key: "gifts", label: "Gifts", emoji: "🎁" },
  { key: "services", label: "Services", emoji: "🛎️" },
];

const normalizeExtraAddonCatalogItem = (item) => ({
  id: item?._id || item?.id,
  _id: item?._id || item?.id,
  name: String(item?.name || "Untitled").trim(),
  price: (() => {
    const p = Number(item?.price);
    return Number.isFinite(p) ? p : 0;
  })(),
  emoji: item?.emoji || "⭐",
  category: String(item?.category || "gifts").toLowerCase(),
  stock: (() => {
    const s = Number(item?.stock);
    return Number.isFinite(s) ? s : 0;
  })(),
  description: item?.description || "",
  isActive: item?.isActive !== false,
});

const STAFF_TABS = [
  { key: "customer", label: "Customer & Celebration", icon: "👤" },
  { key: "extra", label: "Extra Persons", icon: "👥" },
  { key: "complimentary", label: "Complimentary Items", icon: "🎁" },
  { key: "summary", label: "Booking Summary", icon: "📋" },
  { key: "custom", label: "Custom Fields", icon: "☑️" },
];

const DEFAULT_COMPLIMENTARY_ITEMS = [
  "Cake Knife",
  "Bluetooth Speaker",
  "Welcome Board",
  "Rose Petals",
  "Fog Entry",
  "Birthday Caps",
  "LED Name Board",
  "Photography Assistance",
];

const EMPTY_HANDOVER_DETAILS = {
  foodDetails: "",
  cakeMessage: "",
  cakeFlavor: "",
  cakeWeight: "",
  candleNumber: "",
  sashType: "Not Required",
  crownType: "Not Required",
  decorationTheme: "",
  managerDiscount: 0,
  managerRemarks: "",
  specialInstructions: "",
};

const EMPTY_EXTRA_PERSON_DETAILS = {
  totalPersons: 0,
  packageIncludedPersons: 2,
  complimentaryPersons: 0,
  extraPersons: 0,
  chargeablePersons: 0,
  standardCostPerPerson: DEFAULT_EXTRA_ADULT_PRICE,
  discountPerPerson: 0,
  finalCostPerPerson: DEFAULT_EXTRA_ADULT_PRICE,
  extraPersonAmount: 0,
  isActive: false,
  remarks: "",
};

/* ══════════════════════════════════════════════════
   EXTRA CSS (self-contained — new popup, toast, reason cells)
══════════════════════════════════════════════════ */
const EXTRA_CSS = `
.hp-cancel-modal{width:min(560px,94vw);background:#fff;border-radius:20px;box-shadow:0 30px 80px rgba(15,23,42,.35);overflow:hidden;animation:hpPop .18s ease-out}
@keyframes hpPop{from{transform:translateY(10px) scale(.98);opacity:0}to{transform:none;opacity:1}}
.hp-cancel-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:20px 22px;background:linear-gradient(135deg,#7f1d1d,#dc2626);color:#fff}
.hp-cancel-head h2{margin:0;font-size:19px;font-weight:800}
.hp-cancel-head p{margin:4px 0 0;font-size:12.5px;opacity:.9}
.hp-cancel-head button{border:0;background:rgba(255,255,255,.18);color:#fff;width:32px;height:32px;border-radius:10px;cursor:pointer;font-size:15px}
.hp-cancel-body{padding:20px 22px}
.hp-cancel-info{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px}
.hp-cancel-info div{padding:9px 12px;border-radius:10px;background:#f8fafc;border:1px solid #e2e8f0}
.hp-cancel-info span{display:block;font-size:10.5px;color:#64748b;font-weight:700}
.hp-cancel-info b{display:block;margin-top:2px;font-size:13px;color:#0f172a}
.hp-cancel-label{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;font-size:13px;font-weight:800;color:#0f172a}
.hp-cancel-label em{font-style:normal;color:#dc2626}
.hp-cancel-textarea{width:100%;min-height:150px;resize:vertical;padding:12px 14px;border-radius:12px;border:1.5px solid #e2e8f0;font-size:14px;line-height:1.5;font-family:inherit;box-sizing:border-box;outline:none;transition:border-color .15s,box-shadow .15s}
.hp-cancel-textarea:focus{border-color:#dc2626;box-shadow:0 0 0 3px rgba(220,38,38,.12)}
.hp-cancel-textarea.invalid{border-color:#dc2626;background:#fff5f5}
.hp-cancel-meta{display:flex;justify-content:space-between;margin-top:6px;font-size:12px;color:#64748b}
.hp-cancel-meta .err{color:#dc2626;font-weight:700}
.hp-cancel-meta .warn{color:#c2410c;font-weight:700}
.hp-cancel-chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}
.hp-cancel-chips button{border:1px solid #fecaca;background:#fff5f5;color:#b91c1c;padding:5px 10px;border-radius:99px;font-size:11.5px;font-weight:700;cursor:pointer}
.hp-cancel-chips button:hover{background:#fee2e2}
.hp-cancel-foot{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px;background:#f8fafc;border-top:1px solid #e2e8f0}
.hp-reason-cell{display:block;max-width:260px;white-space:pre-wrap;word-break:break-word;font-size:12px;color:#7f1d1d;background:#fff5f5;border:1px solid #fecaca;border-radius:8px;padding:6px 9px;max-height:84px;overflow:auto}
.hp-reason-meta{display:block;margin-top:3px;font-size:10.5px;color:#64748b}
.hp-cancel-reason-box{margin:10px 0 0;padding:12px 14px;border-radius:12px;background:#fff5f5;border:1px solid #fecaca;color:#7f1d1d;white-space:pre-wrap;word-break:break-word}
.hp-cancel-reason-box strong{display:block;margin-bottom:4px;font-size:12px;letter-spacing:.3px}
.hp-discount-split,.hp-extra-split{grid-column:1/-1;display:grid;grid-template-columns:1fr 1.4fr;gap:12px}
.hp-discount-split label,.hp-extra-split label{display:block}
.hp-discount-split input,.hp-discount-split textarea,.hp-extra-split input,.hp-extra-split textarea{width:100%;padding:10px 12px;border-radius:10px;border:1px solid #e2e8f0;box-sizing:border-box;font-family:inherit;font-size:13.5px}
.hp-discount-split textarea,.hp-extra-split textarea{min-height:66px;resize:vertical}
.hp-discount-split .hp-count,.hp-extra-split .hp-count{display:block;margin-top:3px;text-align:right;font-size:11px;color:#64748b}
.hp-discount-chips{display:flex;flex-wrap:wrap;gap:5px;margin-top:6px}
.hp-discount-chips button{border:1px solid #ddd6fe;background:#f5f3ff;color:#6d28d9;padding:3px 9px;border-radius:99px;font-size:11px;font-weight:700;cursor:pointer}
.hp-discount-chips button:hover{background:#ede9fe}
.hp-extra-chips{display:flex;flex-wrap:wrap;gap:5px;margin-top:6px}
.hp-extra-chips button{border:1px solid #fde68a;background:#fffbeb;color:#b45309;padding:3px 9px;border-radius:99px;font-size:11px;font-weight:700;cursor:pointer}
.hp-extra-chips button:hover{background:#fef3c7}
.hp-extra-chips button.active{background:#f59e0b;border-color:#f59e0b;color:#fff}
.hp-discount-card{grid-column:1/-1;margin-top:4px;padding:14px;border-radius:14px;background:#faf5ff;border:1px solid #e9d5ff}
.hp-discount-card h5{margin:0 0 4px;font-size:14px;color:#6d28d9}
.hp-discount-card p{margin:0 0 10px;font-size:12px;color:#7c3aed}
.hp-discount-amount{display:inline-block;margin-bottom:10px;padding:5px 12px;border-radius:99px;background:#fff;border:1px solid #ddd6fe;color:#6d28d9;font-weight:800;font-size:12.5px}
.hp-extra-card{grid-column:1/-1;margin-top:10px;padding:14px;border-radius:14px;background:#fffbeb;border:1px solid #fde68a}
.hp-extra-card h5{margin:0 0 4px;font-size:14px;color:#b45309}
.hp-extra-card p{margin:0 0 10px;font-size:12px;color:#d97706}
.hp-extra-amount{display:inline-block;margin-bottom:10px;padding:5px 12px;border-radius:99px;background:#fff;border:1px solid #fde68a;color:#b45309;font-weight:800;font-size:12.5px}
.hp-toast-stack{position:fixed;right:20px;bottom:20px;z-index:99999;display:flex;flex-direction:column;gap:10px}
.hp-toast{min-width:260px;max-width:380px;padding:12px 16px;border-radius:12px;color:#fff;font-weight:700;font-size:13px;box-shadow:0 14px 40px rgba(15,23,42,.3);animation:hpPop .18s ease-out;display:flex;gap:10px;align-items:flex-start}
.hp-toast.success{background:#15803d}.hp-toast.error{background:#b91c1c}.hp-toast.info{background:#1d4ed8}.hp-toast.warn{background:#c2410c}
.hp-toast button{margin-left:auto;background:none;border:0;color:#fff;cursor:pointer;font-size:14px}
@media(max-width:640px){.hp-cancel-info,.hp-discount-split,.hp-extra-split{grid-template-columns:1fr}}
`;

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => `₹${n(value).toLocaleString("en-IN")}`;

const safeQty = (item) =>
  Math.max(
    1,
    Math.floor(n(item?.qty || item?.quantity || item?.count || 1))
  );

const itemTotal = (item) => n(item?.price) * safeQty(item);

const qtyLabel = (item) => {
  const q = safeQty(item);
  return q > 1 ? ` x ${q} Nos` : "";
};

const hasValue = (v) =>
  v !== undefined &&
  v !== null &&
  !(typeof v === "string" && v.trim() === "");

const pickValue = (...values) => values.find(hasValue);

/* first value that is not undefined/null (keeps intentional "" so a field can be cleared) */
const firstDefined = (...values) =>
  values.find((v) => v !== undefined && v !== null);

const arrayValue = (v) => (Array.isArray(v) ? v : []);

const canUseLocalStorage = () =>
  typeof window !== "undefined" &&
  typeof window.localStorage !== "undefined";

const purgeLegacyLocalCaches = () => {
  if (!canUseLocalStorage()) return;
  LEGACY_LOCAL_KEYS.forEach((k) => {
    try {
      window.localStorage.removeItem(k);
    } catch {}
  });
};

purgeLegacyLocalCaches();

const getTransferId = (order) =>
  order?._transferId ||
  order?.transferId ||
  (order?._source === "transfer" ? order?._id : "") ||
  "";

const getOrderId = (order) =>
  order?._orderId ||
  order?.orderId ||
  order?.bookingId ||
  order?._bookingId ||
  order?.orderSnapshot?._id ||
  order?.orderSnapshot?.id ||
  order?._rawBooking?._id ||
  order?.id ||
  order?._id ||
  "";

const getBookingPatchId = (order) =>
  order?._bookingId ||
  (order?._source === "booking" ? order?._id : "") ||
  order?.bookingId ||
  order?._orderId ||
  "";

const getPersistId = (order) =>
  order?._orderId ||
  order?.orderId ||
  order?.bookingId ||
  order?._bookingId ||
  getTransferId(order) ||
  order?._id ||
  "";

const getRowId = (order) =>
  order?._rowKey ||
  (getTransferId(order) ? `transfer-${getTransferId(order)}` : "") ||
  (getOrderId(order) ? `booking-${getOrderId(order)}` : "") ||
  "";

const getOrderStorageKey = (order) => {
  const id = getRowId(order) || getOrderId(order);
  if (id) return String(id);

  return [
    order?.name,
    order?.whatsapp,
    order?.date,
    order?.timeSlot,
    order?.timestamp,
  ]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .join("|");
};

const isOrderLocked = (order) =>
  !!(
    order &&
    (order.frozen === true ||
      order.permanentlyLocked === true ||
      order.orderSnapshot?.frozen === true ||
      order.orderSnapshot?.permanentlyLocked === true)
  );

const toLockedOrder = (order) => ({
  ...order,
  frozen: true,
  frozenAt:
    order?.frozenAt ||
    order?.orderSnapshot?.frozenAt ||
    new Date().toISOString(),
  frozenBy: order?.frozenBy || order?.orderSnapshot?.frozenBy || "Admin",
  permanentlyLocked: true,
  status: order?.paymentInfo ? "Fully Paid" : order?.status || "Advance Paid",
  total: n(order?.total || computeTotal(order)),
});

const formatTS = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseEventDate = (dateStr) => {
  if (!dateStr) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
    const [yyyy, mm, dd] = String(dateStr).split("-").map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const parts = String(dateStr).split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy) return null;

  const d = new Date(yyyy, mm - 1, dd);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toInputVal = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) return dateStr;

  const p = String(dateStr).split("/");
  if (p.length !== 3) return "";

  return `${p[2]}-${p[1].padStart(2, "0")}-${p[0].padStart(2, "0")}`;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—";

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
    const [y, m, d] = String(dateStr).split("-");
    return `${d}/${m}/${y}`;
  }

  return dateStr;
};

const formatRangeLabel = (from, to) => {
  const fmt = (s) => {
    if (!s) return "";

    let dd;
    let mm;
    let yyyy;

    if (/^\d{4}-\d{2}-\d{2}$/.test(String(s))) {
      [yyyy, mm, dd] = String(s).split("-");
    } else {
      const p = String(s).split("/");
      [dd, mm, yyyy] = p;
    }

    return `${dd} ${MONTH_NAMES[parseInt(mm, 10) - 1] || ""} ${yyyy}`;
  };

  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  if (to) return `Until ${fmt(to)}`;
  return "All Dates";
};

const pad2 = (num) => String(num).padStart(2, "0");

const toISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const buildQuickDay = (offsetDays) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);

  return {
    iso: toISODate(d),
    dayNum: d.getDate(),
    month: MONTH_NAMES[d.getMonth()].slice(0, 3),
    weekday: WEEKDAY_NAMES[d.getDay()],
  };
};

const normalizeCatalogItem = (item) => ({
  id: item?._id || item?.id || item?.slug || item?.name,
  _id: item?._id,
  name: item?.name || item?.title || "Untitled",
  price: n(item?.price),
});

const normalizeItemList = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((x) => x && (x.name || x.title))
    .map((x) => ({
      _id: x._id || x.id || undefined,
      name: String(x.name || x.title || "").trim(),
      price: n(x.price),
      qty: Math.max(1, Math.floor(n(x.qty ?? x.quantity ?? x.count ?? 1))),
    }));

const ADDON_FIELDS = ["cakes", "extraDecors", "gifts", "services", "extraAddons"];

const cacheTime = (c) => {
  const t = new Date(c?.updatedAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const isCacheObject = (c) =>
  !!c &&
  typeof c === "object" &&
  (("updatedAt" in c) ||
    ("total" in c) ||
    ADDON_FIELDS.some((k) => k in c));

const resolveAddonSource = (b = {}, meta = {}) => {
  const caches = [b.addonsCache, meta.addonsCache].filter(isCacheObject);

  if (caches.length) {
    const newest = caches
      .slice()
      .sort((a, z) => cacheTime(z) - cacheTime(a))[0];

    return {
      cakes: normalizeItemList(newest.cakes),
      extraDecors: normalizeItemList(newest.extraDecors),
      gifts: normalizeItemList(newest.gifts),
      services: normalizeItemList(newest.services),
      extraAddons: normalizeItemList(newest.extraAddons),
      total: n(newest.total),
      from: "cache",
    };
  }

  const rootHas = ADDON_FIELDS.some(
    (k) => Array.isArray(b[k]) && b[k].length
  );

  const src = rootHas ? b : meta;

  return {
    cakes: normalizeItemList(src.cakes),
    extraDecors: normalizeItemList(src.extraDecors),
    gifts: normalizeItemList(src.gifts),
    services: normalizeItemList(src.services),
    extraAddons: normalizeItemList(src.extraAddons),
    total: 0,
    from: rootHas ? "root" : "meta",
  };
};

const getAddonArrays = (o) => ({
  cakes: Array.isArray(o?.cakes) ? o.cakes : [],
  extraDecors: Array.isArray(o?.extraDecors) ? o.extraDecors : [],
  gifts: Array.isArray(o?.gifts) ? o.gifts : [],
  services: Array.isArray(o?.services) ? o.services : [],
  extraAddons: Array.isArray(o?.extraAddons) ? o.extraAddons : [],
});

const computeSubtotalBeforeManualAdjustments = (o) => {
  const a = getAddonArrays(o);

  let total = n(o?.basePrice) + n(o?.fixedDecor || DEFAULT_FIXED_DECOR);

  total +=
    Math.max(0, n(o?.extraAdults)) *
    n(o?.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE);

  total += Math.max(0, n(o?.kids)) * n(o?.kidPrice || DEFAULT_KID_PRICE);

  total +=
    Math.max(0, n(o?.glassesCount)) *
    (n(o?.glass3DCost)
      ? n(o?.glass3DCost) / Math.max(1, n(o?.glassesCount))
      : DEFAULT_3D_GLASS_PRICE);

  a.cakes.forEach((x) => {
    total += itemTotal(x);
  });

  a.extraDecors.forEach((x) => {
    total += itemTotal(x);
  });

  a.gifts.forEach((x) => {
    total += itemTotal(x);
  });

  a.services.forEach((x) => {
    total += itemTotal(x);
  });

  a.extraAddons.forEach((x) => {
    total += itemTotal(x);
  });

  return total;
};

/* extraCost is added to the total together with foodCost */
const computeTotal = (o) => {
  const base = computeSubtotalBeforeManualAdjustments(o);
  const foodCost = n(o?.foodCost || o?.paymentInfo?.foodCost);
  const extraCost = n(o?.extraCost || o?.paymentInfo?.extraCost);
  const manualDiscount = n(o?.manualDiscount || o?.paymentInfo?.manualDiscount);
  const couponDiscount = n(o?.discount);

  return Math.max(
    0,
    base + foodCost + extraCost - manualDiscount - couponDiscount
  );
};

const computeExtraAddonTotal = (list) =>
  normalizeItemList(list).reduce((sum, x) => sum + itemTotal(x), 0);

const addonText = (o) => {
  const a = getAddonArrays(o);

  const items = [
    ...a.cakes.map((x) => `🎂 ${x.name}${qtyLabel(x)} (${money(itemTotal(x))})`),
    ...a.extraDecors.map(
      (x) => `🎨 ${x.name}${qtyLabel(x)} (${money(itemTotal(x))})`
    ),
    ...a.gifts.map((x) => `🎁 ${x.name}${qtyLabel(x)} (${money(itemTotal(x))})`),
    ...a.services.map(
      (x) => `🛎️ ${x.name}${qtyLabel(x)} (${money(itemTotal(x))})`
    ),
    ...a.extraAddons.map(
      (x) => `⭐ ${x.name}${qtyLabel(x)} (${money(itemTotal(x))})`
    ),
  ];

  if (n(o?.glassesCount) > 0) {
    items.push(`🥽 3D Glasses x${n(o.glassesCount)} (${money(o.glass3DCost)})`);
  }

  return items.join(", ") || "—";
};

const buildAddonRows = (o) => {
  const a = getAddonArrays(o);

  const rows = [
    ...a.cakes.map((x) => ({ ...x, emoji: "🎂", category: "Cake" })),
    ...a.extraDecors.map((x) => ({ ...x, emoji: "🎨", category: "Decor" })),
    ...a.gifts.map((x) => ({ ...x, emoji: "🎁", category: "Gift" })),
    ...a.services.map((x) => ({ ...x, emoji: "🛎️", category: "Service" })),
    ...a.extraAddons.map((x) => ({ ...x, emoji: "⭐", category: "Extra" })),
  ];

  if (n(o?.glassesCount) > 0) {
    rows.push({
      name: "3D Glasses",
      price: DEFAULT_3D_GLASS_PRICE,
      qty: n(o.glassesCount),
      emoji: "🥽",
      category: "Accessory",
    });
  }

  return rows;
};

const escapeHTML = (str) =>
  String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const isTruthy = (v) =>
  v === true || v === "true" || v === 1 || v === "1";

const encodeWhatsAppText = (text) => encodeURIComponent(text);

const isSuccessfulBooking = (b) =>
  String(b?.state || "").toUpperCase() === "COMPLETED" ||
  [
    "Advance Paid",
    "Fully Paid",
    "Transferred to Staff",
    "In Staff Management",
    "Suspended",
    "Cancelled",
  ].includes(String(b?.status || ""));

/* ══════════════════════════════════════════════════
   DUPLICATE DETECTION HELPERS
══════════════════════════════════════════════════ */
const normName = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normPhone = (v) => {
  const digits = String(v || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const duplicateKeyOf = (o) => {
  const name = normName(o?.name || o?.customerName);
  const phone = normPhone(o?.whatsapp || o?.primaryContactNumber);

  if (!name || name === "—" || !phone || phone.length < 6) return "";

  return `${name}|${phone}`;
};

const buildDuplicateSet = (rows) => {
  const counts = {};

  rows.forEach((o) => {
    const key = duplicateKeyOf(o);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });

  return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
};

/* ══════════════════════════════════════════════════
   PERMANENT WORKFLOW STATE (SUSPENDED / CANCELLED)
══════════════════════════════════════════════════ */
const readRowStates = () => {
  if (!canUseLocalStorage()) return {};

  try {
    const raw = window.localStorage.getItem(ROW_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeRowStates = (map) => {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(ROW_STATE_KEY, JSON.stringify(map || {}));
  } catch {}
};

const identityKeyForRow = (o) => {
  const oid = String(o?._orderId || o?.orderId || "").trim();
  if (oid && oid !== "undefined" && oid !== "null") return `order:${oid}`;

  const merchant = String(o?.merchantOrderId || "").trim();
  if (merchant) return `merchant:${merchant}`;

  return `fallback:${String(o?.whatsapp || "").replace(/\D/g, "")}|${
    o?.date || ""
  }|${o?.timeSlot || ""}`;
};

const saveRowState = (order, state, extra = {}) => {
  const key = identityKeyForRow(order);
  if (!key) return;

  const map = readRowStates();

  if (!state || state === "active") {
    delete map[key];
  } else {
    map[key] = {
      state,
      at: new Date().toISOString(),
      by: "Admin",
      ...extra,
    };
  }

  writeRowStates(map);
};

const resolveRowState = (order, map = {}) => {
  const server = String(
    order?.staffState ||
      order?.orderSnapshot?.staffState ||
      order?._rawTransfer?.staffState ||
      ""
  ).toLowerCase();

  if (server === "suspended" || server === "cancelled" || server === "active") {
    return server;
  }

  const local = map[identityKeyForRow(order)]?.state;
  if (local === "suspended" || local === "cancelled") return local;

  const status = String(order?.status || "").toLowerCase();
  if (status === "cancelled") return "cancelled";
  if (status === "suspended") return "suspended";

  return "active";
};

/* resolves cancel reason / time / user from server first, then local ledger */
const resolveCancelMeta = (order, map = {}) => {
  const local = map[identityKeyForRow(order)] || {};
  const isLocalCancelled = local.state === "cancelled";

  return {
    cancelReason:
      pickValue(
        order?.cancelReason,
        order?.orderSnapshot?.cancelReason,
        order?._rawTransfer?.cancelReason,
        isLocalCancelled ? local.reason : ""
      ) || "",
    cancelledAt:
      pickValue(
        order?.cancelledAt,
        order?.orderSnapshot?.cancelledAt,
        order?._rawTransfer?.cancelledAt,
        isLocalCancelled ? local.at : ""
      ) || "",
    cancelledBy:
      pickValue(
        order?.cancelledBy,
        order?.orderSnapshot?.cancelledBy,
        order?._rawTransfer?.cancelledBy,
        isLocalCancelled ? local.by : ""
      ) || "",
  };
};

/* ══════════════════════════════════════════════════
   FETCH WRAPPER
══════════════════════════════════════════════════ */
const withTimeout = (url, options = {}, ms = 8000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  const method = String(options.method || "GET").toUpperCase();
  const bust =
    method === "GET"
      ? `${url.includes("?") ? "&" : "?"}_ts=${Date.now()}`
      : "";

  return fetch(`${url}${bust}`, {
    ...options,
    cache: "no-store",
    credentials: "include",
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
};

const patchBooking = async (id, body, ms = 12000) => {
  if (!id) return { ok: false, data: null };

  const urls = [
    `${BOOKINGS_API}/admin/update/${encodeURIComponent(String(id))}`,
    `${RESERVATIONS_API}/${encodeURIComponent(String(id))}`,
  ];

  let ok = false;
  let data = null;

  await Promise.all(
    urls.map(async (url) => {
      try {
        const res = await withTimeout(
          url,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
          ms
        );

        if (res.ok) {
          ok = true;
          try {
            const json = await res.json();
            data = json?.data || json || data;
          } catch {}
        }
      } catch {}
    })
  );

  return { ok, data };
};

/* ══════════════════════════════════════════════════
   STAFF HANDOVER HELPERS
══════════════════════════════════════════════════ */
const makeCustomFieldId = () =>
  `cf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const computeStaffExtraPersonDetails = (input = {}, order = {}) => {
  const orderPersons =
    n(order.persons) || n(order.adults) + n(order.kids) || 0;

  const totalPersons = Math.max(
    0,
    Math.floor(n(hasValue(input.totalPersons) ? input.totalPersons : orderPersons))
  );

  const packageIncludedPersons = Math.max(
    0,
    Math.floor(
      n(
        hasValue(input.packageIncludedPersons)
          ? input.packageIncludedPersons
          : Math.max(
              2,
              n(order.adults) + n(order.kids) - n(order.extraAdults)
            )
      )
    )
  );

  const extraPersons = Math.max(0, totalPersons - packageIncludedPersons);

  const complimentaryPersons = Math.min(
    Math.max(0, Math.floor(n(input.complimentaryPersons))),
    extraPersons
  );

  const chargeablePersons = Math.max(
    0,
    extraPersons - complimentaryPersons
  );

  const standardCostPerPerson = Math.max(
    0,
    n(
      hasValue(input.standardCostPerPerson)
        ? input.standardCostPerPerson
        : DEFAULT_EXTRA_ADULT_PRICE
    )
  );

  const discountPerPerson = Math.min(
    Math.max(0, n(input.discountPerPerson)),
    standardCostPerPerson
  );

  const finalCostPerPerson = Math.max(
    0,
    standardCostPerPerson - discountPerPerson
  );

  const extraPersonAmount = chargeablePersons * finalCostPerPerson;

  return {
    totalPersons,
    packageIncludedPersons,
    complimentaryPersons,
    extraPersons,
    chargeablePersons,
    standardCostPerPerson,
    discountPerPerson,
    finalCostPerPerson,
    extraPersonAmount,
    isActive: extraPersons > 0 || finalCostPerPerson > 0,
    remarks: String(input.remarks || "").trim(),
  };
};

const calculateStaffLivePricing = (order, form = {}) => {
  const extraDetails = computeStaffExtraPersonDetails(
    form.extraPersonDetails || {},
    order
  );

  const packageAmount = hasValue(form.packageAmount)
    ? n(form.packageAmount)
    : n(order.total || computeTotal(order));

  const addonsAmount = n(form.addonsAmount);

  const managerDiscount = n(
    hasValue(form.managerDiscount)
      ? form.managerDiscount
      : form.handoverDetails?.managerDiscount
  );

  const otherDiscount = n(form.otherDiscount);

  const finalAmount = Math.max(
    0,
    packageAmount +
      extraDetails.extraPersonAmount +
      addonsAmount -
      managerDiscount -
      otherDiscount
  );

  return {
    packageAmount,
    addonsAmount,
    managerDiscount,
    otherDiscount,
    extraPersonAmount: extraDetails.extraPersonAmount,
    finalAmount,
    extraDetails,
  };
};

const normalizeStaffHandover = (order, source = {}) => {
  const raw =
    source?.data ||
    source?.transfer ||
    source?.reservation ||
    source?.merged ||
    source ||
    {};

  const snapshot =
    raw.orderSnapshot ||
    raw.snapshot ||
    order?.orderSnapshot ||
    order?._rawBooking ||
    {};

  const handoverDetails = {
    ...EMPTY_HANDOVER_DETAILS,
    ...(snapshot.handoverDetails || {}),
    ...(order?.handoverDetails || {}),
    ...(raw.handoverDetails || {}),
  };

  const extraPersonDetails = computeStaffExtraPersonDetails(
    {
      ...EMPTY_EXTRA_PERSON_DETAILS,
      ...(snapshot.extraPersonDetails || {}),
      ...(order?.extraPersonDetails || {}),
      ...(raw.extraPersonDetails || {}),
    },
    order
  );

  const packageAmount =
    n(raw.packageAmount) ||
    n(order?.packageAmount) ||
    n(raw.amount) ||
    n(snapshot.packageAmount) ||
    n(snapshot.amount) ||
    n(order?.total || computeTotal(order));

  const baseForm = {
    _id: raw._id || order?._id || "",
    transferId: raw.transferId || getTransferId(order) || raw._id || "",
    orderId: raw.orderId || getOrderId(order),

    customerName: pickValue(
      raw.customerName,
      raw.name,
      snapshot.customerName,
      snapshot.name,
      order?.name
    ),
    name: pickValue(
      raw.name,
      raw.customerName,
      snapshot.name,
      snapshot.customerName,
      order?.name
    ),
    whatsapp: pickValue(
      raw.whatsapp,
      raw.primaryContactNumber,
      snapshot.whatsapp,
      snapshot.primaryContactNumber,
      order?.whatsapp
    ),
    primaryContactNumber: pickValue(
      raw.primaryContactNumber,
      raw.whatsapp,
      snapshot.primaryContactNumber,
      snapshot.whatsapp,
      order?.whatsapp
    ),
    alternateContactNumber: pickValue(
      raw.alternateContactNumber,
      snapshot.alternateContactNumber,
      order?.alternateContactNumber,
      ""
    ),
    celebrationName: pickValue(
      raw.celebrationName,
      snapshot.celebrationName,
      order?.celebrationName,
      ""
    ),

    theater: pickValue(raw.theater, snapshot.theater, order?.theater),
    eventDate: pickValue(raw.eventDate, raw.date, snapshot.date, order?.date),
    date: pickValue(raw.date, raw.eventDate, snapshot.date, order?.date),
    timeSlot: pickValue(
      raw.timeSlot,
      raw.slot,
      snapshot.timeSlot,
      snapshot.slot,
      order?.timeSlot
    ),
    occasion: pickValue(raw.occasion, snapshot.occasion, order?.occasion),

    adults: n(pickValue(raw.adults, snapshot.adults, order?.adults)),
    kids: n(pickValue(raw.kids, snapshot.kids, order?.kids)),
    persons:
      n(raw.persons) ||
      n(snapshot.persons) ||
      n(order?.persons) ||
      n(order?.adults) + n(order?.kids),

    packageAmount,
    addonsAmount: n(raw.addonsAmount || order?.addonsAmount || 0),
    managerDiscount: n(
      raw.managerDiscount ||
        order?.managerDiscount ||
        handoverDetails.managerDiscount ||
        snapshot.managerDiscount ||
        0
    ),
    otherDiscount: n(raw.otherDiscount || order?.otherDiscount || 0),
    amount: n(raw.amount || packageAmount),
    advancePaid: n(
      raw.advancePaid ||
        snapshot.advancePaid ||
        order?.advancePaid ||
        ADVANCE_PAID
    ),
    finalAmount: n(raw.finalAmount || order?.finalAmount || 0),

    /* Less Discount (Payment Settlement) — Remark + Reason, editable & saved */
    settlementDiscount: n(
      firstDefined(
        raw.manualDiscount,
        order?.paymentInfo?.manualDiscount,
        order?.manualDiscount,
        snapshot.manualDiscount,
        0
      )
    ),
    manualDiscountRemark: String(
      firstDefined(
        raw.manualDiscountRemark,
        handoverDetails.lessDiscountRemark,
        order?.manualDiscountRemark,
        order?.paymentInfo?.manualDiscountRemark,
        snapshot.manualDiscountRemark,
        ""
      ) || ""
    ),
    manualDiscountReason: String(
      firstDefined(
        raw.manualDiscountReason,
        handoverDetails.lessDiscountReason,
        order?.manualDiscountReason,
        order?.paymentInfo?.manualDiscountReason,
        snapshot.manualDiscountReason,
        ""
      ) || ""
    ),

    /* NEW: Extra Cost (Payment Settlement) — Category + Reason, editable & saved */
    settlementExtraCost: n(
      firstDefined(
        raw.extraCost,
        order?.paymentInfo?.extraCost,
        order?.extraCost,
        snapshot.extraCost,
        0
      )
    ),
    extraCostCategory: String(
      firstDefined(
        raw.extraCostCategory,
        handoverDetails.extraCostCategory,
        order?.extraCostCategory,
        order?.paymentInfo?.extraCostCategory,
        snapshot.extraCostCategory,
        ""
      ) || ""
    ),
    extraCostReason: String(
      firstDefined(
        raw.extraCostReason,
        handoverDetails.extraCostReason,
        order?.extraCostReason,
        order?.paymentInfo?.extraCostReason,
        snapshot.extraCostReason,
        ""
      ) || ""
    ),

    /* cancel metadata (kept so later saves never wipe it) */
    cancelReason: String(
      firstDefined(
        raw.cancelReason,
        order?.cancelReason,
        snapshot.cancelReason,
        ""
      ) || ""
    ),
    cancelledAt:
      firstDefined(raw.cancelledAt, order?.cancelledAt, snapshot.cancelledAt, "") ||
      "",
    cancelledBy:
      firstDefined(raw.cancelledBy, order?.cancelledBy, snapshot.cancelledBy, "") ||
      "",

    handoverDetails,
    extraPersonDetails,
    complimentaryItems: arrayValue(
      raw.complimentaryItems?.length
        ? raw.complimentaryItems
        : order?.complimentaryItems
    ),
    customFields: arrayValue(
      raw.customFields?.length ? raw.customFields : order?.customFields
    ).map((field) => ({
      id: field.id || makeCustomFieldId(),
      label: field.label || "",
      type: field.type || "text",
      required: Boolean(field.required),
      options: arrayValue(field.options),
      value: field.value || "",
    })),

    transferredBy: raw.transferredBy || order?.transferredBy || "Admin",
    transferredAt:
      raw.transferredAt ||
      order?.transferredAt ||
      raw.createdAt ||
      raw.updatedAt ||
      "",
    status: raw.status || order?.status || "In Staff Management",
    staffState: raw.staffState || order?._rowState || order?.staffState || "active",
    staffNotes: raw.staffNotes || order?.staffNotes || "",
    staffHandoverSavedAt:
      raw.staffHandoverSavedAt || order?.staffHandoverSavedAt || "",
  };

  const pricing = calculateStaffLivePricing(order, baseForm);

  return {
    ...baseForm,
    ...pricing,
    finalAmount: pricing.finalAmount,
    handoverDetails: {
      ...baseForm.handoverDetails,
      managerDiscount: pricing.managerDiscount,
    },
    extraPersonDetails: pricing.extraDetails,
  };
};

const buildStaffHandoverPayload = (order, form = {}) => {
  const pricing = calculateStaffLivePricing(order, form);
  const sourceOrderId = getOrderId(order);
  const snapshot = {
    ...(order?._rawBooking || {}),
    ...(order?.orderSnapshot || {}),
    ...(order?._transferRaw?.orderSnapshot || {}),
  };

  const remark = String(
    firstDefined(form.manualDiscountRemark, order?.manualDiscountRemark, "") || ""
  );
  const reason = String(
    firstDefined(form.manualDiscountReason, order?.manualDiscountReason, "") || ""
  );

  /* NEW */
  const extraCategory = String(
    firstDefined(form.extraCostCategory, order?.extraCostCategory, "") || ""
  );
  const extraReason = String(
    firstDefined(form.extraCostReason, order?.extraCostReason, "") || ""
  );

  const handoverDetails = {
    ...EMPTY_HANDOVER_DETAILS,
    ...(form.handoverDetails || {}),
    managerDiscount: pricing.managerDiscount,
    lessDiscountRemark: remark,
    lessDiscountReason: reason,
    extraCostCategory: extraCategory,
    extraCostReason: extraReason,
  };

  return {
    orderId: sourceOrderId,
    bookingId: order?._bookingId || order?.bookingId || sourceOrderId,
    merchantOrderId: order?.merchantOrderId || "",

    orderSnapshot: snapshot,

    customerName: form.customerName || form.name || order?.name || "",
    name: form.name || form.customerName || order?.name || "",
    whatsapp:
      form.whatsapp || form.primaryContactNumber || order?.whatsapp || "",
    primaryContactNumber:
      form.primaryContactNumber ||
      form.whatsapp ||
      order?.whatsapp ||
      "",
    alternateContactNumber: form.alternateContactNumber || "",
    celebrationName: form.celebrationName || order?.celebrationName || "",

    theater: form.theater || order?.theater || "",
    eventDate: form.eventDate || form.date || order?.date || "",
    date: form.date || form.eventDate || order?.date || "",
    timeSlot: form.timeSlot || order?.timeSlot || "",
    occasion: form.occasion || order?.occasion || "",

    adults: n(form.adults || order?.adults),
    kids: n(form.kids || order?.kids),
    persons:
      pricing.extraDetails.totalPersons || n(order?.adults) + n(order?.kids),

    packageAmount: pricing.packageAmount,
    addonsAmount: pricing.addonsAmount,
    extraPersonAmount: pricing.extraPersonAmount,
    managerDiscount: pricing.managerDiscount,
    otherDiscount: pricing.otherDiscount,
    amount: pricing.finalAmount,
    advancePaid: n(form.advancePaid || order?.advancePaid || ADVANCE_PAID),
    finalAmount: pricing.finalAmount,

    /* Less Discount Remark + Reason saved with booking details */
    manualDiscount: n(
      firstDefined(form.settlementDiscount, order?.manualDiscount, 0)
    ),
    manualDiscountRemark: remark,
    manualDiscountReason: reason,

    /* NEW: Extra Cost Category + Reason saved with booking details */
    extraCost: n(firstDefined(form.settlementExtraCost, order?.extraCost, 0)),
    extraCostCategory: extraCategory,
    extraCostReason: extraReason,

    /* cancel metadata */
    cancelReason: String(
      firstDefined(form.cancelReason, order?.cancelReason, "") || ""
    ),
    cancelledAt: firstDefined(form.cancelledAt, order?.cancelledAt, "") || "",
    cancelledBy: firstDefined(form.cancelledBy, order?.cancelledBy, "") || "",

    handoverDetails,
    extraPersonDetails: pricing.extraDetails,
    complimentaryItems: arrayValue(form.complimentaryItems).filter(Boolean),
    customFields: arrayValue(form.customFields).map((field) => ({
      id: field.id || makeCustomFieldId(),
      label: field.label || "",
      type: field.type || "text",
      required: Boolean(field.required),
      options: arrayValue(field.options),
      value: String(field.value || ""),
    })),

    transferredBy: form.transferredBy || "Admin",
    transferredAt: form.transferredAt || new Date().toISOString(),
    status: form.status || "In Staff Management",
    staffState: form.staffState || "active",
    staffNotes: form.staffNotes || "",
    staffHandoverSavedAt: new Date().toISOString(),
  };
};

const buildTransferRoutePayload = (order, form = {}) => {
  const handover = form.handoverDetails || {};

  return {
    name: form.name || order?.name || "",
    customerName: form.customerName || form.name || order?.name || "",
    whatsapp:
      form.whatsapp || form.primaryContactNumber || order?.whatsapp || "",
    primaryContactNumber:
      form.primaryContactNumber ||
      form.whatsapp ||
      order?.whatsapp ||
      "",
    alternateContactNumber: form.alternateContactNumber || "",
    foodDetails: handover.foodDetails || "",
    cakeMessage: handover.cakeMessage || "",
    cakeFlavor: handover.cakeFlavor || "",
    cakeWeight: handover.cakeWeight || "",
    candleNumber: handover.candleNumber || "",
    sashType: handover.sashType || "",
    crownType: handover.crownType || "",
    decorationTheme: handover.decorationTheme || "",
    managerDiscount: form.managerDiscount || handover.managerDiscount || 0,
    managerRemarks: handover.managerRemarks || "",
    specialInstructions: handover.specialInstructions || "",
    extraPersonDetails: form.extraPersonDetails || {},
    complimentaryItems: form.complimentaryItems || [],
    customFields: form.customFields || [],
    packageAmount: form.packageAmount,
    addonsAmount: form.addonsAmount,
    otherDiscount: form.otherDiscount,
    manualDiscountRemark: String(
      firstDefined(form.manualDiscountRemark, order?.manualDiscountRemark, "") || ""
    ),
    manualDiscountReason: String(
      firstDefined(form.manualDiscountReason, order?.manualDiscountReason, "") || ""
    ),
    /* NEW */
    extraCostCategory: String(
      firstDefined(form.extraCostCategory, order?.extraCostCategory, "") || ""
    ),
    extraCostReason: String(
      firstDefined(form.extraCostReason, order?.extraCostReason, "") || ""
    ),
    cancelReason: String(
      firstDefined(form.cancelReason, order?.cancelReason, "") || ""
    ),
    cancelledAt: firstDefined(form.cancelledAt, order?.cancelledAt, "") || "",
    cancelledBy: firstDefined(form.cancelledBy, order?.cancelledBy, "") || "",
    status: form.status || order?.status || "In Staff Management",
    staffState: form.staffState || order?._rowState || "active",
  };
};

/* ══════════════════════════════════════════════════
   NORMALIZE BOOKING / TRANSFER ROWS
══════════════════════════════════════════════════ */
const normalizeBookingToRow = (b) => {
  if (!b) return null;

  const meta = b.meta || {};
  const adminBlocked = !!meta.adminBlocked;
  const amountRupees = n(b.amount) / 100;
  const addons = resolveAddonSource(b, meta);

  const draft = {
    adults: n(b.adults || meta.adults || meta.numberOfAdults || 2),
    kids: n(b.kids || meta.kids || meta.numberOfKids || 0),
    extraAdults: n(b.extraAdults || meta.extraAdults || 0),
    glassesCount: n(b.glassesCount || meta.glassesCount || 0),
    glass3DCost: n(b.glass3DCost || meta.glass3DCost || 0),
    basePrice: n(b.basePrice || meta.basePrice || 0),
    fixedDecor: n(b.fixedDecor || meta.fixedDecor || 0),
    extraAdultPrice: n(
      b.extraAdultPrice || meta.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE
    ),
    kidPrice: n(b.kidPrice || meta.kidPrice || DEFAULT_KID_PRICE),
    discount: n(b.discount || meta.discount || meta.couponDiscount || 0),
    foodCost: n(b.foodCost || meta.foodCost || 0),
    extraCost: n(
      b.extraCost || meta.extraCost || b.paymentInfo?.extraCost || 0
    ),
    /* NEW */
    extraCostCategory:
      b.extraCostCategory ||
      meta.extraCostCategory ||
      b.paymentInfo?.extraCostCategory ||
      "",
    extraCostReason:
      b.extraCostReason ||
      meta.extraCostReason ||
      b.paymentInfo?.extraCostReason ||
      "",
    manualDiscount: n(b.manualDiscount || meta.manualDiscount || 0),
    manualDiscountRemark:
      b.manualDiscountRemark ||
      meta.manualDiscountRemark ||
      b.paymentInfo?.manualDiscountRemark ||
      "",
    manualDiscountReason:
      b.manualDiscountReason ||
      meta.manualDiscountReason ||
      b.paymentInfo?.manualDiscountReason ||
      "",
    cakes: addons.cakes,
    extraDecors: addons.extraDecors,
    gifts: addons.gifts,
    services: addons.services,
    extraAddons: addons.extraAddons,
  };

  const computed = computeTotal(draft);
  const storedTotal = n(
    addons.total || b.total || meta.total || meta.grandTotal || amountRupees
  );

  const finalTotal = draft.basePrice > 0 ? computed : storedTotal || computed;

  const id = b._id || b.id || b.orderId || b.bookingId || "";

  return {
    _id: id,
    id,
    orderId: id,
    bookingId: id,
    _orderId: id,
    _bookingId: id,
    _rowKey: `booking-${id}`,
    _source: b.transferId ? "reservation-transfer" : "booking",
    _transferId: b.transferId || null,

    merchantOrderId: b.merchantOrderId || "",
    name: b.name || meta.name || (adminBlocked ? "Admin Block" : "—"),
    customerName: b.customerName || b.name || meta.name || "",
    whatsapp: b.whatsapp || meta.whatsapp || "—",
    primaryContactNumber:
      b.primaryContactNumber || b.whatsapp || meta.whatsapp || "—",
    alternateContactNumber: b.alternateContactNumber || "",
    email: b.email || meta.email || "",
    celebrationName: b.celebrationName || meta.celebrationName || "",
    theater: b.theater || meta.theater || "—",
    date: b.date || meta.date || "—",
    eventDate: b.date || meta.date || "—",
    timeSlot: b.slot || b.timeSlot || meta.slot || "—",
    occasion: adminBlocked ? "Blocked" : b.occasion || meta.occasion || "—",

    ...draft,

    persons: n(b.persons) || draft.adults + draft.kids,
    packageAmount: n(b.packageAmount || b.amount || finalTotal),
    addonsAmount: n(b.addonsAmount || 0),
    extraPersonAmount: n(b.extraPersonAmount || 0),
    managerDiscount: n(b.managerDiscount || b.handoverDetails?.managerDiscount),
    otherDiscount: n(b.otherDiscount || 0),
    finalAmount: n(b.finalAmount || finalTotal),
    total: finalTotal,
    advancePaid: adminBlocked
      ? 0
      : n(b.advancePaid || meta.advancePaid || ADVANCE_PAID),

    timestamp: b.createdAt || b.timestamp || b.updatedAt,
    status: adminBlocked
      ? "Blocked"
      : b.status ||
        meta.status ||
        (isSuccessfulBooking(b) ? "Advance Paid" : "Pending"),
    state: b.state,
    paymentInfo: b.paymentInfo || meta.paymentInfo || null,

    staffState: b.staffState || meta.staffState || "",
    staffStateAt: b.staffStateAt || meta.staffStateAt || null,
    staffStateBy: b.staffStateBy || meta.staffStateBy || "",

    /* cancel metadata */
    cancelReason: b.cancelReason || meta.cancelReason || "",
    cancelledAt: b.cancelledAt || meta.cancelledAt || "",
    cancelledBy: b.cancelledBy || meta.cancelledBy || "",

    handoverDetails: b.handoverDetails || {},
    extraPersonDetails: b.extraPersonDetails || {},
    complimentaryItems: b.complimentaryItems || [],
    customFields: b.customFields || [],
    staffNotes: b.staffNotes || "",

    transferredBy: b.transferredBy || "",
    transferredAt: b.transferredAt || null,
    staffHandoverSavedAt: b.staffHandoverSavedAt || null,

    frozen: !!(
      b.frozen ||
      meta.frozen ||
      b.permanentlyLocked ||
      meta.permanentlyLocked
    ),
    frozenAt: b.frozenAt || meta.frozenAt || null,
    frozenBy: b.frozenBy || meta.frozenBy || null,
    permanentlyLocked: !!(b.permanentlyLocked || meta.permanentlyLocked),

    isAdminBlocked: adminBlocked,
    phonepeOrderId: b.phonepeOrderId || null,
    _rawMeta: meta,
    _rawBooking: b,
    orderSnapshot: b.orderSnapshot || b,
    _dbCached: addons.from === "cache",
  };
};

const normalizeTransferToRow = (t) => {
  if (!t) return null;

  const snapshot = t.orderSnapshot || {};
  const meta = snapshot.meta || {};

  const addonHost = {
    addonsCache: t.addonsCache || snapshot.addonsCache || meta.addonsCache,
    cakes: t.cakes || t.addons?.cakes || snapshot.cakes || meta.cakes || [],
    extraDecors:
      t.extraDecors ||
      t.addons?.extraDecors ||
      snapshot.extraDecors ||
      meta.extraDecors ||
      [],
    gifts: t.gifts || t.addons?.gifts || snapshot.gifts || meta.gifts || [],
    services:
      t.services ||
      t.addons?.services ||
      snapshot.services ||
      meta.services ||
      [],
    extraAddons:
      t.extraAddons ||
      t.addons?.extraAddons ||
      snapshot.extraAddons ||
      meta.extraAddons ||
      [],
  };

  const addons = resolveAddonSource(addonHost, meta);

  const adults = n(t.adults || snapshot.adults || meta.adults || 0);
  const kids = n(t.kids || snapshot.kids || meta.kids || 0);

  const draft = {
    adults,
    kids,
    extraAdults: n(snapshot.extraAdults || meta.extraAdults || 0),
    glassesCount: n(snapshot.glassesCount || meta.glassesCount || 0),
    glass3DCost: n(snapshot.glass3DCost || meta.glass3DCost || 0),
    basePrice: n(snapshot.basePrice || meta.basePrice || t.packageAmount || 0),
    fixedDecor: n(snapshot.fixedDecor || meta.fixedDecor || 0),
    extraAdultPrice: n(
      snapshot.extraAdultPrice || meta.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE
    ),
    kidPrice: n(snapshot.kidPrice || meta.kidPrice || DEFAULT_KID_PRICE),
    discount: n(snapshot.discount || meta.discount || 0),
    foodCost: n(snapshot.foodCost || t.foodCost || 0),
    extraCost: n(
      snapshot.extraCost || t.extraCost || t.paymentInfo?.extraCost || 0
    ),
    /* NEW */
    extraCostCategory:
      t.extraCostCategory ||
      snapshot.extraCostCategory ||
      t.handoverDetails?.extraCostCategory ||
      t.paymentInfo?.extraCostCategory ||
      "",
    extraCostReason:
      t.extraCostReason ||
      snapshot.extraCostReason ||
      t.handoverDetails?.extraCostReason ||
      t.paymentInfo?.extraCostReason ||
      "",
    manualDiscount: n(snapshot.manualDiscount || t.manualDiscount || 0),
    manualDiscountRemark:
      t.manualDiscountRemark ||
      snapshot.manualDiscountRemark ||
      t.handoverDetails?.lessDiscountRemark ||
      t.paymentInfo?.manualDiscountRemark ||
      "",
    manualDiscountReason:
      t.manualDiscountReason ||
      snapshot.manualDiscountReason ||
      t.handoverDetails?.lessDiscountReason ||
      t.paymentInfo?.manualDiscountReason ||
      "",
    cakes: addons.cakes,
    extraDecors: addons.extraDecors,
    gifts: addons.gifts,
    services: addons.services,
    extraAddons: addons.extraAddons,
  };

  const formulaTotal = Math.max(
    0,
    n(t.packageAmount || t.amount) +
      n(t.extraPersonAmount || t.extraPersonDetails?.extraPersonAmount) +
      n(t.addonsAmount) -
      n(t.managerDiscount || t.handoverDetails?.managerDiscount) -
      n(t.otherDiscount)
  );

  const snapshotTotal = n(
    snapshot.total ||
      meta.total ||
      snapshot.grandTotal ||
      meta.grandTotal ||
      addons.total
  );

  const computed = computeTotal(draft);

  const total =
    n(t.finalAmount) ||
    formulaTotal ||
    snapshotTotal ||
    computed ||
    n(t.amount) ||
    n(t.packageAmount);

  const sourceOrderId =
    t.orderId ||
    snapshot._id ||
    snapshot.id ||
    snapshot.orderId ||
    snapshot.bookingId ||
    "";

  return {
    _id: t._id,
    id: t._id,
    _transferId: t._id,
    transferId: t._id,
    _orderId: sourceOrderId,
    orderId: sourceOrderId,
    bookingId: snapshot.bookingId || sourceOrderId,
    _bookingId: snapshot.bookingId || sourceOrderId,
    _rowKey: `transfer-${t._id}`,
    _source: "transfer",

    merchantOrderId:
      t.merchantOrderId ||
      snapshot.merchantOrderId ||
      meta.merchantOrderId ||
      "",
    name:
      t.name ||
      t.customerName ||
      snapshot.name ||
      snapshot.customerName ||
      meta.name ||
      "—",
    customerName:
      t.customerName ||
      t.name ||
      snapshot.customerName ||
      snapshot.name ||
      meta.name ||
      "",
    whatsapp:
      t.whatsapp ||
      t.primaryContactNumber ||
      snapshot.whatsapp ||
      snapshot.primaryContactNumber ||
      meta.whatsapp ||
      "—",
    primaryContactNumber:
      t.primaryContactNumber ||
      t.whatsapp ||
      snapshot.primaryContactNumber ||
      snapshot.whatsapp ||
      meta.whatsapp ||
      "—",
    alternateContactNumber:
      t.alternateContactNumber || snapshot.alternateContactNumber || "",
    email: t.email || snapshot.email || meta.email || "",
    celebrationName:
      t.celebrationName ||
      snapshot.celebrationName ||
      meta.celebrationName ||
      "",
    theater: t.theater || snapshot.theater || meta.theater || "—",
    date:
      t.eventDate ||
      t.date ||
      snapshot.date ||
      snapshot.eventDate ||
      meta.date ||
      "—",
    eventDate:
      t.eventDate ||
      t.date ||
      snapshot.date ||
      snapshot.eventDate ||
      meta.date ||
      "—",
    timeSlot:
      t.timeSlot ||
      t.slot ||
      snapshot.timeSlot ||
      snapshot.slot ||
      meta.slot ||
      "—",
    occasion: t.occasion || snapshot.occasion || meta.occasion || "—",

    ...draft,

    persons: n(t.persons || snapshot.persons || adults + kids),
    packageAmount: n(t.packageAmount || t.amount || snapshot.total || total),
    addonsAmount: n(t.addonsAmount || 0),
    extraPersonAmount: n(
      t.extraPersonAmount || t.extraPersonDetails?.extraPersonAmount
    ),
    managerDiscount: n(t.managerDiscount || t.handoverDetails?.managerDiscount),
    otherDiscount: n(t.otherDiscount),
    finalAmount: n(t.finalAmount || total),
    total,
    advancePaid: n(t.advancePaid || snapshot.advancePaid || ADVANCE_PAID),

    timestamp: t.transferredAt || t.createdAt || t.updatedAt || snapshot.createdAt,
    status: t.status || "In Staff Management",
    state: "TRANSFERRED",
    paymentInfo: t.paymentInfo || snapshot.paymentInfo || meta.paymentInfo || null,

    staffState: t.staffState || snapshot.staffState || "",
    staffStateAt: t.staffStateAt || snapshot.staffStateAt || null,
    staffStateBy: t.staffStateBy || snapshot.staffStateBy || "",

    /* cancel metadata */
    cancelReason: t.cancelReason || snapshot.cancelReason || "",
    cancelledAt: t.cancelledAt || snapshot.cancelledAt || "",
    cancelledBy: t.cancelledBy || snapshot.cancelledBy || "",

    handoverDetails: t.handoverDetails || snapshot.handoverDetails || {},
    extraPersonDetails:
      t.extraPersonDetails || snapshot.extraPersonDetails || {},
    complimentaryItems: t.complimentaryItems || snapshot.complimentaryItems || [],
    customFields: t.customFields || snapshot.customFields || [],
    staffNotes: t.staffNotes || snapshot.staffNotes || "",

    transferredBy: t.transferredBy || snapshot.transferredBy || "Admin",
    transferredAt: t.transferredAt || t.createdAt || "",
    staffHandoverSavedAt: t.staffHandoverSavedAt || "",

    frozen: !!(t.frozen || snapshot.frozen || snapshot.permanentlyLocked),
    frozenAt: t.frozenAt || snapshot.frozenAt || null,
    frozenBy: t.frozenBy || snapshot.frozenBy || null,
    permanentlyLocked: !!(
      t.permanentlyLocked || snapshot.permanentlyLocked
    ),

    orderSnapshot: snapshot,
    _rawTransfer: t,
    _transferRaw: t,
    _rawBooking: snapshot,
    _rawMeta: meta,
    _dbCached: addons.from === "cache",
    _fromTransferList: true,
  };
};

const mergeRowsTransferFirst = (transferRows, bookingRows) => {
  const seen = new Set();
  const out = [];

  const push = (row) => {
    if (!row) return;

    const key = identityKeyForRow(row);
    if (seen.has(key)) return;

    seen.add(key);
    out.push(row);
  };

  transferRows.forEach(push);
  bookingRows.forEach(push);

  return out;
};

/* ══════════════════════════════════════════════════
   WHATSAPP / PRINT / PDF / CSV
══════════════════════════════════════════════════ */
const buildWAMessage = (o) => {
  const addonLines = buildAddonRows(o).map(
    (x) => `${x.emoji} ${x.name}${qtyLabel(x)} - ${money(itemTotal(x))}`
  );

  const total = n(o.total || computeTotal(o));
  const advance = n(o.advancePaid || ADVANCE_PAID);
  const balance = Math.max(0, total - advance);

  const messageText = [
    "🎬 HexaPlay - Booking Confirmation",
    "━━━━━━━━━━━━━━━━━━━━",
    "",
    "👤 Customer",
    `Name: ${o.name || "-"}`,
    `Celebrating: ${o.celebrationName || "-"}`,
    `WhatsApp: ${o.whatsapp || "-"}`,
    `Email: ${o.email || "N/A"}`,
    "",
    "🏟️ Booking",
    `Theater: ${o.theater || "-"}`,
    `Date: ${formatDisplayDate(o.date)}`,
    `Time Slot: ${o.timeSlot || "-"}`,
    `Occasion: ${o.occasion || "-"}`,
    "",
    `👥 Guests: ${n(o.adults)} Adults${
      n(o.kids) > 0 ? `, ${n(o.kids)} Kids` : ""
    }`,
    ...(n(o.glassesCount) > 0 ? [`🥽 3D Glasses: ${n(o.glassesCount)}`] : []),
    "",
    "💵 Pricing",
    `Grand Total: ${money(total)}`,
    `Advance Paid: ${money(advance)}`,
    `Balance Due: ${money(balance)}`,
    ...(addonLines.length ? ["", "🛒 Add-ons:", ...addonLines] : []),
    "",
    `Order ID: ${o.merchantOrderId || getOrderId(o) || "-"}`,
    "",
    "Thank you for choosing HexaPlay! 🎉",
  ].join("\n");

  return encodeWhatsAppText(messageText);
};

const buildBarcodeSVG = (seed) => {
  const s = String(seed || "HEXAPLAY");
  let pos = 0;
  let bars = "";

  for (let i = 0; i < s.length * 4; i++) {
    const c = s.charCodeAt(i % s.length) + i * 7;
    const w = 1 + (c % 3);

    if (i % 2 === 0) {
      bars += `<rect x="${pos}" y="0" width="${w}" height="34" fill="#0b1437"/>`;
    }

    pos += w + 1;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pos} 34" width="170" height="34" preserveAspectRatio="none">${bars}</svg>`;
};

/**
 * Builds and prints the "OFFICIAL BOOKING RECEIPT" for a single order.
 * Fully self-contained: derives handover/extra-person/payment data
 * straight from `o` via normalizeStaffHandover.
 */
const printRowPDF = (o) => {
  const form = normalizeStaffHandover(o, o);
  const hd = form.handoverDetails || {};
  const ep = form.extraPersonDetails || computeStaffExtraPersonDetails({}, o);
  const paymentInfo = o.paymentInfo || null;

  const grand = n(form.finalAmount) || n(o.total) || computeTotal(o);
  const advance = n(form.advancePaid || o.advancePaid || ADVANCE_PAID);
  const balance = Math.max(0, grand - advance);
  const addons = buildAddonRows(o);
  const customFields = arrayValue(form.customFields).filter(
    (f) => f && String(f.label || "").trim()
  );
  const complimentary = arrayValue(form.complimentaryItems).filter(Boolean);

  const discountRemark =
    form.manualDiscountRemark || paymentInfo?.manualDiscountRemark || "";
  const discountReason =
    form.manualDiscountReason || paymentInfo?.manualDiscountReason || "";

  /* NEW */
  const extraCostCategory =
    form.extraCostCategory || paymentInfo?.extraCostCategory || "";
  const extraCostReason =
    form.extraCostReason || paymentInfo?.extraCostReason || "";

  const cancelReason = form.cancelReason || o.cancelReason || "";

  const refId = String(
    o.merchantOrderId || getOrderId(o) || getTransferId(o) || "HEXAPLAY"
  );
  const receiptNo = `HP-${refId
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(-8)
    .toUpperCase()}`;

  const isCancelled = String(o._rowState || o.staffState || "").toLowerCase() === "cancelled";

  const stampText = isCancelled
    ? "CANCELLED"
    : paymentInfo
    ? "PAID IN FULL"
    : isOrderLocked(o)
    ? "FROZEN"
    : "ADVANCE PAID";

  const stampClass = isCancelled ? "cx" : paymentInfo ? "paid" : "adv";

  /* ── local render helpers ── */
  const kv = (label, value, wide) =>
    `<div class="kv${wide ? " wide" : ""}"><span>${escapeHTML(
      label
    )}</span><b>${escapeHTML(hasValue(value) ? value : "—")}</b></div>`;

  const sec = (num, icon, title, body) =>
    `<section class="sec"><div class="sec-h"><i>${num}</i><h3>${icon} ${escapeHTML(
      title
    )}</h3></div>${body}</section>`;

  const priceRow = (label, value, cls = "") =>
    `<tr class="${cls}"><td>${label}</td><td class="amt">${value}</td></tr>`;

  const addonRows = addons.length
    ? addons
        .map(
          (a, i) =>
            `<tr><td>${i + 1}</td><td>${escapeHTML(a.emoji)} ${escapeHTML(
              a.name
            )} <em>${escapeHTML(a.category)}</em></td><td class="c">${safeQty(
              a
            )}</td><td class="r">${money(a.price)}</td><td class="r"><b>${money(
              itemTotal(a)
            )}</b></td></tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="muted">No add-ons selected</td></tr>`;

  const customerBody = `<div class="kv-grid">
      ${kv("Customer Name", form.name || o.name)}
      ${kv(
        "Primary Contact",
        form.primaryContactNumber || form.whatsapp || o.whatsapp
      )}
      ${kv("Alternate Contact", form.alternateContactNumber)}
      ${kv("Email", o.email)}
      ${kv("Celebration Name", form.celebrationName || o.celebrationName)}
      ${kv("Occasion", form.occasion || o.occasion)}
      ${kv("Food Details", hd.foodDetails, true)}
    </div>`;

  const cakeBody = `<div class="kv-grid">
      ${kv("Cake Message", hd.cakeMessage)}
      ${kv("Cake Flavor", hd.cakeFlavor)}
      ${kv("Cake Weight", hasValue(hd.cakeWeight) ? `${hd.cakeWeight} kg` : "")}
      ${kv("Candle Number", hd.candleNumber)}
     
      ${kv("Decoration Theme", hd.decorationTheme, true)}
    </div>`;

  const guestBody = `<div class="kv-grid four">
      ${kv("Total Persons", ep.totalPersons || form.persons)}
      ${kv("Package Included", ep.packageIncludedPersons)}
      ${kv("Extra Persons", ep.extraPersons)}
      ${kv("Complimentary", ep.complimentaryPersons)}
      ${kv("Chargeable", ep.chargeablePersons)}
      ${kv("Standard Cost / Person", money(ep.standardCostPerPerson))}
      ${kv("Discount / Person", money(ep.discountPerPerson))}
      ${kv("Final Cost / Person", money(ep.finalCostPerPerson))}
      ${kv("Adults", n(form.adults || o.adults))}
      ${kv("Kids", n(form.kids || o.kids))}
      ${kv("3D Glasses", n(o.glassesCount))}
      ${kv("Extra Person Amount", money(ep.extraPersonAmount))}
      ${ep.remarks ? kv("Extra Person Remarks", ep.remarks, true) : ""}
    </div>`;

  const complimentaryBody = complimentary.length
    ? `<div class="chips">${complimentary
        .map((c) => `<span>✔ ${escapeHTML(c)}</span>`)
        .join("")}</div>`
    : `<div class="muted-line">No complimentary items selected</div>`;

  const customBody = customFields.length
    ? `<div class="kv-grid">${customFields
        .map((f) => kv(`${f.label}${f.required ? " *" : ""}`, f.value))
        .join("")}</div>`
    : "";

  const notesBody = `<div class="notes">
      <div><span>Special Instructions</span><p>${escapeHTML(
        hd.specialInstructions || "—"
      )}</p></div>
      <div><span>Manager Remarks</span><p>${escapeHTML(
        hd.managerRemarks || "—"
      )}</p></div>
      <div><span>Staff Notes</span><p>${escapeHTML(
        form.staffNotes || "—"
      )}</p></div>
    </div>`;

  const pricingBody = `<table class="price">
      ${priceRow("Package Amount", money(form.packageAmount || o.packageAmount))}
      ${priceRow(
        `Extra Person Amount <em>(${ep.chargeablePersons} × ${money(
          ep.finalCostPerPerson
        )})</em>`,
        `+ ${money(ep.extraPersonAmount)}`
      )}
      ${priceRow("Add-ons / Other Charges", `+ ${money(form.addonsAmount)}`)}
      ${priceRow(
        "Manager Discount",
        `− ${money(form.managerDiscount)}`,
        "neg"
      )}
      ${priceRow("Other Discount", `− ${money(form.otherDiscount)}`, "neg")}
      ${priceRow("Final Booking Total", money(grand), "grand")}
      ${priceRow("Advance Paid", `− ${money(advance)}`, "adv")}
      ${priceRow("Balance Due", money(balance), "bal")}
    </table>`;

  const paymentBody = paymentInfo
    ? `<table class="price">
        ${priceRow("Grand Total", money(paymentInfo.grandTotal ?? grand))}
        ${priceRow(
          "Advance Paid",
          `− ${money(paymentInfo.advancePaid ?? advance)}`,
          "adv"
        )}
        ${priceRow(
          "Actual Balance",
          money(paymentInfo.actualBalance ?? balance)
        )}
        ${priceRow("Food Cost", `+ ${money(paymentInfo.foodCost || 0)}`)}
        ${priceRow(
          `Extra Cost${
            extraCostCategory ? ` <em>${escapeHTML(extraCostCategory)}</em>` : ""
          }`,
          `+ ${money(paymentInfo.extraCost || 0)}`
        )}
        ${
          extraCostReason
            ? priceRow("Extra Cost Reason", escapeHTML(extraCostReason))
            : ""
        }
        ${priceRow(
          `Less Discount${
            discountRemark ? ` <em>${escapeHTML(discountRemark)}</em>` : ""
          }`,
          `− ${money(paymentInfo.manualDiscount || 0)}`,
          "neg"
        )}
        ${
          discountReason
            ? priceRow("Discount Reason", escapeHTML(discountReason))
            : ""
        }
        ${priceRow(
          "Settlement Balance",
          money(paymentInfo.settlementBalance ?? balance)
        )}
        ${priceRow(
          "Payment Mode",
          (paymentInfo.modes || [])
            .map((m) => `${escapeHTML(m.mode)} ${money(m.amount)}`)
            .join(" + ") || "—"
        )}
        ${priceRow("Payment Collected", money(paymentInfo.totalPaid || 0))}
        ${
          paymentInfo.gstApplied
            ? priceRow(
                "GST Number",
                escapeHTML(paymentInfo.gstNumber || "N/A")
              ) + priceRow("GST 18%", `+ ${money(paymentInfo.gstAmount || 0)}`)
            : priceRow("GST", "Not Applied")
        }
        ${priceRow(
          "Final Amount Paid",
          money(paymentInfo.finalAmount || 0),
          "grand"
        )}
        ${priceRow("Remaining Due", money(paymentInfo.remainingDue || 0), "bal")}
        ${priceRow("Paid At", escapeHTML(formatTS(paymentInfo.paidAt)))}
      </table>`
    : `<div class="muted-line">Payment settlement not confirmed yet</div>`;

  const cancelSection = cancelReason
    ? `<section class="sec"><div class="sec-h"><i>!</i><h3>❌ Cancellation Reason</h3></div>
        <div class="cxbox"><p>${escapeHTML(cancelReason)}</p>
        <small>Cancelled ${escapeHTML(formatTS(form.cancelledAt || o.cancelledAt))} by ${escapeHTML(
        form.cancelledBy || o.cancelledBy || "Admin"
      )}</small></div></section>`
    : "";

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>HexaPlay Receipt ${escapeHTML(receiptNo)}</title>
<style>
  @page{size:A4;margin:10mm}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:"Segoe UI",Roboto,Arial,sans-serif;color:#0f172a;background:#fff;font-size:11.5px;line-height:1.45;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .page{max-width:800px;margin:0 auto;border:1px solid #e2e8f0;border-radius:16px;overflow:hidden;position:relative}
  .wm{position:absolute;top:46%;left:50%;transform:translate(-50%,-50%) rotate(-24deg);font-size:84px;font-weight:900;letter-spacing:8px;color:rgba(15,23,42,.035);white-space:nowrap;pointer-events:none;z-index:0}

  .hdr{position:relative;padding:28px 34px 30px;color:#fff;background:linear-gradient(135deg,#0b1437 0%,#1e1b6b 55%,#3b2fb5 100%);overflow:hidden}
  .hdr:before{content:"";position:absolute;right:-60px;top:-80px;width:240px;height:240px;border-radius:50%;background:rgba(201,162,75,.18)}
  .hdr:after{content:"";position:absolute;right:70px;bottom:-90px;width:180px;height:180px;border-radius:50%;background:rgba(255,255,255,.06)}
  .hdr-top{display:flex;justify-content:space-between;align-items:flex-start;position:relative;z-index:1}
  .brand h1{font-size:28px;letter-spacing:1px;font-weight:800}
  .brand h1 span{color:#e7c56b}
  .brand p{margin-top:4px;font-size:10.5px;letter-spacing:3px;text-transform:uppercase;color:#cbd5ff}
  .rc{text-align:right}
  .rc small{display:block;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#aab4ff}
  .rc b{display:block;font-size:17px;letter-spacing:1px;color:#e7c56b;margin:2px 0 6px}
  .rc span{font-size:10.5px;color:#dbe1ff}
  .gold-line{height:5px;background:linear-gradient(90deg,#c9a24b,#f3dc9b,#c9a24b)}
  .title-bar{position:relative;z-index:1;margin-top:18px;display:flex;justify-content:space-between;align-items:flex-end}
  .title-bar h2{font-size:15px;font-weight:700;letter-spacing:.5px}
  .stamp{border:2.5px solid;border-radius:8px;padding:5px 12px;font-weight:900;letter-spacing:2px;font-size:12px;transform:rotate(-4deg);background:rgba(255,255,255,.96)}
  .stamp.paid{color:#15803d;border-color:#15803d}
  .stamp.adv{color:#b45309;border-color:#b45309}
  .stamp.cx{color:#b91c1c;border-color:#b91c1c}

  .ticket{position:relative;z-index:1;margin:18px 28px 6px;display:grid;grid-template-columns:repeat(4,1fr);border:1.5px dashed #c9a24b;border-radius:14px;background:linear-gradient(180deg,#fffdf6,#fff8e6)}
  .ticket:before,.ticket:after{content:"";position:absolute;top:50%;width:16px;height:16px;border-radius:50%;background:#fff;border:1.5px dashed #c9a24b;transform:translateY(-50%)}
  .ticket:before{left:-9px}.ticket:after{right:-9px}
  .ticket div{padding:13px 16px;border-right:1px dashed #e5d29a}
  .ticket div:last-child{border-right:0}
  .ticket span{display:block;font-size:8.5px;letter-spacing:1.6px;text-transform:uppercase;color:#92722a;font-weight:700}
  .ticket b{display:block;margin-top:3px;font-size:13.5px;color:#0b1437}

  .body{position:relative;z-index:1;padding:14px 28px 8px}
  .sec{margin-bottom:14px;page-break-inside:avoid}
  .sec-h{display:flex;align-items:center;gap:9px;margin-bottom:8px;padding-bottom:6px;border-bottom:1.5px solid #e8ebf5}
  .sec-h i{font-style:normal;width:22px;height:22px;border-radius:7px;background:#0b1437;color:#e7c56b;font-weight:800;font-size:11px;display:flex;align-items:center;justify-content:center}
  .sec-h h3{font-size:11.5px;text-transform:uppercase;letter-spacing:1.4px;color:#1e1b6b}

  .kv-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
  .kv-grid.four{grid-template-columns:repeat(4,1fr)}
  .kv{padding:8px 11px;border-radius:9px;background:#f6f7fc;border-left:3px solid #c9a24b}
  .kv.wide{grid-column:1/-1}
  .kv span{display:block;font-size:8.5px;letter-spacing:1.1px;text-transform:uppercase;color:#64748b;font-weight:700}
  .kv b{display:block;margin-top:2px;font-size:12px;color:#0f172a;word-break:break-word}

  .two{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .chips{display:flex;flex-wrap:wrap;gap:6px}
  .chips span{padding:5px 11px;border-radius:99px;background:#ecfdf5;color:#15803d;border:1px solid #bbf7d0;font-weight:700;font-size:10.5px}
  .muted-line{padding:10px;text-align:center;color:#94a3b8;background:#f8fafc;border-radius:9px;border:1px dashed #cbd5e1}
  .notes{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
  .notes div{padding:9px 11px;border-radius:9px;background:#fffbeb;border:1px solid #fde68a}
  .notes span{font-size:8.5px;letter-spacing:1.1px;text-transform:uppercase;color:#92400e;font-weight:800}
  .notes p{margin-top:3px;white-space:pre-wrap;word-break:break-word}
  .cxbox{padding:10px 13px;border-radius:10px;background:#fef2f2;border:1px solid #fecaca;color:#7f1d1d}
  .cxbox p{white-space:pre-wrap;word-break:break-word}
  .cxbox small{display:block;margin-top:5px;color:#991b1b}

  table{width:100%;border-collapse:collapse}
  .items{border:1px solid #e2e8f0;border-radius:10px;overflow:hidden}
  .items th{background:#0b1437;color:#e7c56b;font-size:9px;letter-spacing:1.2px;text-transform:uppercase;padding:8px 10px;text-align:left}
  .items td{padding:8px 10px;border-bottom:1px solid #eef2f7}
  .items tr:nth-child(even) td{background:#fafbff}
  .items em,.price em{font-style:normal;font-size:9px;color:#64748b;background:#eef2ff;padding:1px 6px;border-radius:99px;margin-left:4px}
  .c{text-align:center}.r{text-align:right}
  .muted{text-align:center;color:#94a3b8;padding:12px !important}

  .price{border:1px solid #e2e8f0;border-radius:10px;overflow:hidden}
  .price td{padding:8px 13px;border-bottom:1px solid #eef2f7}
  .price .amt{text-align:right;font-weight:800;word-break:break-word}
  .price tr:last-child td{border-bottom:0}
  .price .neg td{color:#dc2626}
  .price .adv td{background:#ecfdf5;color:#15803d;font-weight:800}
  .price .grand td{background:#0b1437;color:#fff;font-size:13px;font-weight:800}
  .price .grand .amt{color:#e7c56b}
  .price .bal td{background:#fff7ed;color:#c2410c;font-weight:800}

  .sign{display:grid;grid-template-columns:1fr 1fr 190px;gap:22px;align-items:end;padding:6px 28px 16px;position:relative;z-index:1}
  .sign div.line{border-top:1.5px solid #94a3b8;padding-top:5px;text-align:center;font-size:9.5px;color:#64748b;text-transform:uppercase;letter-spacing:1px}
  .sign .bc{text-align:center}
  .sign .bc small{display:block;font-size:8.5px;letter-spacing:2px;color:#64748b;margin-top:2px}
  .terms{margin:0 28px 14px;padding:10px 14px;border-radius:10px;background:#f8fafc;border:1px solid #e2e8f0;font-size:9.5px;color:#64748b;position:relative;z-index:1}
  .terms b{color:#1e1b6b}
  .thanks{margin:0 28px 16px;text-align:center;padding:12px;border-radius:12px;color:#fff;font-weight:700;letter-spacing:.4px;background:linear-gradient(135deg,#1e1b6b,#3b2fb5);position:relative;z-index:1}
  .ftr{display:flex;justify-content:space-between;padding:11px 28px;background:#0b1437;color:#aab4ff;font-size:10px;position:relative;z-index:1}
  .ftr b{color:#e7c56b}
  @media print{.page{border:0;border-radius:0}}
</style></head><body>
<div class="page">
  <div class="wm">HEXAPLAY</div>

  <div class="hdr">
    <div class="hdr-top">
      <div class="brand"><h1>🎬 Hexa<span>Play</span></h1><p>Private Theater Experience</p></div>
      <div class="rc">
        <small>Receipt No.</small>
        <b>${escapeHTML(receiptNo)}</b>
        <span>Issued: ${escapeHTML(formatTS(new Date().toISOString()))}</span><br/>
        <span>Ref: ${escapeHTML(refId)}</span>
      </div>
    </div>
    <div class="title-bar">
      <h2>OFFICIAL BOOKING RECEIPT</h2>
      <div class="stamp ${stampClass}">${stampText}</div>
    </div>
  </div>
  <div class="gold-line"></div>

  <div class="ticket">
    <div><span>Theater</span><b>${escapeHTML(form.theater || o.theater || "—")}</b></div>
    <div><span>Event Date</span><b>${escapeHTML(
      formatDisplayDate(form.date || o.date)
    )}</b></div>
    <div><span>Time Slot</span><b>${escapeHTML(
      form.timeSlot || o.timeSlot || "—"
    )}</b></div>
    <div><span>Guests</span><b>${n(form.adults || o.adults)} Adults${
      n(form.kids || o.kids) ? ` · ${n(form.kids || o.kids)} Kids` : ""
    }</b></div>
  </div>

  <div class="body">
    ${sec("1", "👤", "Customer & Celebration", customerBody)}
    ${sec("2", "🎂", "Cake, Sash, Crown & Decoration", cakeBody)}
    ${sec("3", "👥", "Guests & Extra Person Details", guestBody)}
    <div class="two">
      ${sec("4", "🎁", "Complimentary Items", complimentaryBody)}
      ${sec(
        "5",
        "📌",
        "Booking Status",
        `<div class="kv-grid" style="grid-template-columns:1fr 1fr">
          ${kv("Status", isOrderLocked(o) ? "Frozen" : form.status || o.status)}
          ${kv("Transferred By", form.transferredBy || "Admin")}
          ${kv("Transferred At", formatTS(form.transferredAt || o.transferredAt || o.timestamp))}
          ${kv("Last Saved", formatTS(form.staffHandoverSavedAt))}
        </div>`
      )}
    </div>
    ${cancelSection}
    ${customFields.length ? sec("6", "☑️", "Custom Fields", customBody) : ""}
    ${sec("7", "📝", "Instructions & Notes", notesBody)}
    ${sec(
      "8",
      "🛒",
      "Add-ons & Items",
      `<table class="items"><thead><tr><th>#</th><th>Item</th><th class="c">Qty</th><th class="r">Rate</th><th class="r">Amount</th></tr></thead><tbody>${addonRows}</tbody></table>`
    )}
    <div class="two">
      ${sec("9", "🧾", "Pricing Summary", pricingBody)}
      ${sec("10", "💳", "Payment Settlement", paymentBody)}
    </div>
  </div>

  <div class="terms"><b>Terms:</b> Advance amount is non-refundable. Please arrive 10 minutes before your slot. Extra persons, add-ons and food are charged as per the above summary. This is a computer-generated receipt.</div>

  <div class="sign">
    <div class="line">Customer Signature</div>
    <div class="line">Authorized Signatory</div>
    <div class="bc">${buildBarcodeSVG(refId)}<small>${escapeHTML(receiptNo)}</small></div>
  </div>

  <div class="thanks">🎉 Thank you for choosing HexaPlay — Making every moment cinematic!</div>
  <div class="ftr"><span>HexaPlay Private Theater</span><span>Total <b>${money(grand)}</b> · Advance <b>${money(advance)}</b> · Balance <b>${money(balance)}</b></span></div>
</div>
</body></html>`;

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;";

  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }, 600);
};

const downloadPDF = (rows, filterLabel, showReason = false) => {
  const totalAdvance = rows.reduce(
    (a, o) => a + n(o.advancePaid || ADVANCE_PAID),
    0
  );

  const totalGrand = rows.reduce(
    (a, o) => a + n(o.total || computeTotal(o)),
    0
  );

  const tableRows = rows
    .map(
      (o, i) =>
        `<tr>
          <td>${i + 1}</td>
          <td><b>${escapeHTML(o.name || "—")}</b><br/><span>${escapeHTML(
          o.whatsapp || "—"
        )}</span></td>
          <td>${escapeHTML(o.theater || "—")}</td>
          <td>${escapeHTML(formatDisplayDate(o.date))}<br/><span>${escapeHTML(
          o.timeSlot || "—"
        )}</span></td>
          <td>${escapeHTML(o.occasion || "—")}</td>
          <td>${n(o.adults)}A${n(o.kids) ? ` / ${n(o.kids)}K` : ""}</td>
          <td>${escapeHTML(addonText(o))}</td>
          <td><b>${money(
            o.total || computeTotal(o)
          )}</b><br/><span>${money(
          o.advancePaid || ADVANCE_PAID
        )} advance</span></td>
          <td>${escapeHTML(o.status || "—")}</td>
          ${
            showReason
              ? `<td>${escapeHTML(o.cancelReason || "—")}<br/><span>${escapeHTML(
                  o.cancelledAt ? formatTS(o.cancelledAt) : ""
                )}</span></td>`
              : ""
          }
        </tr>`
    )
    .join("");

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>HexaPlay Staff Management</title>
  <style>
    body{font-family:Arial,sans-serif;margin:0;color:#111827;font-size:11px}
    .cover{padding:28px 34px;background:linear-gradient(135deg,#18115f,#2563eb);color:#fff}h1{margin:0}
    table{width:100%;border-collapse:collapse}
    th{background:#f8fafc;color:#64748b;text-transform:uppercase;font-size:9px;padding:10px;border-bottom:2px solid #e5e7eb;text-align:left}
    td{padding:9px 10px;border-bottom:1px solid #eef2f7;vertical-align:top;word-break:break-word}span{color:#64748b;font-size:10px}
    .footer{padding:14px 24px;background:#f8fafc;display:flex;justify-content:space-between}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>
  <div class="cover"><h1>🎬 HexaPlay – Staff Management</h1><p>${escapeHTML(
    filterLabel
  )} · Transfer + Payment Success Records</p></div>
  <table><thead><tr><th>#</th><th>Customer</th><th>Theater</th><th>Date / Slot</th><th>Occasion</th><th>Guests</th><th>Add-ons</th><th>Amount</th><th>Status</th>${
    showReason ? "<th>Cancel Reason</th>" : ""
  }</tr></thead>
  <tbody>${tableRows}</tbody></table>
  <div class="footer"><b>${rows.length} bookings</b><b>Advance ${money(
    totalAdvance
  )} · Total ${money(totalGrand)}</b></div>
  </body></html>`;

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;";

  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }, 500);
};

/* CSV export (Excel-friendly, UTF-8 BOM) */
const downloadCSV = (rows, label = "staff-records") => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  const header = [
    "#",
    "Customer",
    "Mobile",
    "Theater",
    "Event Date",
    "Time Slot",
    "Occasion",
    "Adults",
    "Kids",
    "Total",
    "Advance",
    "Status",
    "Extra Cost",
    "Extra Cost Category",
    "Extra Cost Reason",
    "Less Discount",
    "Less Discount Remark",
    "Less Discount Reason",
    "Cancel Reason",
    "Cancelled At",
    "Cancelled By",
  ];

  const lines = rows.map((o, i) =>
    [
      i + 1,
      o.name,
      o.whatsapp,
      o.theater,
      formatDisplayDate(o.date),
      o.timeSlot,
      o.occasion,
      n(o.adults),
      n(o.kids),
      n(o.total || computeTotal(o)),
      n(o.advancePaid || ADVANCE_PAID),
      o.status,
      n(o.paymentInfo?.extraCost ?? o.extraCost),
      o.extraCostCategory || o.paymentInfo?.extraCostCategory || "",
      o.extraCostReason || o.paymentInfo?.extraCostReason || "",
      n(o.paymentInfo?.manualDiscount ?? o.manualDiscount),
      o.manualDiscountRemark || o.paymentInfo?.manualDiscountRemark || "",
      o.manualDiscountReason || o.paymentInfo?.manualDiscountReason || "",
      o.cancelReason || "",
      o.cancelledAt ? formatTS(o.cancelledAt) : "",
      o.cancelledBy || "",
    ]
      .map(esc)
      .join(",")
  );

  const csv = `\uFEFF${[header.map(esc).join(","), ...lines].join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = `hexaplay-${String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}-${toISODate(new Date())}.csv`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ══════════════════════════════════════════════════
   CANCEL REASON MODAL
   Opens when the ❌ Cancel button is clicked. Reason is required,
   max 1000 characters, and is stored with the Cancelled Orders record.
══════════════════════════════════════════════════ */
const CancelReasonModal = ({ order, busy, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const textRef = useRef(null);

  const trimmedLen = reason.trim().length;
  const tooShort = trimmedLen < CANCEL_REASON_MIN;
  const nearLimit = reason.length >= CANCEL_REASON_MAX - 100;

  useEffect(() => {
    textRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = () => {
    setTouched(true);
    if (tooShort || busy) return;
    onConfirm(reason.trim());
  };

  return (
    <div className="aol-modal-overlay" onClick={() => !busy && onClose()}>
      <div
        className="hp-cancel-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hp-cancel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hp-cancel-head">
          <div>
            <h2 id="hp-cancel-title">❌ Cancel this booking?</h2>
            <p>
              The booking will move to <b>Cancelled Orders</b> together with your
              reason.
            </p>
          </div>

          <button type="button" onClick={onClose} disabled={busy} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="hp-cancel-body">
          <div className="hp-cancel-info">
            <div>
              <span>Customer</span>
              <b>{order.name || "—"}</b>
            </div>
            <div>
              <span>Mobile</span>
              <b>{order.whatsapp || "—"}</b>
            </div>
            <div>
              <span>Theater</span>
              <b>{order.theater || "—"}</b>
            </div>
            <div>
              <span>Date / Slot</span>
              <b>
                {formatDisplayDate(order.date)} · {order.timeSlot || "—"}
              </b>
            </div>
          </div>

          <div className="hp-cancel-chips">
            {CANCEL_QUICK_REASONS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() =>
                  setReason((prev) => {
                    const next = prev.trim() ? `${prev.trim()}. ${q}` : q;
                    return next.slice(0, CANCEL_REASON_MAX);
                  })
                }
              >
                + {q}
              </button>
            ))}
          </div>

          <div className="hp-cancel-label">
            <span>
              Why is this booking cancelled? <em>*</em>
            </span>
          </div>

          <textarea
            ref={textRef}
            className={`hp-cancel-textarea ${touched && tooShort ? "invalid" : ""}`}
            value={reason}
            maxLength={CANCEL_REASON_MAX}
            placeholder="Write the full cancellation reason here (customer request, payment issue, slot problem, etc.)"
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") submit();
            }}
          />

          <div className="hp-cancel-meta">
            <span className={touched && tooShort ? "err" : ""}>
              {touched && tooShort
                ? `Please enter at least ${CANCEL_REASON_MIN} characters.`
                : "Ctrl + Enter to confirm"}
            </span>

            <span className={nearLimit ? "warn" : ""}>
              {reason.length}/{CANCEL_REASON_MAX}
            </span>
          </div>
        </div>

        <div className="hp-cancel-foot">
          <button className="aol-light-btn" onClick={onClose} disabled={busy}>
            Keep booking
          </button>

          <button className="aol-danger-btn" onClick={submit} disabled={busy}>
            {busy ? "Cancelling..." : "❌ Confirm cancellation"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   ADD ADDONS MODAL
══════════════════════════════════════════════════ */
const AddAddonsModal = ({ order, onClose, onSave }) => {
  const [catalog, setCatalog] = useState({
    cakes: [],
    decorations: [],
    gifts: [],
    services: [],
  });

  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [activeCategory, setActiveCategory] = useState("cakes");
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState({
    cakes: normalizeItemList(order.cakes),
    extraDecors: normalizeItemList(order.extraDecors),
    gifts: normalizeItemList(order.gifts),
    services: normalizeItemList(order.services),
  });

  useEffect(() => {
    let alive = true;

    const fetchCatalog = async () => {
      setLoadingCatalog(true);

      try {
        const results = await Promise.all(
          CATEGORY_DEFS.map((c) =>
            withTimeout(`${ADMIN_API}/${c.key}`, {}, 8000)
              .then((r) => (r.ok ? r.json() : []))
              .catch(() => [])
          )
        );

        if (!alive) return;

        const next = {};

        CATEGORY_DEFS.forEach((c, i) => {
          const raw = Array.isArray(results[i])
            ? results[i]
            : Array.isArray(results[i]?.data)
            ? results[i].data
            : [];

          next[c.key] = raw.map(normalizeCatalogItem);
        });

        setCatalog(next);
      } finally {
        if (alive) setLoadingCatalog(false);
      }
    };

    fetchCatalog();

    return () => {
      alive = false;
    };
  }, []);

  const active =
    CATEGORY_DEFS.find((c) => c.key === activeCategory) || CATEGORY_DEFS[0];

  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = catalog[activeCategory] || [];

    return (q
      ? list.filter((x) => x.name.toLowerCase().includes(q))
      : list
    ).slice(0, 20);
  }, [catalog, activeCategory, searchQuery]);

  const isSelected = (field, item) =>
    (selected[field] || []).some(
      (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
    );

  const toggleAddon = (catKey, item) => {
    const cat = CATEGORY_DEFS.find((c) => c.key === catKey) || CATEGORY_DEFS[0];
    const field = cat.field;

    setSelected((prev) => {
      const list = prev[field] || [];
      const exists = list.some(
        (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
      );

      if (exists) {
        return {
          ...prev,
          [field]: list.filter(
            (x) => x.name !== item.name && x._id !== item._id
          ),
        };
      }

      return {
        ...prev,
        [field]: [
          ...list,
          {
            _id: item._id,
            name: item.name,
            price: n(item.price),
            qty: 1,
          },
        ],
      };
    });

    setSearchQuery("");
  };

  const changeQty = (field, name, delta) => {
    setSelected((prev) => ({
      ...prev,
      [field]: (prev[field] || [])
        .map((x) =>
          x.name === name
            ? { ...x, qty: Math.max(0, safeQty(x) + delta) }
            : x
        )
        .filter((x) => n(x.qty) > 0),
    }));
  };

  const removeAddon = (field, name) => {
    setSelected((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((x) => x.name !== name),
    }));
  };

  const newTotal = useMemo(
    () => computeTotal({ ...order, ...selected }),
    [order, selected]
  );

  const previewOrder = useMemo(
    () => ({
      ...order,
      ...selected,
      total: newTotal,
      finalAmount: newTotal,
      packageAmount: newTotal,
    }),
    [order, selected, newTotal]
  );

  const allSelected = useMemo(
    () => [
      ...selected.cakes.map((x) => ({ ...x, field: "cakes", emoji: "🎂" })),
      ...selected.extraDecors.map((x) => ({
        ...x,
        field: "extraDecors",
        emoji: "🎨",
      })),
      ...selected.gifts.map((x) => ({ ...x, field: "gifts", emoji: "🎁" })),
      ...selected.services.map((x) => ({
        ...x,
        field: "services",
        emoji: "🛎️",
      })),
    ],
    [selected]
  );

  const handleSave = async () => {
    setSaving(true);

    try {
      const ok = await onSave(previewOrder);
      if (ok !== false) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aol-modal-overlay" onClick={onClose}>
      <div className="aol-addon-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aol-addon-modal-header">
          <div className="aol-modal-title-block">
            <span className="aol-big-icon">🛍️</span>
            <div>
              <h2>Add / Edit Add-ons</h2>
              <p>
                {order.name} · {order.theater} ·{" "}
                {formatDisplayDate(order.date)}
              </p>
            </div>
          </div>

          <button className="aol-icon-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="aol-addon-modal-body">
          <div className="aol-addon-left-panel">
            <div className="aol-addon-cat-tabs">
              {CATEGORY_DEFS.map((cat) => (
                <button
                  key={cat.key}
                  className={`aol-addon-cat-tab ${
                    activeCategory === cat.key ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveCategory(cat.key);
                    setSearchQuery("");
                  }}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  {selected[cat.field]?.length > 0 && (
                    <b>{selected[cat.field].length}</b>
                  )}
                </button>
              ))}
            </div>

            <div className="aol-addon-search-wrap">
              <span>🔍</span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${active.label}...`}
                autoFocus
              />
              {searchQuery && <button onClick={() => setSearchQuery("")}>✕</button>}
            </div>

            <div className="aol-addon-suggestions">
              {loadingCatalog ? (
                <div className="aol-mini-state">Loading catalog...</div>
              ) : suggestions.length === 0 ? (
                <div className="aol-mini-state">No item found</div>
              ) : (
                suggestions.map((item) => {
                  const picked = isSelected(active.field, item);

                  return (
                    <button
                      key={item._id || item.name}
                      className={`aol-addon-suggestion-item ${
                        picked ? "selected" : ""
                      }`}
                      onClick={() => toggleAddon(activeCategory, item)}
                    >
                      <span className="aol-suggestion-main">
                        <b>{active.emoji}</b>
                        {item.name}
                      </span>
                      <span className="aol-suggestion-price">
                        {money(item.price)} <i>{picked ? "✓" : "+"}</i>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="aol-addon-right-panel">
            <div className="aol-panel-head">
              <h3>Selected Items</h3>
              <span>
                {allSelected.length} item{allSelected.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="aol-addon-selected-list">
              {allSelected.length === 0 ? (
                <div className="aol-empty-mini">
                  🛒<p>No add-ons selected yet</p>
                </div>
              ) : (
                allSelected.map((a) => (
                  <div
                    key={`${a.field}-${a.name}`}
                    className="aol-addon-selected-item"
                  >
                    <div>
                      <strong>
                        {a.emoji} {a.name}
                        {qtyLabel(a)}
                      </strong>
                      <small>
                        {money(a.price)}
                        {safeQty(a) > 1 &&
                          ` x${safeQty(a)} = ${money(itemTotal(a))}`}
                      </small>
                    </div>

                    <div className="aol-selected-actions">
                      <div className="aol-gift-qty-admin">
                        <button onClick={() => changeQty(a.field, a.name, -1)}>
                          −
                        </button>
                        <b>{safeQty(a)}</b>
                        <button onClick={() => changeQty(a.field, a.name, 1)}>
                          +
                        </button>
                      </div>

                      <button
                        className="aol-remove-small"
                        onClick={() => removeAddon(a.field, a.name)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="aol-addon-price-breakup">
              <h4>Live Price List</h4>

              <div>
                <span>Base + Decor + Guests</span>
                <b>
                  {money(
                    computeSubtotalBeforeManualAdjustments({
                      ...previewOrder,
                      cakes: [],
                      extraDecors: [],
                      gifts: [],
                      services: [],
                      extraAddons: [],
                    })
                  )}
                </b>
              </div>

              {allSelected.map((a) => (
                <div key={`price-${a.field}-${a.name}`}>
                  <span>
                    {a.emoji} {a.name}
                    {qtyLabel(a)}
                  </span>
                  <b>{money(itemTotal(a))}</b>
                </div>
              ))}

              {normalizeItemList(order.extraAddons).length > 0 && (
                <div>
                  <span>⭐ Extra Add-ons</span>
                  <b>{money(computeExtraAddonTotal(order.extraAddons))}</b>
                </div>
              )}

              {n(order.discount) > 0 && (
                <div className="negative">
                  <span>Coupon Discount</span>
                  <b>-{money(order.discount)}</b>
                </div>
              )}

              <div className="total">
                <span>New Grand Total</span>
                <b>{money(newTotal)}</b>
              </div>

              <div className="balance">
                <span>Balance Due</span>
                <b>{money(Math.max(0, newTotal - n(order.advancePaid || ADVANCE_PAID)))}</b>
              </div>
            </div>
          </div>
        </div>

        <div className="aol-addon-modal-footer">
          <strong>New Total: {money(newTotal)}</strong>

          <div>
            <button className="aol-light-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              className="aol-primary-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving to server..." : "💾 Save Add-ons"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   EXTRA ADD-ONS MODAL
   Loads real items live from /api/extra-addons/all
   (the Super Admin "Extra Add-ons" inventory page).
   No sample / dummy data. Supports multi-item selection.
══════════════════════════════════════════════════ */
const ExtraAddonsModal = ({ order, onClose, onSave }) => {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [activeCategory, setActiveCategory] = useState("cakes");
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(() =>
    normalizeItemList(order.extraAddons)
  );

  useEffect(() => {
    let alive = true;

    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      setCatalogError("");

      try {
        const res = await withTimeout(`${EXTRA_ADDONS_API}/all`, {}, 10000);

        if (!res.ok) throw new Error(`Request failed (${res.status})`);

        const json = await res.json();
        const raw = Array.isArray(json?.data) ? json.data : [];

        if (!alive) return;

        setCatalog(
          raw.map(normalizeExtraAddonCatalogItem).filter((x) => x.isActive)
        );
      } catch (err) {
        if (alive) {
          setCatalogError(
            "Could not load Extra Add-ons from the server. Please add items in the Extra Add-ons page first."
          );
        }
      } finally {
        if (alive) setLoadingCatalog(false);
      }
    };

    fetchCatalog();

    return () => {
      alive = false;
    };
  }, []);

  const categoriesWithCounts = useMemo(
    () =>
      EXTRA_ADDON_CATEGORY_DEFS.map((c) => ({
        ...c,
        count: catalog.filter((x) => x.category === c.key).length,
      })),
    [catalog]
  );

  const active =
    EXTRA_ADDON_CATEGORY_DEFS.find((c) => c.key === activeCategory) ||
    EXTRA_ADDON_CATEGORY_DEFS[0];

  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = catalog.filter((x) => x.category === activeCategory);

    return (q
      ? list.filter(
          (x) =>
            x.name.toLowerCase().includes(q) ||
            (x.description || "").toLowerCase().includes(q)
        )
      : list
    ).slice(0, 50);
  }, [catalog, activeCategory, searchQuery]);

  const emojiOf = (name) =>
    catalog.find((x) => x.name === name)?.emoji || "⭐";

  const isPicked = (item) =>
    selected.some(
      (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
    );

  const toggleItem = (item) => {
    setSelected((prev) => {
      const exists = prev.some(
        (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
      );

      if (exists) {
        return prev.filter(
          (x) => x.name !== item.name && x._id !== item._id
        );
      }

      return [
        ...prev,
        { _id: item._id, name: item.name, price: n(item.price), qty: 1 },
      ];
    });
  };

  const changeQty = (name, delta) => {
    setSelected((prev) =>
      prev
        .map((x) =>
          x.name === name ? { ...x, qty: Math.max(0, safeQty(x) + delta) } : x
        )
        .filter((x) => n(x.qty) > 0)
    );
  };

  const removeItem = (name) =>
    setSelected((prev) => prev.filter((x) => x.name !== name));

  const extraTotal = useMemo(() => computeExtraAddonTotal(selected), [selected]);

  const newTotal = useMemo(
    () => computeTotal({ ...order, extraAddons: selected }),
    [order, selected]
  );

  const handleSave = async () => {
    setSaving(true);

    try {
      const ok = await onSave({
        ...order,
        extraAddons: normalizeItemList(selected),
        total: newTotal,
        finalAmount: newTotal,
        packageAmount: newTotal,
      });

      if (ok !== false) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aol-modal-overlay" onClick={onClose}>
      <div
        className="aol-addon-modal aol-extra-addon-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aol-addon-modal-header">
          <div className="aol-modal-title-block">
            <span className="aol-big-icon">⭐</span>
            <div>
              <h2>Add / Edit Add-ons — Extra Add-ons</h2>
              <p>
                {order.name} · {order.theater} · {formatDisplayDate(order.date)}
              </p>
            </div>
          </div>

          <button className="aol-icon-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="aol-addon-modal-body">
          <div className="aol-addon-left-panel">
            <div className="aol-addon-cat-tabs">
              {categoriesWithCounts.map((cat) => (
                <button
                  key={cat.key}
                  className={`aol-addon-cat-tab ${
                    activeCategory === cat.key ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveCategory(cat.key);
                    setSearchQuery("");
                  }}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  <b>{cat.count}</b>
                </button>
              ))}
            </div>

            <div className="aol-addon-search-wrap">
              <span>🔍</span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${active.label}...`}
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")}>✕</button>
              )}
            </div>

            <div className="aol-addon-suggestions">
              {loadingCatalog ? (
                <div className="aol-mini-state">Loading extra add-ons...</div>
              ) : catalogError ? (
                <div className="aol-mini-state">{catalogError}</div>
              ) : suggestions.length === 0 ? (
                <div className="aol-mini-state">
                  No {active.label.toLowerCase()} added in Extra Add-ons yet.
                </div>
              ) : (
                suggestions.map((item) => {
                  const picked = isPicked(item);
                  const outOfStock = item.stock <= 0;

                  return (
                    <button
                      key={item._id || item.name}
                      className={`aol-addon-suggestion-item aol-extra-suggestion ${
                        picked ? "selected" : ""
                      } ${outOfStock ? "out-of-stock" : ""}`}
                      onClick={() => toggleItem(item)}
                    >
                      <span className="aol-suggestion-main">
                        <b>{item.emoji}</b>
                        <span className="aol-extra-name">
                          {item.name}
                          {item.description ? (
                            <i>{item.description}</i>
                          ) : (
                            <i>
                              Stock: {item.stock}
                              {outOfStock ? " · Out of stock" : ""}
                            </i>
                          )}
                        </span>
                      </span>

                      <span className="aol-suggestion-price">
                        {money(item.price)} <i>{picked ? "✓" : "+"}</i>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="aol-addon-right-panel">
            <div className="aol-panel-head">
              <h3>Selected Extra Add-ons</h3>
              <span>
                {selected.length} item{selected.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="aol-addon-selected-list">
              {selected.length === 0 ? (
                <div className="aol-empty-mini">
                  ⭐<p>No extra add-ons selected yet</p>
                </div>
              ) : (
                selected.map((a) => (
                  <div key={a.name} className="aol-addon-selected-item">
                    <div>
                      <strong>
                        {emojiOf(a.name)} {a.name}
                        {qtyLabel(a)}
                      </strong>
                      <small>
                        {money(a.price)}
                        {safeQty(a) > 1 &&
                          ` x${safeQty(a)} = ${money(itemTotal(a))}`}
                      </small>
                    </div>

                    <div className="aol-selected-actions">
                      <div className="aol-gift-qty-admin">
                        <button onClick={() => changeQty(a.name, -1)}>−</button>
                        <b>{safeQty(a)}</b>
                        <button onClick={() => changeQty(a.name, 1)}>+</button>
                      </div>

                      <button
                        className="aol-remove-small"
                        onClick={() => removeItem(a.name)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="aol-addon-price-breakup">
              <h4>Live Price List</h4>

              <div>
                <span>Current Booking Total</span>
                <b>{money(n(order.total || computeTotal(order)))}</b>
              </div>

              {selected.map((a) => (
                <div key={`x-price-${a.name}`}>
                  <span>
                    {emojiOf(a.name)} {a.name}
                    {qtyLabel(a)}
                  </span>
                  <b>{money(itemTotal(a))}</b>
                </div>
              ))}

              <div className="total">
                <span>Extra Add-ons Amount</span>
                <b>{money(extraTotal)}</b>
              </div>

              <div className="balance">
                <span>New Grand Total</span>
                <b>{money(newTotal)}</b>
              </div>
            </div>
          </div>
        </div>

        <div className="aol-addon-modal-footer">
          <strong>
            Extra Add-ons: {money(extraTotal)} · New Total: {money(newTotal)}
          </strong>

          <div>
            <button className="aol-light-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>

            <button
              className="aol-primary-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving to server..." : "💾 Save Extra Add-ons"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   PAY NOW MODAL
   • Less Discount: Remark (short label) + Reason (detailed)
   • Extra Cost:    Category (short label) + Reason (detailed)  ← NEW
   All are saved with the booking and editable in
   "Edit Booking Details".
══════════════════════════════════════════════════ */
const PayNowModal = ({ order, onClose, onSubmit }) => {
  const grandTotal = n(order.total || computeTotal(order));
  const advance = n(order.advancePaid || ADVANCE_PAID);
  const actualBalance = Math.max(0, grandTotal - advance);
  const existingModes = order.paymentInfo?.modes || [];

  const buildInitialModes = () => {
    const base = {
      cash: { enabled: false, amount: "" },
      card: { enabled: false, amount: "" },
      upi: { enabled: false, amount: "" },
      bank: { enabled: false, amount: "" },
    };

    existingModes.forEach((m) => {
      const key = String(m.mode || "").toLowerCase();
      if (base[key]) base[key] = { enabled: true, amount: n(m.amount) };
    });

    return base;
  };

  const [foodCost, setFoodCost] = useState(
    n(order.foodCost || order.paymentInfo?.foodCost)
  );

  const [manualDiscount, setManualDiscount] = useState(
    n(order.manualDiscount || order.paymentInfo?.manualDiscount)
  );

  /* Less Discount Remark (short label) */
  const [discountRemark, setDiscountRemark] = useState(
    order.manualDiscountRemark || order.paymentInfo?.manualDiscountRemark || ""
  );

  /* Reason (detailed text) */
  const [discountReason, setDiscountReason] = useState(
    order.manualDiscountReason || order.paymentInfo?.manualDiscountReason || ""
  );

  /* extra cost (added to Settlement Balance) */
  const [extraCost, setExtraCost] = useState(
    n(order.extraCost || order.paymentInfo?.extraCost)
  );

  /* NEW: Extra Cost Category + Reason */
  const [extraCostCategory, setExtraCostCategory] = useState(
    order.extraCostCategory || order.paymentInfo?.extraCostCategory || ""
  );

  const [extraCostReason, setExtraCostReason] = useState(
    order.extraCostReason || order.paymentInfo?.extraCostReason || ""
  );

  const [paymentModes, setPaymentModes] = useState(buildInitialModes);
  const [applyGST, setApplyGST] = useState(Boolean(order.paymentInfo?.gstApplied));
  const [gstNumber, setGstNumber] = useState(order.paymentInfo?.gstNumber || "");
  const [submitting, setSubmitting] = useState(false);

  /* Settlement Balance = Actual Balance + Food Cost + Extra Cost − Less Discount */
  const settlementBalance = Math.max(
    0,
    actualBalance + n(foodCost) + n(extraCost) - n(manualDiscount)
  );

  const totalPaid = Object.values(paymentModes).reduce(
    (sum, mode) => sum + (mode.enabled ? n(mode.amount) : 0),
    0
  );

  const gstAmount = applyGST ? Math.round(totalPaid * GST_RATE) : 0;
  const finalAmount = totalPaid + gstAmount;
  const remainingDue = Math.max(0, settlementBalance - totalPaid);
  const overPaid = Math.max(0, totalPaid - settlementBalance);

  const progress =
    settlementBalance > 0
      ? Math.min(100, Math.round((totalPaid / settlementBalance) * 100))
      : 100;

  const discountNeedsText = n(manualDiscount) > 0;
  const extraNeedsText = n(extraCost) > 0; /* NEW */

  const modeConfig = {
    cash: { icon: "💵", label: "Cash", color: "#16a34a" },
    card: { icon: "💳", label: "Card", color: "#2563eb" },
    upi: { icon: "📱", label: "UPI", color: "#7c3aed" },
    bank: { icon: "🏦", label: "Bank", color: "#f97316" },
  };

  const distributeRemaining = (targetMode) => {
    const paidOther = Object.entries(paymentModes).reduce(
      (sum, [key, mode]) =>
        key === targetMode ? sum : sum + (mode.enabled ? n(mode.amount) : 0),
      0
    );

    return Math.max(0, settlementBalance - paidOther);
  };

  const toggleMode = (mode) => {
    setPaymentModes((prev) => {
      const willEnable = !prev[mode].enabled;

      return {
        ...prev,
        [mode]: {
          enabled: willEnable,
          amount: willEnable ? distributeRemaining(mode) || "" : "",
        },
      };
    });
  };

  const updateModeAmount = (mode, value) => {
    setPaymentModes((prev) => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        amount: value === "" ? "" : Math.max(0, n(value)),
      },
    }));
  };

  const clearModes = () =>
    setPaymentModes({
      cash: { enabled: false, amount: "" },
      card: { enabled: false, amount: "" },
      upi: { enabled: false, amount: "" },
      bank: { enabled: false, amount: "" },
    });

  const autoSplit = () => {
    const enabled = Object.entries(paymentModes)
      .filter(([, m]) => m.enabled)
      .map(([k]) => k);

    if (enabled.length === 0) {
      alert("Please select payment mode first.");
      return;
    }

    const each = Math.floor(settlementBalance / enabled.length);
    const remainder = settlementBalance - each * enabled.length;

    setPaymentModes((prev) => {
      const next = { ...prev };

      enabled.forEach((key, idx) => {
        next[key] = {
          ...next[key],
          amount: each + (idx === 0 ? remainder : 0),
        };
      });

      return next;
    });
  };

  const handleSubmit = async () => {
    if (totalPaid <= 0) {
      alert("Please collect amount in at least one payment mode.");
      return;
    }

    if (remainingDue > 0) {
      alert(
        `Balance due is still ${money(
          remainingDue
        )}. Please complete payment before confirm.`
      );
      return;
    }

    /* Remark AND reason are required whenever a discount is applied */
    if (discountNeedsText && !discountRemark.trim()) {
      alert("Please enter the Less Discount Remark.");
      return;
    }

    if (discountNeedsText && !discountReason.trim()) {
      alert("Please enter the Reason for the Less Discount.");
      return;
    }

    /* NEW: Category AND reason are required whenever an extra cost is applied */
    if (extraNeedsText && !extraCostCategory.trim()) {
      alert("Please select or enter the Extra Cost Category.");
      return;
    }

    if (extraNeedsText && !extraCostReason.trim()) {
      alert("Please enter the Reason for the Extra Cost.");
      return;
    }

    if (applyGST && !gstNumber.trim()) {
      alert("Please enter GST number or select No GST.");
      return;
    }

    setSubmitting(true);

    try {
      const modes = Object.entries(paymentModes)
        .filter(([, mode]) => mode.enabled && n(mode.amount) > 0)
        .map(([mode, data]) => ({
          mode: mode.toUpperCase(),
          amount: n(data.amount),
        }));

      const ok = await onSubmit({
        modes,
        grandTotal,
        advancePaid: advance,
        actualBalance,
        foodCost: n(foodCost),
        extraCost: n(extraCost),
        /* NEW: cleared automatically when there is no extra cost */
        extraCostCategory: extraNeedsText ? extraCostCategory.trim() : "",
        extraCostReason: extraNeedsText ? extraCostReason.trim() : "",
        manualDiscount: n(manualDiscount),
        manualDiscountRemark: discountRemark.trim(),
        manualDiscountReason: discountReason.trim(),
        settlementBalance,
        totalPaid,
        remainingDue,
        overPaid,
        gstApplied: applyGST,
        gstNumber: applyGST ? gstNumber.trim().toUpperCase() : null,
        gstAmount,
        finalAmount,
        paidAt: new Date().toISOString(),
      });

      if (ok !== false) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="aol-modal-overlay" onClick={onClose}>
      <div className="aol-paynow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aol-paynow-hero">
          <div className="aol-modal-title-block">
            <span className="aol-big-icon">💳</span>
            <div>
              <h2>Payment Settlement</h2>
              <p>
                {order.name} · {order.theater} · {formatDisplayDate(order.date)}
              </p>
            </div>
          </div>

          <button className="aol-icon-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="aol-paynow-body">
          <div className="aol-pay-summary-grid">
            <div>
              <span>Grand Total</span>
              <b>{money(grandTotal)}</b>
            </div>
            <div>
              <span>Advance Paid</span>
              <b>{money(advance)}</b>
            </div>
            <div className="due">
              <span>Actual Balance</span>
              <b>{money(actualBalance)}</b>
            </div>
          </div>

          <div className="aol-adjustment-card">
            <div className="aol-section-heading">
              <h3>Food Cost, Extra Cost & Less Discount</h3>
              <p>Adjust the amounts before final settlement.</p>
            </div>

            <div className="aol-adjustment-grid">
              <label>
                <span>🍽️ Food Cost</span>
                <div>
                  <i>₹</i>
                  <input
                    type="number"
                    min="0"
                    value={foodCost}
                    onChange={(e) => setFoodCost(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </label>

              <label>
                <span>🏷️ Less Discount Price</span>
                <div>
                  <i>₹</i>
                  <input
                    type="number"
                    min="0"
                    value={manualDiscount}
                    onChange={(e) => setManualDiscount(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </label>

              {/* Less Discount row split into Remark + Reason */}
              <div className="hp-discount-split">
                <label>
                  <span>
                    📝 Less Discount Remark{" "}
                    {discountNeedsText && <b style={{ color: "#dc2626" }}>*</b>}
                  </span>

                  <input
                    type="text"
                    value={discountRemark}
                    maxLength={100}
                    onChange={(e) => setDiscountRemark(e.target.value)}
                    placeholder="e.g. Regular customer"
                  />

                  
                </label>

                <label>
                  <span>
                    💬 Reason{" "}
                    {discountNeedsText && <b style={{ color: "#dc2626" }}>*</b>}
                  </span>

                  <textarea
                    value={discountReason}
                    maxLength={DISCOUNT_REASON_MAX}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder=""
                  />

                  <span className="hp-count">
                    {discountReason.length}/{DISCOUNT_REASON_MAX}
                  </span>
                </label>
              </div>

              <label style={{ gridColumn: "1 / -1" }}>
                <span>➕ Extra Cost</span>
                <div>
                  <i>₹</i>
                  <input
                    type="number"
                    min="0"
                    value={extraCost}
                    onChange={(e) => setExtraCost(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <small
                  style={{
                    display: "block",
                    marginTop: 6,
                    color: "#6d28d9",
                    fontWeight: 600,
                  }}
                >
                  ℹ️ Extra cost is added to the Settlement Balance.
                </small>
              </label>

              {/* NEW: Extra Cost row split into Category + Reason */}
              <div className="hp-extra-split">
                <label>
                  <span>
                    🗂️ Extra Cost Category{" "}
                    {extraNeedsText && <b style={{ color: "#dc2626" }}>*</b>}
                  </span>

                  <input
                    type="text"
                    value={extraCostCategory}
                    maxLength={100}
                    onChange={(e) => setExtraCostCategory(e.target.value)}
                    placeholder="e.g. Extra Decoration"
                  />

                  
                </label>

                <label>
                  <span>
                    💬 Extra Cost Reason{" "}
                    {extraNeedsText && <b style={{ color: "#dc2626" }}>*</b>}
                  </span>

                  <textarea
                    value={extraCostReason}
                    maxLength={EXTRA_COST_REASON_MAX}
                    onChange={(e) => setExtraCostReason(e.target.value)}
                    placeholder=""
                  />

                  <span className="hp-count">
                    {extraCostReason.length}/{EXTRA_COST_REASON_MAX}
                  </span>
                </label>
              </div>
            </div>

            <div className="aol-settlement-balance">
              <span>Settlement Balance</span>
              <strong>{money(settlementBalance)}</strong>
            </div>
          </div>

          <div className="aol-paynow-section">
            <div className="aol-section-heading horizontal">
              <div>
                <h3>Payment Mode</h3>
                <p>Select one or multiple payment modes.</p>
              </div>

              <div className="aol-mode-tools">
                <button onClick={autoSplit}>Auto Split</button>
                <button onClick={clearModes}>Clear</button>
              </div>
            </div>

            <div className="aol-live-balance-card">
              <div className="aol-progress-top">
                <span>Collected {money(totalPaid)}</span>
                <b>{progress}%</b>
              </div>

              <div className="aol-progress">
                <span style={{ width: `${progress}%` }} />
              </div>

              <div className="aol-progress-bottom">
                <span>
                  Balance Due:{" "}
                  <b className={remainingDue > 0 ? "danger" : "ok"}>
                    {money(remainingDue)}
                  </b>
                </span>
                {overPaid > 0 && (
                  <span>
                    Extra: <b>{money(overPaid)}</b>
                  </span>
                )}
              </div>
            </div>

            <div className="aol-payment-mode-grid">
              {Object.entries(modeConfig).map(([mode, cfg]) => {
                const isActive = paymentModes[mode].enabled;

                return (
                  <div
                    key={mode}
                    className={`aol-mode-card ${isActive ? "active" : ""}`}
                    style={{ "--mode-color": cfg.color }}
                  >
                    <label className="aol-mode-check">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => toggleMode(mode)}
                      />
                      <span>{cfg.icon}</span>
                      <b>{cfg.label}</b>
                    </label>

                    {isActive && (
                      <div className="aol-mode-amount">
                        <i>₹</i>
                        <input
                          type="number"
                          min="0"
                          step="10"
                          value={paymentModes[mode].amount}
                          onChange={(e) => updateModeAmount(mode, e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="aol-gst-card">
            <div className="aol-section-heading">
              <h3>GST Details</h3>
              <p>Optional GST calculation on collected amount.</p>
            </div>

            <div className="aol-gst-options">
              <label className={!applyGST ? "selected" : ""}>
                <input
                  type="radio"
                  checked={!applyGST}
                  onChange={() => {
                    setApplyGST(false);
                    setGstNumber("");
                  }}
                />
                🚫 No GST
              </label>

              <label className={applyGST ? "selected" : ""}>
                <input
                  type="radio"
                  checked={applyGST}
                  onChange={() => setApplyGST(true)}
                />
                🧾 Apply GST 18%
              </label>
            </div>

            {applyGST && (
              <input
                className="aol-gst-input"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                maxLength="15"
                placeholder="GST Number e.g. 27AABCT1234H1Z0"
              />
            )}
          </div>

          <div className="aol-final-calc">
            <h3>Final Calculation</h3>

            <div>
              <span>Grand Total</span>
              <b>{money(grandTotal)}</b>
            </div>

            <div className="negative">
              <span>− Advance Paid</span>
              <b>−{money(advance)}</b>
            </div>

            <div>
              <span>Actual Balance</span>
              <b>{money(actualBalance)}</b>
            </div>

            <div>
              <span>+ Food Cost</span>
              <b>+{money(foodCost)}</b>
            </div>

            <div>
              <span>+ Extra Cost</span>
              <b>+{money(extraCost)}</b>
            </div>

            {/* NEW: Extra Cost Category + Reason in the final calculation */}
            {extraNeedsText && extraCostCategory.trim() && (
              <div>
                <span>🗂️ Extra Cost Category</span>
                <b>{extraCostCategory}</b>
              </div>
            )}

            {extraNeedsText && extraCostReason.trim() && (
              <div>
                <span>💬 Extra Cost Reason</span>
                <b
                  style={{
                    maxWidth: "60%",
                    textAlign: "right",
                    wordBreak: "break-word",
                  }}
                >
                  {extraCostReason}
                </b>
              </div>
            )}

            <div className="negative">
              <span>− Less Discount</span>
              <b>−{money(manualDiscount)}</b>
            </div>

            {discountRemark.trim() && (
              <div>
                <span>📝 Less Discount Remark</span>
                <b>{discountRemark}</b>
              </div>
            )}

            {discountReason.trim() && (
              <div>
                <span>💬 Reason</span>
                <b style={{ maxWidth: "60%", textAlign: "right", wordBreak: "break-word" }}>
                  {discountReason}
                </b>
              </div>
            )}

            <div className="subtotal">
              <span>Settlement Balance</span>
              <b>{money(settlementBalance)}</b>
            </div>

            {Object.entries(paymentModes).map(([mode, data]) =>
              data.enabled && n(data.amount) > 0 ? (
                <div key={mode}>
                  <span>
                    {modeConfig[mode].icon} {modeConfig[mode].label}
                  </span>
                  <b>{money(data.amount)}</b>
                </div>
              ) : null
            )}

            <div>
              <span>Total Collected</span>
              <b>{money(totalPaid)}</b>
            </div>

            {applyGST && (
              <>
                <div>
                  <span>GST Number</span>
                  <b>{gstNumber || "—"}</b>
                </div>

                <div>
                  <span>GST 18%</span>
                  <b>+{money(gstAmount)}</b>
                </div>
              </>
            )}

            <div className="final">
              <span>Final Amount Paid</span>
              <b>{money(finalAmount)}</b>
            </div>

            <div className={remainingDue > 0 ? "danger-row" : "success-row"}>
              <span>
                {remainingDue > 0 ? "⚠️ Remaining Balance" : "✅ Payment Complete"}
              </span>
              <b>
                {remainingDue > 0
                  ? money(remainingDue)
                  : overPaid > 0
                  ? `Extra ${money(overPaid)}`
                  : "Perfect"}
              </b>
            </div>
          </div>
        </div>

        <div className="aol-paynow-footer">
          <span>
            Payment confirm will save settlement permanently for staff record.
          </span>

          <div>
            <button
              className="aol-light-btn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              className="aol-success-btn"
              onClick={handleSubmit}
              disabled={submitting || totalPaid <= 0 || remainingDue > 0}
            >
              {submitting ? "Saving..." : "✅ Confirm Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   QUICK DATE STRIP
══════════════════════════════════════════════════ */
const QuickDateStrip = ({ chips, activeKey, onSelect, allCount }) => (
  <div
    className="aol-quickdate-bar"
    style={{
      width: "100%",
      padding: "22px",
      borderRadius: "18px",
      background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
      border: "1px solid #e5e7eb",
      boxShadow: "0 10px 35px rgba(15, 23, 42, 0.07)",
      boxSizing: "border-box",
    }}
  >
    <div
      className="aol-quickdate-head"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "15px",
        marginBottom: "18px",
        flexWrap: "wrap",
      }}
    >
      <div>
        <span
          className="aol-quickdate-eyebrow"
          style={{
            display: "inline-block",
            marginBottom: "5px",
            fontSize: "11px",
            fontWeight: "800",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          Schedule Lens
        </span>

        <h3
          style={{
            margin: 0,
            fontSize: "21px",
            lineHeight: "1.3",
            fontWeight: "800",
            color: "#0f172a",
            letterSpacing: "-0.4px",
          }}
        >
          Jump to a day
        </h3>
      </div>

      <div
        style={{
          padding: "7px 12px",
          borderRadius: "999px",
          background: "#f1f5f9",
          border: "1px solid #e2e8f0",
          color: "#475569",
          fontSize: "12px",
          fontWeight: "700",
          whiteSpace: "nowrap",
        }}
      >
        Quick Filter
      </div>
    </div>

    <div
      className="aol-quickdate-track"
      style={{
        display: "flex",
        gap: "12px",
        width: "100%",
        overflowX: "auto",
        paddingBottom: "5px",
        scrollbarWidth: "thin",
      }}
    >
      {chips.map((chip) => {
        const isActive = activeKey === chip.key;

        return (
          <button
            key={chip.key}
            type="button"
            className={`aol-quickdate-chip accent-${chip.accent} ${
              isActive ? "active" : ""
            }`}
            onClick={() => onSelect(chip)}
            style={{
              position: "relative",
              minWidth: "145px",
              flex: "0 0 auto",
              padding: "15px 16px",
              borderRadius: "14px",
              border: isActive
                ? "1.5px solid #2563eb"
                : "1px solid #e2e8f0",
              background: isActive
                ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
                : "#ffffff",
              color: "#0f172a",
              cursor: "pointer",
              textAlign: "left",
              transition:
                "all 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease",
              boxShadow: isActive
                ? "0 8px 22px rgba(37, 99, 235, 0.16)"
                : "0 3px 10px rgba(15, 23, 42, 0.04)",
              outline: "none",
              overflow: "hidden",
            }}
          >
            {isActive && (
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "3px",
                  background: "linear-gradient(90deg, #2563eb, #06b6d4)",
                }}
              />
            )}

            <span
              className="aol-qd-label"
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "11px",
                fontWeight: "800",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
                color: isActive ? "#2563eb" : "#64748b",
              }}
            >
              {chip.label}
            </span>

            <span
              className="aol-qd-date"
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "7px",
                marginBottom: "9px",
              }}
            >
              <b
                style={{
                  fontSize: "25px",
                  lineHeight: "1",
                  fontWeight: "850",
                  color: isActive ? "#1d4ed8" : "#0f172a",
                  letterSpacing: "-0.8px",
                }}
              >
                {chip.dayNum}
              </b>

              <i
                style={{
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontWeight: "700",
                  color: "#64748b",
                }}
              >
                {chip.month}
                {chip.spanLabel ? ` · ${chip.spanLabel}` : ""}
              </i>
            </span>

            <span
              className="aol-qd-count"
              style={{
                display: "inline-flex",
                alignItems: "center",
                width: "fit-content",
                padding: "5px 9px",
                borderRadius: "7px",
                background: isActive ? "#ffffff" : "#f8fafc",
                border: "1px solid",
                borderColor: isActive ? "#bfdbfe" : "#e5e7eb",
                fontSize: "11px",
                fontWeight: "700",
                color: "#475569",
              }}
            >
              {chip.count} booking{chip.count !== 1 ? "s" : ""}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        className={`aol-quickdate-chip accent-slate ${
          activeKey === "all" ? "active" : ""
        }`}
        onClick={() => onSelect(null)}
        style={{
          position: "relative",
          minWidth: "145px",
          flex: "0 0 auto",
          padding: "15px 16px",
          borderRadius: "14px",
          border:
            activeKey === "all" ? "1.5px solid #0f172a" : "1px solid #e2e8f0",
          background:
            activeKey === "all"
              ? "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)"
              : "#ffffff",
          color: "#0f172a",
          cursor: "pointer",
          textAlign: "left",
          transition: "all 0.25s ease",
          boxShadow:
            activeKey === "all"
              ? "0 8px 22px rgba(15, 23, 42, 0.14)"
              : "0 3px 10px rgba(15, 23, 42, 0.04)",
          outline: "none",
          overflow: "hidden",
        }}
      >
        {activeKey === "all" && (
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "3px",
              background: "#0f172a",
            }}
          />
        )}

        <span
          className="aol-qd-label"
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "11px",
            fontWeight: "800",
            letterSpacing: "0.7px",
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          All Dates
        </span>

        <span
          className="aol-qd-date"
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "7px",
            marginBottom: "9px",
          }}
        >
          <b
            style={{
              fontSize: "25px",
              lineHeight: "1",
              fontWeight: "850",
              color: "#0f172a",
            }}
          >
            ∞
          </b>

          <i
            style={{
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: "700",
              color: "#64748b",
            }}
          >
            full range
          </i>
        </span>

        <span
          className="aol-qd-count"
          style={{
            display: "inline-flex",
            alignItems: "center",
            width: "fit-content",
            padding: "5px 9px",
            borderRadius: "7px",
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
            fontSize: "11px",
            fontWeight: "700",
            color: "#475569",
          }}
        >
          {allCount} booking{allCount !== 1 ? "s" : ""}
        </span>
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
const StaffManagement = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [frozenOrders, setFrozenOrders] = useState([]);
  const [suspendedOrders, setSuspendedOrders] = useState([]);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [viewMode, setViewMode] = useState("active");

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [dbConnected, setDbConnected] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [addonModal, setAddonModal] = useState(null);
  const [extraAddonModal, setExtraAddonModal] = useState(null);
  const [payNowModal, setPayNowModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const [movingState, setMovingState] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [toasts, setToasts] = useState([]);

  const [staffDetailCache, setStaffDetailCache] = useState({});
  const [staffDetailLoading, setStaffDetailLoading] = useState({});
  const [staffDetailSaving, setStaffDetailSaving] = useState({});
  const [staffDetailError, setStaffDetailError] = useState({});

  const searchRef = useRef(null);
  const fetchInFlight = useRef(false);

  const filterLabel = formatRangeLabel(fromDate, toDate);
  const showFrozen = viewMode === "frozen";
  const isSuspendedView = viewMode === "suspended";
  const isCancelledView = viewMode === "cancelled";
  const isArchiveView = isSuspendedView || isCancelledView;

  /* lightweight toast notifications */
  const notify = useCallback((type, message, ms = 4500) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    setToasts((prev) => [...prev.slice(-3), { id, type, message }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ms);
  }, []);

  const dismissToast = (id) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  useEffect(() => {
    purgeLegacyLocalCaches();
  }, []);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await withTimeout(HEALTH_API, {}, 5000);
        if (!res.ok) throw new Error("Health failed");

        const json = await res.json();
        setDbConnected(json.status === "ok" || json.database === "connected");
      } catch {
        setDbConnected(false);
      }
    };

    checkHealth();
  }, []);

  const fetchOrders = useCallback(async (isBackground = false) => {
    if (fetchInFlight.current && isBackground) return;

    fetchInFlight.current = true;

    if (!isBackground) setLoading(true);
    setApiError(null);

    try {
      const [bookingResult, transferResult] = await Promise.allSettled([
        withTimeout(`${BOOKINGS_API}/admin/all`, {}, 12000),
        withTimeout(`${TRANSFER_LIST_API}?limit=2000`, {}, 12000),
      ]);

      let bookingRows = [];
      let transferRows = [];
      const errors = [];

      if (bookingResult.status === "fulfilled" && bookingResult.value.ok) {
        try {
          const json = await bookingResult.value.json();
          if (json.success === false) throw new Error(json.message || "Booking fetch failed");

          const data = Array.isArray(json.data) ? json.data : [];

          bookingRows = data
            .filter((b) => isSuccessfulBooking(b) && !b.meta?.adminBlocked)
            .map(normalizeBookingToRow)
            .filter(Boolean);
        } catch (err) {
          errors.push(err.message);
        }
      } else if (bookingResult.status === "fulfilled") {
        errors.push(`Bookings ${bookingResult.value.status}`);
      } else {
        errors.push(bookingResult.reason?.message || "Bookings failed");
      }

      if (transferResult.status === "fulfilled" && transferResult.value.ok) {
        try {
          const json = await transferResult.value.json();
          if (json.success === false) throw new Error(json.message || "Transfer fetch failed");

          const data = Array.isArray(json.data) ? json.data : [];

          transferRows = data.map(normalizeTransferToRow).filter(Boolean);
        } catch (err) {
          errors.push(err.message);
        }
      } else if (transferResult.status === "fulfilled") {
        errors.push(`Transfers ${transferResult.value.status}`);
      } else {
        errors.push(transferResult.reason?.message || "Transfers failed");
      }

      const merged = mergeRowsTransferFirst(transferRows, bookingRows);
      const stateMap = readRowStates();

      const tagged = merged.map((o) => {
        const rowState = resolveRowState(o, stateMap);

        return {
          ...o,
          _rowState: rowState,
          /* cancel reason/time/user resolved (server first, local ledger fallback) */
          ...(rowState === "cancelled" ? resolveCancelMeta(o, stateMap) : {}),
        };
      });

      const lockedRows = tagged.filter((o) => isOrderLocked(o)).map(toLockedOrder);
      const openRows = tagged.filter((o) => !isOrderLocked(o));

      setFrozenOrders(lockedRows);
      setSuspendedOrders(openRows.filter((o) => o._rowState === "suspended"));
      setCancelledOrders(openRows.filter((o) => o._rowState === "cancelled"));
      setOrders(
        openRows.filter(
          (o) => o._rowState !== "suspended" && o._rowState !== "cancelled"
        )
      );

      setDbConnected(true);
      setLastSync(new Date());

      if (!merged.length && errors.length && !isBackground) {
        setApiError(errors.join(" | "));
      }
    } catch (err) {
      if (!isBackground) {
        setApiError(err.message);
        setDbConnected(false);
      }
    } finally {
      fetchInFlight.current = false;
      if (!isBackground) setLoading(false);
    }
  }, []);

  const fetchMonths = useCallback(() => {
    const allOrders = [
      ...orders,
      ...frozenOrders,
      ...suspendedOrders,
      ...cancelledOrders,
    ];

    const monthMap = {};

    allOrders.forEach((o) => {
      const d = parseEventDate(o.date);
      if (!d) return;

      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthMap[key] = (monthMap[key] || 0) + 1;
    });

    setAvailableMonths(
      Object.entries(monthMap)
        .map(([k, count]) => {
          const [year, month] = k.split("-").map(Number);
          return {
            year,
            month,
            count,
            label: `${MONTH_NAMES[month]} ${year}`,
          };
        })
        .sort((a, b) => b.year - a.year || b.month - a.month)
    );
  }, [orders, frozenOrders, suspendedOrders, cancelledOrders]);

  useEffect(() => {
    let active = true;

    fetchOrders(false);

    const interval = setInterval(() => {
      if (active) fetchOrders(true);
    }, 5000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [fetchOrders]);

  useEffect(() => {
    fetchMonths();
  }, [fetchMonths]);

  useEffect(() => {
    const handlePaymentSuccess = (event) => {
      if (event.key === PAYMENT_CONFIRMATION_KEY && event.newValue) {
        try {
          const paymentData = JSON.parse(event.newValue);

          if (paymentData?.paymentSuccess || isTruthy(paymentData?.paymentSuccess)) {
            setPaymentSuccess(paymentData);
            fetchOrders(true);
            localStorage.removeItem(PAYMENT_CONFIRMATION_KEY);
          }
        } catch {}
      }
    };

    window.addEventListener("storage", handlePaymentSuccess);

    const pending = canUseLocalStorage()
      ? localStorage.getItem(PAYMENT_CONFIRMATION_KEY)
      : null;

    if (pending) {
      try {
        const paymentData = JSON.parse(pending);

        if (paymentData?.paymentSuccess || isTruthy(paymentData?.paymentSuccess)) {
          setPaymentSuccess(paymentData);
          fetchOrders(true);
          localStorage.removeItem(PAYMENT_CONFIRMATION_KEY);
        }
      } catch {}
    }

    return () => window.removeEventListener("storage", handlePaymentSuccess);
  }, [fetchOrders]);

  useEffect(() => {
    const handleFocus = () => {
      fetchOrders(true);
    };

    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchOrders]);

  useEffect(() => {
    if (!paymentSuccess) return;

    const timer = setTimeout(() => setPaymentSuccess(null), 10000);
    return () => clearTimeout(timer);
  }, [paymentSuccess]);

  useEffect(() => {
    setExpandedRow(null);
  }, [viewMode]);

  const guardLockedAction = (order) => {
    if (!isOrderLocked(order)) return false;

    notify(
      "warn",
      "This booking is permanently locked. Admin cannot edit or perform actions on a frozen row."
    );

    return true;
  };

  const persistHandover = useCallback(async (order, form, extraSnapshot = {}) => {
    const persistId = getPersistId(order);

    if (!persistId) return { ok: false, data: null };

    const payload = buildStaffHandoverPayload(order, form);

    payload.orderSnapshot = {
      ...(order?._rawBooking || {}),
      ...(order?.orderSnapshot || {}),
      ...(order?._transferRaw?.orderSnapshot || {}),
      ...extraSnapshot,
    };

    payload.retransfer = false;

    let ok = false;
    let data = null;

    try {
      const res = await withTimeout(
        RESERVATION_HANDOVER_API(persistId),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        15000
      );

      if (res.ok) {
        const json = await res.json();
        ok = json.success !== false;
        data = json?.data || payload;
      }
    } catch {}

    if (!ok && getTransferId(order)) {
      try {
        const transferPayload = buildTransferRoutePayload(order, form);

        const res = await withTimeout(
          TRANSFER_ROW_API(getTransferId(order)),
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(transferPayload),
          },
          15000
        );

        if (res.ok) {
          const json = await res.json();
          ok = json.success !== false;
          data = json?.data || json?.transfer || payload;
        }
      } catch {}
    }

    return { ok, data: data || payload };
  }, []);

  const hydrateStaffDetails = useCallback(
    async (order, force = false) => {
      const rowId = getRowId(order);
      const persistId = getPersistId(order);

      if (!persistId || !rowId) return;
      if (!force && staffDetailCache[rowId]) return;

      setStaffDetailLoading((prev) => ({ ...prev, [rowId]: true }));
      setStaffDetailError((prev) => ({ ...prev, [rowId]: "" }));

      let payload = null;

      try {
        const res = await withTimeout(RESERVATION_HANDOVER_API(persistId), {}, 10000);

        if (res.ok) {
          const json = await res.json();
          payload = json?.data || json;
        }
      } catch {}

      if (!payload && getTransferId(order)) {
        try {
          const res = await withTimeout(TRANSFER_ROW_API(getTransferId(order)), {}, 8000);

          if (res.ok) {
            const json = await res.json();
            payload = json?.data || json?.transfer || json;
          }
        } catch {}
      }

      const normalized = normalizeStaffHandover(order, payload || order);

      setStaffDetailCache((prev) => ({
        ...prev,
        [rowId]: normalized,
      }));

      setStaffDetailLoading((prev) => ({ ...prev, [rowId]: false }));
    },
    [staffDetailCache]
  );

  const handleToggleExpandedRow = (order, id) => {
    const isOpen = expandedRow === id;

    if (isOpen) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(id);
    hydrateStaffDetails(order);
  };

  const handleSaveStaffDetails = async (order, form) => {
    if (guardLockedAction(order)) return false;

    const rowId = getRowId(order);

    setStaffDetailSaving((prev) => ({ ...prev, [rowId]: true }));
    setStaffDetailError((prev) => ({ ...prev, [rowId]: "" }));

    /* keep Less Discount + Extra Cost Remark/Reason inside payment record too */
    const nextPaymentInfo = order.paymentInfo
      ? {
          ...order.paymentInfo,
          manualDiscountRemark: form.manualDiscountRemark || "",
          manualDiscountReason: form.manualDiscountReason || "",
          extraCostCategory: form.extraCostCategory || "",
          extraCostReason: form.extraCostReason || "",
        }
      : order.paymentInfo;

    const { ok, data } = await persistHandover(order, form, {
      manualDiscountRemark: form.manualDiscountRemark || "",
      manualDiscountReason: form.manualDiscountReason || "",
      extraCostCategory: form.extraCostCategory || "",
      extraCostReason: form.extraCostReason || "",
      ...(nextPaymentInfo ? { paymentInfo: nextPaymentInfo } : {}),
    });

    setStaffDetailSaving((prev) => ({ ...prev, [rowId]: false }));

    if (!ok) {
      const msg =
        "Staff handover details could not be saved permanently. Please check /api/reservations/handover/:orderId and /api/transfer routes.";

      setStaffDetailError((prev) => ({ ...prev, [rowId]: msg }));
      notify("error", msg, 7000);
      return false;
    }

    const normalized = normalizeStaffHandover(order, {
      ...(data || form),
      manualDiscountRemark: form.manualDiscountRemark || "",
      manualDiscountReason: form.manualDiscountReason || "",
      extraCostCategory: form.extraCostCategory || "",
      extraCostReason: form.extraCostReason || "",
    });

    setStaffDetailCache((prev) => ({
      ...prev,
      [rowId]: normalized,
    }));

    const patchFields = {
      name: normalized.name,
      customerName: normalized.customerName,
      whatsapp: normalized.whatsapp,
      primaryContactNumber: normalized.primaryContactNumber,
      alternateContactNumber: normalized.alternateContactNumber,
      celebrationName: normalized.celebrationName,
      handoverDetails: normalized.handoverDetails,
      extraPersonDetails: normalized.extraPersonDetails,
      complimentaryItems: normalized.complimentaryItems,
      customFields: normalized.customFields,
      staffNotes: normalized.staffNotes,
      managerDiscount: normalized.managerDiscount,
      otherDiscount: normalized.otherDiscount,
      finalAmount: normalized.finalAmount,
      total: normalized.finalAmount || order.total,
      status: normalized.status || order.status,
      transferredAt: normalized.transferredAt || order.transferredAt,
      transferredBy: normalized.transferredBy || order.transferredBy,
      manualDiscountRemark: normalized.manualDiscountRemark,
      manualDiscountReason: normalized.manualDiscountReason,
      extraCostCategory: normalized.extraCostCategory,
      extraCostReason: normalized.extraCostReason,
      ...(nextPaymentInfo ? { paymentInfo: nextPaymentInfo } : {}),
    };

    const applyPatch = (list) =>
      list.map((o) => (getRowId(o) === rowId ? { ...o, ...patchFields } : o));

    setOrders(applyPatch);
    setSuspendedOrders(applyPatch);
    setCancelledOrders(applyPatch);

    notify("success", "Staff handover details saved permanently.");
    fetchOrders(true);
    return true;
  };

  const patchOrder = useCallback(
    async (order, fields) => {
      if (guardLockedAction(order)) return false;

      const rowId = getRowId(order);
      const isTransferRow =
        order?._source === "transfer" ||
        order?._source === "reservation-transfer" ||
        Boolean(getTransferId(order));

      const applyPatch = (list) =>
        list.map((o) => (getRowId(o) === rowId ? { ...o, ...fields } : o));

      setOrders(applyPatch);
      setSuspendedOrders(applyPatch);
      setCancelledOrders(applyPatch);

      if (isTransferRow) {
        const baseForm = normalizeStaffHandover(order, order);
        const nextForm = {
          ...baseForm,
          ...fields,
          handoverDetails: {
            ...baseForm.handoverDetails,
            ...(fields.handoverDetails || {}),
          },
          extraPersonDetails: {
            ...baseForm.extraPersonDetails,
            ...(fields.extraPersonDetails || {}),
          },
        };

        const { ok } = await persistHandover(order, nextForm, {
          ...(fields.orderSnapshot || {}),
          status: fields.status || order.status,
          staffState: fields.staffState || order._rowState || "active",
          paymentInfo: fields.paymentInfo || order.paymentInfo,
          foodCost: fields.foodCost ?? order.foodCost,
          extraCost: fields.extraCost ?? order.extraCost,
          extraCostCategory:
            fields.extraCostCategory ?? order.extraCostCategory,
          extraCostReason: fields.extraCostReason ?? order.extraCostReason,
          manualDiscount: fields.manualDiscount ?? order.manualDiscount,
          manualDiscountRemark:
            fields.manualDiscountRemark ?? order.manualDiscountRemark,
          manualDiscountReason:
            fields.manualDiscountReason ?? order.manualDiscountReason,
          cancelReason: fields.cancelReason ?? order.cancelReason,
          cancelledAt: fields.cancelledAt ?? order.cancelledAt,
          cancelledBy: fields.cancelledBy ?? order.cancelledBy,
          total: fields.total ?? order.total,
          extraAddons: fields.extraAddons ?? order.extraAddons,
          frozen: fields.frozen ?? order.frozen,
          frozenAt: fields.frozenAt ?? order.frozenAt,
          frozenBy: fields.frozenBy ?? order.frozenBy,
          permanentlyLocked:
            fields.permanentlyLocked ?? order.permanentlyLocked,
        });

        return ok;
      }

      const id = getBookingPatchId(order);
      if (!id) return false;

      const { ok } = await patchBooking(id, { ...fields, meta: { ...fields } });
      return ok;
    },
    [persistHandover]
  );

  /* ════════ SUSPEND / CANCEL / RESTORE ════════
     Cancel is confirmed through the Cancel Reason popup
     (opts.reason), so no window.confirm is shown for it. */
  const handleRowState = async (order, nextState, opts = {}) => {
    if (guardLockedAction(order)) return;

    const labels = {
      suspended: "Suspended Orders",
      cancelled: "Cancelled Orders",
      active: "Active Staff Records",
    };

    if (nextState !== "cancelled") {
      const confirmMsg =
        nextState === "active"
          ? `Restore ${order.name || "this booking"} back to Active Staff Records?`
          : `Move ${order.name || "this booking"} permanently to ${
              labels[nextState]
            }?`;

      if (!window.confirm(confirmMsg)) return;
    }

    const cancelReason =
      nextState === "cancelled"
        ? String(opts.reason || "").trim().slice(0, CANCEL_REASON_MAX)
        : "";

    setMovingState(true);

    const stamp = new Date().toISOString();

    const statusByState = {
      suspended: "Suspended",
      cancelled: "Cancelled",
      active: order.paymentInfo ? "Fully Paid" : "In Staff Management",
    };

    const nextStatus = statusByState[nextState];

    const cancelFields = {
      cancelReason,
      cancelledAt: nextState === "cancelled" ? stamp : "",
      cancelledBy: nextState === "cancelled" ? "Admin" : "",
    };

    const movedRow = {
      ...order,
      _rowState: nextState,
      status: nextStatus,
      staffState: nextState,
      staffStateAt: stamp,
      staffStateBy: "Admin",
      suspendedAt: nextState === "suspended" ? stamp : null,
      cancelledAt: nextState === "cancelled" ? stamp : null,
      ...cancelFields,
    };

    /* Permanent local ledger — survives reload & re-fetch (reason included) */
    saveRowState(
      order,
      nextState,
      nextState === "cancelled" ? { reason: cancelReason } : {}
    );

    const key = identityKeyForRow(order);
    const strip = (list) => list.filter((o) => identityKeyForRow(o) !== key);

    setOrders((prev) =>
      nextState === "active" ? [movedRow, ...strip(prev)] : strip(prev)
    );

    setSuspendedOrders((prev) =>
      nextState === "suspended" ? [movedRow, ...strip(prev)] : strip(prev)
    );

    setCancelledOrders((prev) =>
      nextState === "cancelled" ? [movedRow, ...strip(prev)] : strip(prev)
    );

    setExpandedRow(null);

    const ok = await patchOrder(order, {
      status: nextStatus,
      staffState: nextState,
      staffStateAt: stamp,
      staffStateBy: "Admin",
      suspendedAt: nextState === "suspended" ? stamp : null,
      ...cancelFields,
    });

    setMovingState(false);

    if (nextState === "cancelled") {
      notify(
        ok ? "success" : "warn",
        ok
          ? "Booking cancelled. Reason saved in Cancelled Orders."
          : "Cancelled. Server did not confirm, but the reason is stored permanently on this device."
      );
    } else if (!ok) {
      notify(
        "warn",
        "Server did not confirm the move, but it is stored permanently on this device and will stay in the selected section.",
        6500
      );
    } else {
      notify("success", `Moved to ${labels[nextState]}.`);
    }

    fetchOrders(true);
  };

  /* Cancel button entry point → opens the reason popup */
  const requestCancel = (order) => {
    if (guardLockedAction(order)) return;
    setCancelModal(order);
  };

  const confirmCancel = async (reason) => {
    const target = cancelModal;
    if (!target) return;

    await handleRowState(target, "cancelled", { reason });
    setCancelModal(null);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const displayOrders = useMemo(() => {
    if (viewMode === "frozen") return frozenOrders;
    if (viewMode === "suspended") return suspendedOrders;
    if (viewMode === "cancelled") return cancelledOrders;
    return orders;
  }, [viewMode, orders, frozenOrders, suspendedOrders, cancelledOrders]);

  const searchFields = (o) => [
    o.name,
    o.theater,
    o.whatsapp,
    o.occasion,
    o.celebrationName,
    o.email,
    o.date,
    o.timeSlot,
    o.merchantOrderId,
    o.status,
    o.cancelReason,
    o.manualDiscountRemark,
    o.manualDiscountReason,
    o.extraCostCategory,
    o.extraCostReason,
  ];

  const quickFilterBase = useMemo(() => {
    let rows = [...displayOrders];

    if (search.trim()) {
      const q = search.trim().toLowerCase();

      rows = rows.filter((o) =>
        searchFields(o).some((v) => String(v || "").toLowerCase().includes(q))
      );
    }

    return rows;
  }, [displayOrders, search]);

  const countInRange = useCallback(
    (fromISO, toISO) => {
      const f = parseEventDate(fromISO);
      const t = parseEventDate(toISO);

      return quickFilterBase.filter((o) => {
        const ev = parseEventDate(o.date);
        if (!ev) return false;
        if (f && ev < f) return false;
        if (t && ev > t) return false;
        return true;
      }).length;
    },
    [quickFilterBase]
  );

  const quickChips = useMemo(() => {
    const today = buildQuickDay(0);
    const tomorrow = buildQuickDay(1);
    const dayAfter = buildQuickDay(2);
    const weekEnd = buildQuickDay(6);

    return [
      {
        key: "today",
        label: "Today",
        accent: "indigo",
        from: today.iso,
        to: today.iso,
        dayNum: today.dayNum,
        month: today.month,
      },
      {
        key: "tomorrow",
        label: "Tomorrow",
        accent: "violet",
        from: tomorrow.iso,
        to: tomorrow.iso,
        dayNum: tomorrow.dayNum,
        month: tomorrow.month,
      },
      {
        key: "dayAfter",
        label: "Day After",
        accent: "teal",
        from: dayAfter.iso,
        to: dayAfter.iso,
        dayNum: dayAfter.dayNum,
        month: dayAfter.month,
      },
      {
        key: "week",
        label: "This Week",
        accent: "amber",
        from: today.iso,
        to: weekEnd.iso,
        dayNum: "7",
        month: "days",
        spanLabel: `till ${weekEnd.dayNum} ${weekEnd.month}`,
      },
    ].map((chip) => ({
      ...chip,
      count: countInRange(chip.from, chip.to),
    }));
  }, [countInRange]);

  const activeQuickKey = useMemo(() => {
    if (!fromDate && !toDate) return "all";

    const match = quickChips.find(
      (c) => fromDate === c.from && toDate === c.to
    );

    return match ? match.key : null;
  }, [fromDate, toDate, quickChips]);

  const handleQuickSelect = (chip) => {
    if (!chip || activeQuickKey === chip.key) {
      setFromDate("");
      setToDate("");
    } else {
      setFromDate(chip.from);
      setToDate(chip.to);
    }

    setExpandedRow(null);
  };

  const filtered = useMemo(() => {
    let rows = [...displayOrders];

    const fromD = fromDate ? parseEventDate(fromDate) : null;
    const toD = toDate ? parseEventDate(toDate) : null;

    if (fromD || toD) {
      rows = rows.filter((o) => {
        const ev = parseEventDate(o.date);
        if (!ev) return false;
        if (fromD && ev < fromD) return false;
        if (toD && ev > toD) return false;
        return true;
      });
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();

      rows = rows.filter((o) =>
        searchFields(o).some((v) => String(v || "").toLowerCase().includes(q))
      );
    }

    rows.sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";

      if (sortKey === "total") {
        av = n(av);
        bv = n(bv);
        return sortDir === "asc" ? av - bv : bv - av;
      }

      if (sortKey === "timestamp" || sortKey === "cancelledAt") {
        av = new Date(av || 0).getTime();
        bv = new Date(bv || 0).getTime();
        return sortDir === "asc" ? av - bv : bv - av;
      }

      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    return rows;
  }, [displayOrders, fromDate, toDate, search, sortKey, sortDir]);

  /* ════════ DUPLICATE (NAME + MOBILE) DETECTION ════════ */
  const duplicateKeys = useMemo(() => buildDuplicateSet(filtered), [filtered]);

  const duplicateRowCount = useMemo(
    () =>
      filtered.filter((o) => {
        const key = duplicateKeyOf(o);
        return key && duplicateKeys.has(key);
      }).length,
    [filtered, duplicateKeys]
  );

  const handleStatusUpdate = async (order, status) => {
    /* choosing "Cancelled" in the dropdown also asks for a reason */
    if (status === "Cancelled") {
      requestCancel(order);
      return;
    }

    const ok = await patchOrder(order, { status });

    if (!ok) {
      notify("error", "Status could not be saved on the server. Please retry.");
    }

    fetchOrders(true);
  };

  const applyAddonFields = (rowId, fields) => {
    const apply = (list) =>
      list.map((o) =>
        getRowId(o) === rowId ? { ...o, ...fields, _dbCached: true } : o
      );

    setOrders(apply);
    setSuspendedOrders(apply);
    setCancelledOrders(apply);
  };

  const handleSaveAddons = async (updatedOrder) => {
    const rowId = getRowId(updatedOrder);

    if (!rowId) {
      notify("error", "Missing row id — cannot save.");
      return false;
    }

    if (guardLockedAction(updatedOrder)) return false;

    const items = {
      cakes: normalizeItemList(updatedOrder.cakes),
      extraDecors: normalizeItemList(updatedOrder.extraDecors),
      gifts: normalizeItemList(updatedOrder.gifts),
      services: normalizeItemList(updatedOrder.services),
      extraAddons: normalizeItemList(updatedOrder.extraAddons),
    };

    const computedNewTotal = computeTotal({ ...updatedOrder, ...items });

    const addonsCache = {
      ...items,
      total: computedNewTotal,
      updatedAt: new Date().toISOString(),
    };

    const fields = {
      ...items,
      total: computedNewTotal,
      finalAmount: computedNewTotal,
      packageAmount: computedNewTotal,
      addonsCache,
    };

    applyAddonFields(rowId, fields);

    const isTransferRow =
      updatedOrder?._source === "transfer" || Boolean(getTransferId(updatedOrder));

    let ok = false;

    if (isTransferRow) {
      const baseForm = normalizeStaffHandover(updatedOrder, updatedOrder);

      const save = await persistHandover(
        updatedOrder,
        {
          ...baseForm,
          packageAmount: computedNewTotal,
          finalAmount: computedNewTotal,
          amount: computedNewTotal,
        },
        {
          ...items,
          addonsCache,
          total: computedNewTotal,
          finalAmount: computedNewTotal,
          packageAmount: computedNewTotal,
        }
      );

      ok = save.ok;
    } else {
      const id = getBookingPatchId(updatedOrder);

      const saved = await patchBooking(id, {
        ...fields,
        meta: { ...fields },
      });

      ok = saved.ok;
    }

    await fetchOrders(true);
    setTimeout(() => fetchOrders(true), 800);

    if (!ok) {
      notify(
        "error",
        "Add-ons were NOT saved on the server. Please check booking/reservation/transfer save routes.",
        7000
      );
      return false;
    }

    notify("success", "Add-ons saved.");
    return true;
  };

  const handleSaveExtraAddons = async (updatedOrder) =>
    handleSaveAddons(updatedOrder);

  /* Saves extraCost (+ Category + Reason) and Less Discount Remark + Reason with the settlement */
  const handlePaymentSubmit = async (order, paymentInfo) => {
    const adjustedTotal = computeTotal({
      ...order,
      foodCost: paymentInfo.foodCost,
      extraCost: paymentInfo.extraCost,
      manualDiscount: paymentInfo.manualDiscount,
      paymentInfo: null,
    });

    const fields = {
      status: "Fully Paid",
      frozen: false,
      foodCost: paymentInfo.foodCost,
      extraCost: paymentInfo.extraCost,
      extraCostCategory: paymentInfo.extraCostCategory,
      extraCostReason: paymentInfo.extraCostReason,
      manualDiscount: paymentInfo.manualDiscount,
      manualDiscountRemark: paymentInfo.manualDiscountRemark,
      manualDiscountReason: paymentInfo.manualDiscountReason,
      total: adjustedTotal,
      finalAmount: adjustedTotal,
      packageAmount: adjustedTotal,
      paymentInfo,
    };

    const ok = await patchOrder(order, fields);

    /* keep the open "Edit Booking Details" form in sync */
    setStaffDetailCache((prev) => {
      const rowId = getRowId(order);
      if (!prev[rowId]) return prev;

      return {
        ...prev,
        [rowId]: {
          ...prev[rowId],
          settlementDiscount: n(paymentInfo.manualDiscount),
          manualDiscountRemark: paymentInfo.manualDiscountRemark || "",
          manualDiscountReason: paymentInfo.manualDiscountReason || "",
          settlementExtraCost: n(paymentInfo.extraCost),
          extraCostCategory: paymentInfo.extraCostCategory || "",
          extraCostReason: paymentInfo.extraCostReason || "",
        },
      };
    });

    await fetchOrders(true);

    if (!ok) {
      notify("error", "Settlement could not be saved on the server. Please retry.");
      return false;
    }

    notify("success", "Payment settlement saved.");
    return true;
  };

  const handleFreeze = async (order) => {
    if (guardLockedAction(order)) return;

    const msg = order.paymentInfo
      ? "Freeze this booking permanently for admin? After freeze, this row cannot be edited, deleted, paid, printed, unfreezed or used for any admin action from this list."
      : "Payment settlement is not confirmed yet. Do you still want to freeze and permanently lock this booking for admin?";

    if (!window.confirm(msg)) return;

    setFreezing(true);

    const frozenAt = new Date().toISOString();

    const frozenOrder = toLockedOrder({
      ...order,
      frozen: true,
      frozenAt,
      frozenBy: "Admin",
      permanentlyLocked: true,
      lockedAt: frozenAt,
      lockedBy: "Admin",
      status: order.paymentInfo ? "Fully Paid" : order.status || "Advance Paid",
    });

    try {
      const freezePayload = {
        frozen: true,
        status: frozenOrder.status,
        frozenAt,
        frozenBy: "Admin",
        permanentlyLocked: true,
        lockedAt: frozenAt,
        lockedBy: "Admin",
      };

      const ok = await patchOrder(order, freezePayload);

      if (!ok) {
        notify("error", "Freeze failed on the server. Please retry.");
        setFreezing(false);
        return;
      }

      try {
        await withTimeout(
          `${SUPERADMIN_API}/frozen-orders`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              originalId: getOrderId(order) || undefined,
              transferId: getTransferId(order) || undefined,
              name: frozenOrder.name,
              celebrationName: frozenOrder.celebrationName,
              whatsapp: frozenOrder.whatsapp,
              email: frozenOrder.email,
              theater: frozenOrder.theater,
              date: frozenOrder.date,
              timeSlot: frozenOrder.timeSlot,
              occasion: frozenOrder.occasion,
              adults: frozenOrder.adults,
              kids: frozenOrder.kids,
              extraAdults: frozenOrder.extraAdults,
              extraAdultPrice: frozenOrder.extraAdultPrice,
              kidPrice: frozenOrder.kidPrice,
              glassesCount: frozenOrder.glassesCount,
              glass3DCost: frozenOrder.glass3DCost,
              basePrice: frozenOrder.basePrice,
              fixedDecor: frozenOrder.fixedDecor,
              discount: frozenOrder.discount,
              total: frozenOrder.total,
              advancePaid: frozenOrder.advancePaid,
              cakes: frozenOrder.cakes,
              extraDecors: frozenOrder.extraDecors,
              gifts: frozenOrder.gifts,
              services: frozenOrder.services,
              extraAddons: frozenOrder.extraAddons,
              paymentInfo: frozenOrder.paymentInfo,
              status: "Frozen",
              frozenAt,
              frozenBy: "Admin",
              originalTimestamp: frozenOrder.timestamp,
            }),
          },
          10000
        );
      } catch {}

      const key = identityKeyForRow(frozenOrder);
      const strip = (list) => list.filter((o) => identityKeyForRow(o) !== key);

      setOrders(strip);
      setSuspendedOrders(strip);
      setCancelledOrders(strip);

      setFrozenOrders((prev) => {
        const exists = prev.some((o) => identityKeyForRow(o) === key);

        if (exists) {
          return prev.map((o) =>
            identityKeyForRow(o) === key ? frozenOrder : o
          );
        }

        return [...prev, frozenOrder];
      });

      notify("success", "Order frozen permanently.");
      setExpandedRow(null);
      fetchOrders(true);
    } catch (err) {
      notify("error", `Failed to freeze order: ${err.message}`);
    } finally {
      setFreezing(false);
    }
  };

  const handleDelete = async (order) => {
    if (guardLockedAction(order)) return;

    setDeleting(true);

    try {
      const transferId = getTransferId(order);
      const id = getBookingPatchId(order) || getOrderId(order);

      if (transferId) {
        const res = await withTimeout(
          TRANSFER_ROW_API(transferId),
          { method: "DELETE" },
          12000
        );

        if (!res.ok) throw new Error(`Transfer delete failed ${res.status}`);
      } else {
        const merchantOrderId = order.merchantOrderId;

        if (merchantOrderId && dbConnected !== false) {
          const res = await withTimeout(
            `${BOOKINGS_API}/admin/unblock-slot`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ merchantOrderId }),
            },
            10000
          );

          if (!res.ok) throw new Error(`Delete failed ${res.status}`);
        }

        if (id) {
          try {
            await withTimeout(
              `${RESERVATIONS_API}/${encodeURIComponent(String(id))}`,
              { method: "DELETE" },
              8000
            );
          } catch {}
        }
      }

      const rowId = getRowId(order);
      const strip = (list) => list.filter((o) => getRowId(o) !== rowId);

      setOrders(strip);
      setSuspendedOrders(strip);
      setCancelledOrders(strip);

      saveRowState(order, "active");

      setDeleteConfirm(null);
      setExpandedRow(null);
      notify("success", "Booking deleted.");
      fetchOrders(true);
    } catch (err) {
      notify("error", `Delete failed: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const jumpToMonth = (am) => {
    const y = am.year;
    const m = am.month;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const pad = (x) => String(x).padStart(2, "0");

    setFromDate(`${y}-${pad(m + 1)}-01`);
    setToDate(`${y}-${pad(m + 1)}-${pad(lastDay)}`);
    setExpandedRow(null);
  };

  const isSearchActive = search.trim().length > 0;
  const isDateFiltered = Boolean(fromDate || toDate);

  const statusColors = {
    "Advance Paid": { bg: "#dcfce7", color: "#15803d" },
    "Fully Paid": { bg: "#dbeafe", color: "#1d4ed8" },
    Cancelled: { bg: "#fee2e2", color: "#dc2626" },
    Suspended: { bg: "#ffedd5", color: "#c2410c" },
    Pending: { bg: "#fef9c3", color: "#a16207" },
    Initiated: { bg: "#fef9c3", color: "#a16207" },
    "In Staff Management": { bg: "#ede9fe", color: "#6d28d9" },
    "Transferred to Staff": { bg: "#e0f2fe", color: "#0369a1" },
  };

  const viewInfoText = () => {
    if (showFrozen)
      return `${frozenOrders.length} frozen booking${
        frozenOrders.length !== 1 ? "s" : ""
      }`;

    if (isSuspendedView)
      return `${suspendedOrders.length} suspended order${
        suspendedOrders.length !== 1 ? "s" : ""
      }`;

    if (isCancelledView)
      return `${cancelledOrders.length} cancelled order${
        cancelledOrders.length !== 1 ? "s" : ""
      }`;

    return `${orders.length} active staff record${
      orders.length !== 1 ? "s" : ""
    }`;
  };

  const Th = ({ k, label }) => (
    <th
      className={`aol-th ${sortKey === k ? "aol-th-active" : ""}`}
      onClick={() => handleSort(k)}
    >
      {label} <span>{sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}</span>
    </th>
  );

  /* table column count changes in the Cancelled view (extra reason column) */
  const colCount = isCancelledView ? 13 : 12;

  if (loading) {
    return (
      <div className="aol-loading">
        <div className="aol-spinner" />
        <p>Loading staff management records...</p>
      </div>
    );
  }

  return (
    <div className="aol-page">
      <style>{EXTRA_CSS}</style>

      {/* toast stack */}
      <div className="hp-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`hp-toast ${t.type}`}>
            <span>
              {t.type === "success"
                ? "✅"
                : t.type === "error"
                ? "❌"
                : t.type === "warn"
                ? "⚠️"
                : "ℹ️"}
            </span>
            <span>{t.message}</span>
            <button type="button" onClick={() => dismissToast(t.id)}>
              ✕
            </button>
          </div>
        ))}
      </div>

      <div className="aol-header">
        <div className="aol-header-left">
          <div
            className="aol-logo"
            onClick={() => navigate("/")}
            style={{ cursor: "pointer" }}
          >
            🎬
          </div>

          <div>
            <h1>Staff Management</h1>

            <p>
              HexaPlay Admin · {filtered.length} booking
              {filtered.length !== 1 ? "s" : ""}{" "}
              <span
                className={`aol-live-badge ${
                  dbConnected !== false ? "ok" : "warn"
                }`}
              >
                {dbConnected !== false ? "✅ Live MongoDB" : "⚠️ Offline"}
              </span>

              <span
                style={{
                  display: "inline-block",
                  backgroundColor: "#ede9fe",
                  color: "#6d28d9",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "600",
                  marginLeft: "8px",
                }}
              >
                Transfer Records Included
              </span>

              <span
                style={{
                  display: "inline-block",
                  backgroundColor: "#ecfdf5",
                  color: "#15803d",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "600",
                  marginLeft: "8px",
                }}
              >
                🌐 Permanent Staff Data
              </span>

              {lastSync && (
                <span
                  style={{
                    display: "inline-block",
                    color: "#64748b",
                    fontSize: "11px",
                    fontWeight: 600,
                    marginLeft: "8px",
                  }}
                >
                  · synced {lastSync.toLocaleTimeString("en-IN")}
                </span>
              )}
            </p>

            {paymentSuccess && (
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: "#dcfce7",
                  color: "#15803d",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "600",
                  marginLeft: "12px",
                }}
              >
                ✅ New payment received
              </span>
            )}
          </div>
        </div>

        <div className="aol-header-right">
          <div className="aol-search-wrap">
            <span>🔍</span>

            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, theater, phone, occasion, order ID, reason..."
            />

            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  searchRef.current?.focus();
                }}
              >
                ✕
              </button>
            )}
          </div>

          <button
            className="aol-refresh-btn"
            onClick={() => {
              purgeLegacyLocalCaches();
              fetchOrders(false);
              setPaymentSuccess(null);
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {apiError && (
        <div className="aol-error-banner">
          <span>⚠️ Could not reach server ({apiError}).</span>
          <button onClick={() => fetchOrders(false)}>Retry</button>
        </div>
      )}

      <div className="aol-filter-bar">
        <div className="aol-date-range-group">
          <strong>📅 Event Date Range</strong>

          <label>
            From{" "}
            <input
              type="date"
              value={toInputVal(fromDate)}
              max={toInputVal(toDate) || undefined}
              onChange={(e) => {
                setFromDate(e.target.value);
                setExpandedRow(null);
              }}
            />
          </label>

          <span>→</span>

          <label>
            To{" "}
            <input
              type="date"
              value={toInputVal(toDate)}
              min={toInputVal(fromDate) || undefined}
              onChange={(e) => {
                setToDate(e.target.value);
                setExpandedRow(null);
              }}
            />
          </label>

          {(fromDate || toDate) && (
            <button
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div className="aol-month-chips">
          {availableMonths.slice(0, 6).map((am) => (
            <button key={am.label} onClick={() => jumpToMonth(am)}>
              {am.label}
              <b>{am.count}</b>
            </button>
          ))}
        </div>

        <button
          className="aol-download-btn"
          disabled={filtered.length === 0}
          onClick={() => downloadPDF(filtered, filterLabel, isCancelledView)}
        >
          🖨️ Download PDF
        </button>

        <button
          className="aol-download-btn"
          disabled={filtered.length === 0}
          onClick={() => downloadCSV(filtered, viewMode)}
          style={{ marginLeft: 8 }}
        >
          📊 Export CSV
        </button>
      </div>

      {/* ══════ VIEW TOGGLE BAR ══════ */}
      <div className="aol-view-toggle-bar">
        <div className="aol-view-toggle">
          <button
            type="button"
            className={`aol-toggle-btn ${viewMode === "active" ? "active" : ""}`}
            onClick={() => setViewMode("active")}
          >
            📋 Active Staff Records
            <span className="aol-count-badge">{orders.length}</span>
          </button>

          <button
            type="button"
            className={`aol-toggle-btn ${
              viewMode === "suspended" ? "active" : ""
            }`}
            onClick={() => setViewMode("suspended")}
          >
            ⏸️ Suspended Orders
            <span className="aol-count-badge suspended">
              {suspendedOrders.length}
            </span>
          </button>

          <button
            type="button"
            className={`aol-toggle-btn ${
              viewMode === "cancelled" ? "active" : ""
            }`}
            onClick={() => setViewMode("cancelled")}
          >
            ❌ Cancelled Orders
            <span className="aol-count-badge cancelled">
              {cancelledOrders.length}
            </span>
          </button>

          <button
            type="button"
            className={`aol-toggle-btn ${viewMode === "frozen" ? "active" : ""}`}
            onClick={() => setViewMode("frozen")}
          >
            🔒 Frozen Records
            <span className="aol-count-badge frozen">{frozenOrders.length}</span>
          </button>
        </div>

        <div className="aol-view-info">{viewInfoText()}</div>
      </div>

      {duplicateRowCount > 0 && (
        <div className="aol-duplicate-alert">
          <span>
            ⚠️ <b>{duplicateRowCount}</b> row
            {duplicateRowCount !== 1 ? "s" : ""} found with the same customer
            name and mobile number. Duplicate rows are highlighted in red below.
          </span>
        </div>
      )}

      <QuickDateStrip
        chips={quickChips}
        activeKey={activeQuickKey}
        onSelect={handleQuickSelect}
        allCount={quickFilterBase.length}
      />

      <div className="aol-toolbar">
        <span>
          <b>{filtered.length}</b> booking{filtered.length !== 1 ? "s" : ""}
          {showFrozen && " (Frozen)"}
          {isSuspendedView && " (Suspended)"}
          {isCancelledView && " (Cancelled)"}
          {isDateFiltered ? (
            <>
              {" "}
              in <b>{filterLabel}</b>
            </>
          ) : null}
          {isSearchActive ? (
            <>
              {" "}
              matching <b>"{search}"</b>
            </>
          ) : null}
        </span>

        <span>← Scroll table horizontally →</span>
      </div>

      <div className="aol-table-outer">
        {filtered.length === 0 ? (
          <div className="aol-empty">
            <div>
              {isSearchActive
                ? "🔍"
                : showFrozen
                ? "🔒"
                : isSuspendedView
                ? "⏸️"
                : isCancelledView
                ? "❌"
                : "📋"}
            </div>

            <h3>
              {isSearchActive
                ? "No results found"
                : showFrozen
                ? "No frozen bookings"
                : isSuspendedView
                ? "No suspended orders"
                : isCancelledView
                ? "No cancelled orders"
                : "No staff management records found"}
            </h3>

            <p>
              {isSearchActive
                ? `No orders match "${search}".`
                : isDateFiltered
                ? `No bookings for ${filterLabel}.`
                : showFrozen
                ? "No bookings have been frozen yet."
                : isSuspendedView
                ? "Click the ⏸️ Suspend button on any active row to move it here permanently."
                : isCancelledView
                ? "Click the ❌ Cancel button on any active row, write the reason, and it will appear here permanently."
                : "Transfer a booking from Transfer Content page. It will appear here permanently."}
            </p>

            {viewMode === "active" && (
              <button
                className="aol-primary-btn"
                onClick={() => navigate("/")}
                style={{ marginTop: 16 }}
              >
                🎬 Book a Theater
              </button>
            )}
          </div>
        ) : (
          <div className="aol-table-scroll">
            <table className="aol-table">
              <thead>
                <tr>
                  <th className="aol-th">#</th>
                  <Th k="timestamp" label="Transferred / Booked On" />
                  <Th k="name" label="Customer" />
                  <Th k="theater" label="Theater" />
                  <Th k="date" label="Event Date" />
                  <th className="aol-th">Slot</th>
                  <Th k="occasion" label="Occasion" />
                  <th className="aol-th">Guests</th>
                  <Th k="total" label="Amount" />
                  <th className="aol-th">Status</th>
                  {isCancelledView && (
                    <Th k="cancelledAt" label="Cancel Reason" />
                  )}
                  <th className="aol-th">Add-ons</th>
                  <th className="aol-th action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((o, i) => {
                  const id = getRowId(o) || `${i}`;
                  const isExpanded = expandedRow === id;
                  const addons = buildAddonRows(o);
                  const isFrozen = isOrderLocked(o);
                  const total = n(o.total || computeTotal(o));
                  const sc =
                    statusColors[o.status || "Advance Paid"] ||
                    statusColors["Advance Paid"];

                  const dupKey = duplicateKeyOf(o);
                  const isDuplicate = Boolean(dupKey) && duplicateKeys.has(dupKey);

                  const isNewPayment =
                    paymentSuccess &&
                    (o.merchantOrderId === paymentSuccess.merchantOrderId ||
                      getOrderId(o) === paymentSuccess.bookingId ||
                      getTransferId(o) === paymentSuccess.transferId);

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`aol-row ${isExpanded ? "expanded" : ""} ${
                          isFrozen ? "frozen" : ""
                        } ${isNewPayment ? "new-payment" : ""} ${
                          isDuplicate ? "aol-duplicate-row" : ""
                        }`}
                        onClick={() => handleToggleExpandedRow(o, id)}
                      >
                        <td>
                          <span className="aol-row-num">{i + 1}</span>
                          {isFrozen && <span title="Frozen"> 🔒</span>}
                          {isNewPayment && <span title="New Payment"> 🆕</span>}
                          {isDuplicate && (
                            <span title="Duplicate customer name + mobile number">
                              {" "}
                              ⚠️
                            </span>
                          )}
                          {o._fromTransferList && (
                            <span title="From Transfer Content"> 🚚</span>
                          )}
                          {o._dbCached && (
                            <span title="Add-ons stored in MongoDB"> ☁️</span>
                          )}
                        </td>

                        <td>
                          <span className="aol-date-ts">
                            {formatTS(o.transferredAt || o.timestamp)}
                          </span>
                        </td>

                        <td>
                          <div className="aol-customer">
                            <div className="aol-avatar">
                              {o.name?.[0]?.toUpperCase() || "?"}
                            </div>

                            <div>
                              <b>{o.name || "—"}</b>
                              <span>{o.whatsapp || "—"}</span>

                              {isDuplicate && (
                                <small className="aol-dup-badge">
                                  ⚠️ Duplicate Name + Mobile
                                </small>
                              )}

                              {o.celebrationName && (
                                <small>🎉 {o.celebrationName}</small>
                              )}

                              {o._fromTransferList && (
                                <small style={{ color: "#6d28d9" }}>
                                  🚚 Transfer Content
                                </small>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="aol-theater-pill">
                            {o.theater || "—"}
                          </span>
                        </td>

                        <td>
                          <b>{formatDisplayDate(o.date)}</b>
                        </td>

                        <td>{o.timeSlot || "—"}</td>

                        <td>
                          <span className="aol-occasion-pill">
                            {o.occasion || "—"}
                          </span>
                        </td>

                        <td>
                          <span>👤 {n(o.adults)}</span>
                          {n(o.kids) > 0 && <span> 🧒 {n(o.kids)}</span>}
                          {n(o.glassesCount) > 0 && (
                            <span> 🥽 {n(o.glassesCount)}</span>
                          )}
                        </td>

                        <td>
                          <div className="aol-amount-cell">
                            <b>{money(total)}</b>
                            <span>{money(o.advancePaid || ADVANCE_PAID)} advance</span>
                            {o.paymentInfo && (
                              <small>
                                Settled{" "}
                                {money(
                                  o.paymentInfo.totalPaid ||
                                    o.paymentInfo.finalAmount
                                )}
                              </small>
                            )}
                          </div>
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          {isFrozen ? (
                            <span className="aol-frozen-badge">🔒 Frozen</span>
                          ) : isSuspendedView ? (
                            <span className="aol-state-badge suspended">
                              ⏸️ Suspended
                            </span>
                          ) : isCancelledView ? (
                            <span className="aol-state-badge cancelled">
                              ❌ Cancelled
                            </span>
                          ) : (
                            <select
                              value={o.status || "In Staff Management"}
                              onChange={(e) =>
                                handleStatusUpdate(o, e.target.value)
                              }
                              style={{ background: sc.bg, color: sc.color }}
                            >
                              <option>In Staff Management</option>
                              <option>Transferred to Staff</option>
                              <option>Advance Paid</option>
                              <option>Fully Paid</option>
                              <option>Pending</option>
                              <option>Initiated</option>
                              <option>Cancelled</option>
                            </select>
                          )}
                        </td>

                        {/* Cancel Reason column (Cancelled Orders tab only) */}
                        {isCancelledView && (
                          <td onClick={(e) => e.stopPropagation()}>
                            {o.cancelReason ? (
                              <>
                                <span
                                  className="hp-reason-cell"
                                  title={o.cancelReason}
                                >
                                  {o.cancelReason}
                                </span>
                                <span className="hp-reason-meta">
                                  {formatTS(o.cancelledAt || o.staffStateAt)} ·{" "}
                                  {o.cancelledBy || "Admin"}
                                </span>
                              </>
                            ) : (
                              <span className="aol-no-addon">No reason recorded</span>
                            )}
                          </td>
                        )}

                        <td>
                          {addons.length === 0 ? (
                            <span className="aol-no-addon">—</span>
                          ) : (
                            <div className="aol-addon-chips">
                              {addons.slice(0, 2).map((a) => (
                                <span key={`${a.name}-${a.category}`}>
                                  {a.emoji} {a.name}
                                  {qtyLabel(a)}
                                </span>
                              ))}

                              {addons.length > 2 && <b>+{addons.length - 2}</b>}
                            </div>
                          )}
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="aol-actions">
                            {isFrozen ? (
                              <span
                                className="aol-locked-actions"
                                title="Admin actions are permanently locked for this row."
                              >
                                🔒 Permanently Locked
                              </span>
                            ) : isArchiveView ? (
                              <>
                                <button
                                  className="restore"
                                  disabled={movingState}
                                  onClick={() => handleRowState(o, "active")}
                                >
                                  ↩️ Restore
                                </button>

                                <button
                                  className="print"
                                  onClick={() => printRowPDF(o)}
                                >
                                  Print
                                </button>

                                {isSuspendedView && (
                                  <button
                                    className="cancelled"
                                    disabled={movingState}
                                    onClick={() => requestCancel(o)}
                                  >
                                    ❌ Cancel
                                  </button>
                                )}

                                {isCancelledView && (
                                  <button
                                    className="suspend"
                                    disabled={movingState}
                                    onClick={() => handleRowState(o, "suspended")}
                                  >
                                    ⏸️ Suspend
                                  </button>
                                )}

                                <button
                                  className="delete"
                                  onClick={() => setDeleteConfirm(o)}
                                >
                                  Delete
                                </button>
                              </>
                            ) : (
                              <>
                                <a
                                  className="wa"
                                  href={`https://wa.me/${String(
                                    o.whatsapp || ""
                                  ).replace(/\D/g, "")}?text=${buildWAMessage(o)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  WhatsApp
                                </a>

                                <button
                                  className="addon"
                                  onClick={() => setAddonModal(o)}
                                >
                                  + Add-ons
                                </button>

                                <button
                                  className="extra-addon"
                                  onClick={() => setExtraAddonModal(o)}
                                >
                                  ⭐ Extra Add-ons
                                </button>

                                <button
                                  className="print"
                                  onClick={() => printRowPDF(o)}
                                >
                                  Print
                                </button>

                                <button
                                  className="pay"
                                  onClick={() => setPayNowModal(o)}
                                >
                                  💳 Pay
                                </button>

                                <button
                                  className="suspend"
                                  disabled={movingState}
                                  onClick={() => handleRowState(o, "suspended")}
                                >
                                  ⏸️ Suspend
                                </button>

                                {/* opens the Cancel Reason popup */}
                                <button
                                  className="cancelled"
                                  disabled={movingState}
                                  onClick={() => requestCancel(o)}
                                >
                                  ❌ Cancel
                                </button>

                                <button
                                  className="freeze"
                                  onClick={() => handleFreeze(o)}
                                  disabled={freezing}
                                >
                                  {freezing ? "Freezing..." : "🔒 Freeze"}
                                </button>

                                <button
                                  className="delete"
                                  onClick={() => setDeleteConfirm(o)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="aol-expand-row">
                          <td colSpan={colCount}>
                            <div className="aol-expand-body">
                              {isFrozen && (
                                <div className="aol-frozen-banner">
                                  🔒 <b>This booking is permanently locked.</b>{" "}
                                  Admin cannot edit this row or perform any action
                                  from the order list.
                                  {o.frozenAt && (
                                    <span> · Frozen on {formatTS(o.frozenAt)}</span>
                                  )}
                                </div>
                              )}

                              {isDuplicate && (
                                <div className="aol-duplicate-banner">
                                  ⚠️ <b>Duplicate booking detected.</b> Another row
                                  has the same customer name and mobile number.
                                  Please verify before collecting payment.
                                </div>
                              )}

                              {isSuspendedView && (
                                <div className="aol-state-banner suspended">
                                  ⏸️ <b>This order is suspended.</b>
                                  {o.staffStateAt && (
                                    <span> · Since {formatTS(o.staffStateAt)}</span>
                                  )}
                                </div>
                              )}

                              {isCancelledView && (
                                <div className="aol-state-banner cancelled">
                                  ❌ <b>This order is cancelled.</b>
                                  {(o.cancelledAt || o.staffStateAt) && (
                                    <span>
                                      {" "}
                                      · Since {formatTS(o.cancelledAt || o.staffStateAt)}
                                      {o.cancelledBy ? ` · by ${o.cancelledBy}` : ""}
                                    </span>
                                  )}

                                  {/* full cancel reason */}
                                  <div className="hp-cancel-reason-box">
                                    <strong>Cancellation reason</strong>
                                    {o.cancelReason || "No reason recorded."}
                                  </div>
                                </div>
                              )}

                              <StaffHandoverWorkspace
                                order={o}
                                initialData={staffDetailCache[id]}
                                loading={Boolean(staffDetailLoading[id])}
                                saving={Boolean(staffDetailSaving[id])}
                                error={staffDetailError[id]}
                                frozen={isFrozen}
                                onSave={(form) => handleSaveStaffDetails(o, form)}
                                onRefresh={() => hydrateStaffDetails(o, true)}
                                onPrint={() => printRowPDF(o)}
                                onPay={() => !isFrozen && setPayNowModal(o)}
                                onExtraAddons={() =>
                                  !isFrozen && setExtraAddonModal(o)
                                }
                                onDelete={() => !isFrozen && setDeleteConfirm(o)}
                                onClose={() => setExpandedRow(null)}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addonModal && !isOrderLocked(addonModal) && (
        <AddAddonsModal
          order={addonModal}
          onClose={() => setAddonModal(null)}
          onSave={handleSaveAddons}
        />
      )}

      {extraAddonModal && !isOrderLocked(extraAddonModal) && (
        <ExtraAddonsModal
          order={extraAddonModal}
          onClose={() => setExtraAddonModal(null)}
          onSave={handleSaveExtraAddons}
        />
      )}

      {payNowModal && !isOrderLocked(payNowModal) && (
        <PayNowModal
          order={payNowModal}
          onClose={() => setPayNowModal(null)}
          onSubmit={(info) => handlePaymentSubmit(payNowModal, info)}
        />
      )}

      {/* Cancel reason popup */}
      {cancelModal && !isOrderLocked(cancelModal) && (
        <CancelReasonModal
          order={cancelModal}
          busy={movingState}
          onClose={() => setCancelModal(null)}
          onConfirm={confirmCancel}
        />
      )}

      {deleteConfirm && !isOrderLocked(deleteConfirm) && (
        <div className="aol-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="aol-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aol-delete-icon">🗑️</div>

            <h3>Delete this booking?</h3>

            <p>
              You are about to permanently remove <b>{deleteConfirm.name}</b>'s
              booking for <b>{deleteConfirm.theater}</b> on{" "}
              <b>{formatDisplayDate(deleteConfirm.date)}</b>.
            </p>

            <div>
              <button
                className="aol-light-btn"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>

              <button
                className="aol-danger-btn"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════
   STAFF HANDOVER WORKSPACE  (Edit Booking Details)
══════════════════════════════════════════════════ */
const StaffHandoverWorkspace = ({
  order,
  initialData,
  loading,
  saving,
  error,
  frozen,
  onSave,
  onRefresh,
  onPrint,
  onPay,
  onExtraAddons,
  onDelete,
  onClose,
}) => {
  const orderKey = getRowId(order);

  const [activeTab, setActiveTab] = useState("customer");
  const [form, setForm] = useState(() =>
    normalizeStaffHandover(order, initialData || order)
  );
  const [newComplimentary, setNewComplimentary] = useState("");

  useEffect(() => {
    setForm(normalizeStaffHandover(order, initialData || order));
  }, [orderKey, initialData, order]);

  const disabled = frozen || saving;

  const pricing = useMemo(
    () => calculateStaffLivePricing(order, form),
    [order, form]
  );

  const extraAddonsTotal = useMemo(
    () => computeExtraAddonTotal(order.extraAddons),
    [order.extraAddons]
  );

  const updateRoot = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateManyRoot = (updates) => {
    setForm((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const updateHandover = (key, value) => {
    setForm((prev) => {
      const nextHandover = {
        ...prev.handoverDetails,
        [key]: value,
      };

      const next = {
        ...prev,
        handoverDetails: nextHandover,
      };

      if (key === "managerDiscount") {
        next.managerDiscount = n(value);
      }

      return normalizeStaffHandover(order, next);
    });
  };

  const updateExtra = (key, value) => {
    setForm((prev) => {
      const nextExtra = computeStaffExtraPersonDetails(
        {
          ...prev.extraPersonDetails,
          [key]: value,
        },
        order
      );

      return normalizeStaffHandover(order, {
        ...prev,
        extraPersonDetails: nextExtra,
      });
    });
  };

  const toggleComplimentary = (item) => {
    setForm((prev) => {
      const exists = prev.complimentaryItems.includes(item);

      return {
        ...prev,
        complimentaryItems: exists
          ? prev.complimentaryItems.filter((x) => x !== item)
          : [...prev.complimentaryItems, item],
      };
    });
  };

  const addComplimentary = () => {
    const value = newComplimentary.trim();
    if (!value) return;

    setForm((prev) => ({
      ...prev,
      complimentaryItems: prev.complimentaryItems.includes(value)
        ? prev.complimentaryItems
        : [...prev.complimentaryItems, value],
    }));

    setNewComplimentary("");
  };

  const addCustomField = () => {
    setForm((prev) => ({
      ...prev,
      customFields: [
        ...prev.customFields,
        {
          id: makeCustomFieldId(),
          label: "New Field",
          type: "text",
          required: false,
          options: [],
          value: "",
        },
      ],
    }));
  };

  const updateCustomField = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      customFields: prev.customFields.map((field) =>
        field.id === id
          ? {
              ...field,
              [key]:
                key === "options"
                  ? String(value)
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean)
                  : value,
            }
          : field
      ),
    }));
  };

  const removeCustomField = (id) => {
    setForm((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((field) => field.id !== id),
    }));
  };

  const save = () => {
    const missingRequired = form.customFields.find(
      (f) => f.required && !String(f.value || "").trim()
    );

    if (missingRequired) {
      setActiveTab("custom");
      alert(`Please fill the required custom field: ${missingRequired.label}`);
      return;
    }

    onSave({
      ...form,
      ...pricing,
      finalAmount: pricing.finalAmount,
      packageAmount: pricing.packageAmount,
      managerDiscount: pricing.managerDiscount,
      extraPersonDetails: pricing.extraDetails,
      handoverDetails: {
        ...form.handoverDetails,
        managerDiscount: pricing.managerDiscount,
        lessDiscountRemark: form.manualDiscountRemark || "",
        lessDiscountReason: form.manualDiscountReason || "",
        extraCostCategory: form.extraCostCategory || "",
        extraCostReason: form.extraCostReason || "",
      },
    });
  };

  const renderCustomerTab = () => (
    <div className="aol-staff-tab-grid">
      <section className="aol-staff-card">
        <h4>👤 Customer Information</h4>
        <p>Primary customer and contact information.</p>

        <div className="aol-staff-form-grid">
          <label>
            Customer Name <b>*</b>
            <input
              value={form.name || ""}
              disabled={disabled}
              onChange={(e) =>
                updateManyRoot({
                  name: e.target.value,
                  customerName: e.target.value,
                })
              }
            />
          </label>

          <label>
            Primary Contact Number <b>*</b>
            <input
              value={form.primaryContactNumber || ""}
              disabled={disabled}
              onChange={(e) =>
                updateManyRoot({
                  primaryContactNumber: e.target.value,
                  whatsapp: e.target.value,
                })
              }
            />
          </label>

          <label>
            Alternate Contact Number
            <input
              value={form.alternateContactNumber || ""}
              disabled={disabled}
              placeholder="Optional alternate number"
              onChange={(e) =>
                updateRoot("alternateContactNumber", e.target.value)
              }
            />
          </label>

          <label>
            Celebration Name
            <input
              value={form.celebrationName || ""}
              disabled={disabled}
              onChange={(e) => updateRoot("celebrationName", e.target.value)}
            />
          </label>
        </div>

        <label className="aol-staff-full">
          Food Details
          <textarea
            value={form.handoverDetails.foodDetails || ""}
            disabled={disabled}
            maxLength="500"
            placeholder="Food menu, dietary requirements, serving notes..."
            onChange={(e) => updateHandover("foodDetails", e.target.value)}
          />

          <span className="aol-char-count">
            {(form.handoverDetails.foodDetails || "").length}/500
          </span>
        </label>
      </section>

      <section className="aol-staff-card">
        <h4>🎂 Cake & Celebration</h4>
        <p>Configure cake, candle, sash and crown requirements.</p>

        <div className="aol-staff-form-grid">
          <label>
            Cake Message
            <input
              value={form.handoverDetails.cakeMessage || ""}
              disabled={disabled}
              placeholder="Happy Birthday"
              onChange={(e) => updateHandover("cakeMessage", e.target.value)}
            />
          </label>

          <label>
            Cake Flavor
            <select
              value={form.handoverDetails.cakeFlavor || ""}
              disabled={disabled}
              onChange={(e) => updateHandover("cakeFlavor", e.target.value)}
            >
              <option value="">Select Flavor</option>
              <option>Chocolate</option>
              <option>Vanilla</option>
              <option>Butterscotch</option>
              <option>Black Forest</option>
              <option>Red Velvet</option>
              <option>Pineapple</option>
            </select>
          </label>

          <label>
            Cake Weight
            <div className="aol-input-suffix">
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.handoverDetails.cakeWeight || ""}
                disabled={disabled}
                onChange={(e) => updateHandover("cakeWeight", e.target.value)}
              />
              <i>kg</i>
            </div>
          </label>

          <label>
            Candle Number
            <input
              type="number"
              min="0"
              value={form.handoverDetails.candleNumber || ""}
              disabled={disabled}
              onChange={(e) =>
                updateHandover("candleNumber", e.target.value)
              }
            />
          </label>
        </div>
      </section>

      
        

        

       

        
        

        

        
     
    </div>
  );

  const renderExtraTab = () => (
    <div className="aol-staff-tab-grid single">
      <section className="aol-staff-card wide">
        <h4>👥 Extra Person Details</h4>
        <p>
          Complimentary persons are never charged. Amount is calculated only for
          chargeable extra persons.
        </p>

        <div className="aol-extra-grid">
          <label>
            Total Persons
            <input
              type="number"
              min="0"
              value={form.extraPersonDetails.totalPersons}
              disabled={disabled}
              onChange={(e) => updateExtra("totalPersons", e.target.value)}
            />
          </label>

          <label>
            Package Included Persons
            <input
              type="number"
              min="0"
              value={form.extraPersonDetails.packageIncludedPersons}
              disabled={disabled}
              onChange={(e) =>
                updateExtra("packageIncludedPersons", e.target.value)
              }
            />
          </label>

          <label>
            Complimentary Persons
            <input
              type="number"
              min="0"
              value={form.extraPersonDetails.complimentaryPersons}
              disabled={disabled}
              onChange={(e) =>
                updateExtra("complimentaryPersons", e.target.value)
              }
            />
          </label>

          <label>
            Standard Cost / Person
            <input
              type="number"
              min="0"
              value={form.extraPersonDetails.standardCostPerPerson}
              disabled={disabled}
              onChange={(e) =>
                updateExtra("standardCostPerPerson", e.target.value)
              }
            />
          </label>

          <label>
            Discount / Person
            <input
              type="number"
              min="0"
              value={form.extraPersonDetails.discountPerPerson}
              disabled={disabled}
              onChange={(e) => updateExtra("discountPerPerson", e.target.value)}
            />
          </label>
        </div>

        <div className="aol-extra-result-grid">
          <div>
            <span>Extra Persons</span>
            <b>{form.extraPersonDetails.extraPersons}</b>
          </div>

          <div>
            <span>Chargeable Persons</span>
            <b>{form.extraPersonDetails.chargeablePersons}</b>
          </div>

          <div>
            <span>Final Cost / Person</span>
            <b>{money(form.extraPersonDetails.finalCostPerPerson)}</b>
          </div>

          <div className="total">
            <span>Extra Person Amount</span>
            <b>{money(form.extraPersonDetails.extraPersonAmount)}</b>
          </div>
        </div>

        <label className="aol-staff-full">
          Extra Person Remarks
          <textarea
            value={form.extraPersonDetails.remarks || ""}
            disabled={disabled}
            placeholder="Any extra person notes..."
            onChange={(e) => updateExtra("remarks", e.target.value)}
          />
        </label>
      </section>
    </div>
  );

  const renderComplimentaryTab = () => (
    <div className="aol-staff-tab-grid single">
      <section className="aol-staff-card wide">
        <h4>🎁 Complimentary Items</h4>
        <p>Select complimentary items offered to the customer.</p>

        <div className="aol-complimentary-grid">
          {DEFAULT_COMPLIMENTARY_ITEMS.map((item) => {
            const picked = form.complimentaryItems.includes(item);

            return (
              <button
                key={item}
                type="button"
                disabled={disabled}
                className={picked ? "active" : ""}
                onClick={() => toggleComplimentary(item)}
              >
                {picked ? "✅" : "＋"} {item}
              </button>
            );
          })}
        </div>

        <div className="aol-add-complimentary">
          <input
            value={newComplimentary}
            disabled={disabled}
            placeholder="Add custom complimentary item"
            onChange={(e) => setNewComplimentary(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addComplimentary();
            }}
          />

          <button type="button" disabled={disabled} onClick={addComplimentary}>
            Add
          </button>
        </div>

        <div className="aol-picked-list">
          {form.complimentaryItems.length === 0 ? (
            <span>No complimentary items selected.</span>
          ) : (
            form.complimentaryItems.map((item) => (
              <button
                key={item}
                type="button"
                disabled={disabled}
                onClick={() => toggleComplimentary(item)}
              >
                {item} ✕
              </button>
            ))
          )}
        </div>
      </section>
    </div>
  );

  const renderSummaryTab = () => (
    <div className="aol-staff-tab-grid single">
      <section className="aol-staff-card wide">
        <h4>📋 Booking Summary</h4>
        <p>Final handover summary saved into Staff Management.</p>

        <div className="aol-summary-grid">
          <div>
            <span>Customer</span>
            <b>{form.name || "—"}</b>
          </div>

          <div>
            <span>Phone</span>
            <b>{form.primaryContactNumber || form.whatsapp || "—"}</b>
          </div>

          <div>
            <span>Theater</span>
            <b>{form.theater || "—"}</b>
          </div>

          <div>
            <span>Date / Slot</span>
            <b>
              {formatDisplayDate(form.date)} · {form.timeSlot || "—"}
            </b>
          </div>

          <div>
            <span>Occasion</span>
            <b>{form.occasion || "—"}</b>
          </div>

          <div>
            <span>Celebration Name</span>
            <b>{form.celebrationName || "—"}</b>
          </div>

          <div>
            <span>Cake</span>
            <b>
              {form.handoverDetails.cakeFlavor || "—"} ·{" "}
              {form.handoverDetails.cakeMessage || "No message"}
            </b>
          </div>

          <div>
            <span>Decoration</span>
            <b>{form.handoverDetails.decorationTheme || "—"}</b>
          </div>

          <div>
            <span>Extra Person Amount</span>
            <b>{money(pricing.extraPersonAmount)}</b>
          </div>

          <div>
            <span>Extra Add-ons Amount</span>
            <b>{money(extraAddonsTotal)}</b>
          </div>

          <div>
            <span>Extra Cost</span>
            <b>{money(form.settlementExtraCost)}</b>
          </div>

          <div>
            <span>Extra Cost Category</span>
            <b>{form.extraCostCategory || "—"}</b>
          </div>

          <div>
            <span>Extra Cost Reason</span>
            <b>{form.extraCostReason || "—"}</b>
          </div>

          <div>
            <span>Less Discount</span>
            <b>{money(form.settlementDiscount)}</b>
          </div>

          <div>
            <span>Less Discount Remark</span>
            <b>{form.manualDiscountRemark || "—"}</b>
          </div>

          <div>
            <span>Discount Reason</span>
            <b>{form.manualDiscountReason || "—"}</b>
          </div>

          <div>
            <span>Final Booking Total</span>
            <b>{money(pricing.finalAmount)}</b>
          </div>

          {form.cancelReason && (
            <div>
              <span>Cancellation Reason</span>
              <b>{form.cancelReason}</b>
            </div>
          )}
        </div>

        <label className="aol-staff-full">
          Staff Notes
          <textarea
            value={form.staffNotes || ""}
            disabled={disabled}
            placeholder="Internal staff notes..."
            onChange={(e) => updateRoot("staffNotes", e.target.value)}
          />
        </label>
      </section>
    </div>
  );

  const renderCustomTab = () => (
    <div className="aol-staff-tab-grid single">
      <section className="aol-staff-card wide">
        <div className="aol-custom-head">
          <div>
            <h4>☑️ Custom Fields</h4>
            <p>Add dynamic extra fields for your staff workflow.</p>
          </div>

          <button type="button" disabled={disabled} onClick={addCustomField}>
            ＋ Add Field
          </button>
        </div>

        {form.customFields.length === 0 ? (
          <div className="aol-custom-empty">No custom fields added yet.</div>
        ) : (
          <div className="aol-custom-list">
            {form.customFields.map((field) => (
              <div key={field.id} className="aol-custom-row">
                <input
                  value={field.label}
                  disabled={disabled}
                  placeholder="Field label"
                  onChange={(e) =>
                    updateCustomField(field.id, "label", e.target.value)
                  }
                />

                <select
                  value={field.type}
                  disabled={disabled}
                  onChange={(e) =>
                    updateCustomField(field.id, "type", e.target.value)
                  }
                >
                  <option value="text">Text</option>
                  <option value="textarea">Textarea</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="radio">Radio</option>
                </select>

                {(field.type === "dropdown" || field.type === "radio") && (
                  <input
                    value={(field.options || []).join(", ")}
                    disabled={disabled}
                    placeholder="Options comma separated"
                    onChange={(e) =>
                      updateCustomField(field.id, "options", e.target.value)
                    }
                  />
                )}

                {field.type === "textarea" ? (
                  <textarea
                    value={field.value}
                    disabled={disabled}
                    placeholder="Value"
                    onChange={(e) =>
                      updateCustomField(field.id, "value", e.target.value)
                    }
                  />
                ) : field.type === "dropdown" ? (
                  <select
                    value={field.value}
                    disabled={disabled}
                    onChange={(e) =>
                      updateCustomField(field.id, "value", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === "radio" ? (
                  <select
                    value={field.value}
                    disabled={disabled}
                    onChange={(e) =>
                      updateCustomField(field.id, "value", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={
                      field.type === "number"
                        ? "number"
                        : field.type === "date"
                        ? "date"
                        : "text"
                    }
                    value={field.value}
                    disabled={disabled}
                    placeholder="Value"
                    onChange={(e) =>
                      updateCustomField(field.id, "value", e.target.value)
                    }
                  />
                )}

                <label className="aol-custom-required">
                  <input
                    type="checkbox"
                    checked={field.required}
                    disabled={disabled}
                    onChange={(e) =>
                      updateCustomField(field.id, "required", e.target.checked)
                    }
                  />
                  Required
                </label>

                <button
                  type="button"
                  disabled={disabled}
                  className="aol-custom-remove"
                  onClick={() => removeCustomField(field.id)}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );

  const renderActiveTab = () => {
    if (activeTab === "extra") return renderExtraTab();
    if (activeTab === "complimentary") return renderComplimentaryTab();
    if (activeTab === "summary") return renderSummaryTab();
    if (activeTab === "custom") return renderCustomTab();
    return renderCustomerTab();
  };

  return (
    <div className={`aol-transfer-shell ${loading ? "loading" : ""}`}>
      <aside className="aol-transfer-left">
        <div className="aol-transfer-left-head">
          <div>
            <h3>✎ Edit Booking Details</h3>
            <p>Update customer, cake, decoration and staff handover information</p>
          </div>

          <span className="aol-transfer-status">
            {frozen ? "🔒 Locked" : "● Transferred"}
          </span>

          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="aol-transfer-profile">
          <div className="aol-transfer-avatar">
            {(form.name || order.name || "?").charAt(0).toUpperCase()}
          </div>

          <div>
            <h4>{form.name || order.name || "—"}</h4>
            <span>📞 {form.primaryContactNumber || order.whatsapp || "—"}</span>
            {form.celebrationName && <small>🎉 {form.celebrationName}</small>}
          </div>

          <b>{form.occasion || order.occasion || "—"}</b>
        </div>

        <div className="aol-transfer-meta">
          <div>
            <span>🏟️ Theater</span>
            <b>{form.theater || order.theater || "—"}</b>
          </div>

          <div>
            <span>📅 Event Date</span>
            <b>{formatDisplayDate(form.date || order.date)}</b>
          </div>

          <div>
            <span>⏰ Time Slot</span>
            <b>{form.timeSlot || order.timeSlot || "—"}</b>
          </div>

          <div>
            <span>🎉 Occasion</span>
            <b>{form.occasion || order.occasion || "—"}</b>
          </div>

          <div>
            <span>₹ Amount</span>
            <b>{money(pricing.finalAmount || order.total || computeTotal(order))}</b>
          </div>

          <div>
            <span>⭐ Extra Add-ons</span>
            <b>{money(extraAddonsTotal)}</b>
          </div>

          <div>
            <span>👤 Transferred By</span>
            <b>{form.transferredBy || "Admin"}</b>
          </div>

          <div>
            <span>🕘 Transferred At</span>
            <b>{formatTS(form.transferredAt || order.transferredAt || order.timestamp)}</b>
          </div>
        </div>
      </aside>

      <main className="aol-transfer-main">
        {error && <div className="aol-staff-error">⚠️ {error}</div>}

        {loading && (
          <div className="aol-staff-loading">
            Fetching transfer-content details...
          </div>
        )}

        <div className="aol-transfer-tabs">
          {STAFF_TABS.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={activeTab === tab.key ? "active" : ""}
              onClick={() => setActiveTab(tab.key)}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {renderActiveTab()}
      </main>

      <aside className="aol-transfer-right">
        <div className="aol-live-pricing-card">
          <div className="aol-live-head">
            <h4>🧾 Live Pricing</h4>
            <span>Auto Calculated</span>
          </div>

          <div>
            <span>Package Amount</span>
            <b>{money(pricing.packageAmount)}</b>
          </div>

          <div>
            <span>Extra Person Amount</span>
            <b>{money(pricing.extraPersonAmount)}</b>
          </div>

          <div>
            <span>Extra Add-ons</span>
            <b>{money(extraAddonsTotal)}</b>
          </div>

          <div>
            <span>Add-ons / Other Charges</span>
            <b>{money(pricing.addonsAmount)}</b>
          </div>

          <div className="discount">
            <span>Discounts</span>
            <b>- {money(pricing.managerDiscount + pricing.otherDiscount)}</b>
          </div>

          <div className="final">
            <span>Final Booking Total</span>
            <b>{money(pricing.finalAmount)}</b>
          </div>
        </div>

        <div className="aol-quick-actions-card">
          <h4>⚙️ Quick Actions</h4>

          <button type="button" onClick={onPrint}>
            🔍 View / Print Booking
          </button>

          <button type="button" onClick={onRefresh}>
            🔁 Refresh Transfer
          </button>

          <button type="button" disabled={frozen} onClick={onExtraAddons}>
            ⭐ Extra Add-ons
          </button>

          <button type="button" disabled={frozen} onClick={onPay}>
            💳 Payment
          </button>

          <button type="button" className="danger" disabled={frozen} onClick={onDelete}>
            🗑 Delete Booking
          </button>
        </div>

        <div className="aol-transfer-save-card">
          <button type="button" className="save" disabled={disabled} onClick={save}>
            {saving ? "Saving..." : "💾 Save Changes"}
          </button>

          <button type="button" className="cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </aside>
    </div>
  );
};

export default StaffManagement;